/**
 * Generated by orval from the Collective Payroll API contract. Do not edit manually.
 */
import { useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';

import type {
    AutoPayrollState,
    AutoPayrollToggleRequest,
    CompensationBenchmark,
    CompensationBenchmarkRequest,
    CompensationCatchUpRequest,
    CompensationFinancialData,
    CompensationReport,
    ContractorCreateRequest,
    ContractorPayment,
    ContractorPaymentCreateRequest,
    ContractorPaymentGroup,
    ContractorPaymentList,
    ContractorPaymentPreview,
    ContractorPaymentPreviewRequest,
    ContractorPaymentReceipt,
    ContractorRehireRequest,
    ContractorTerminateRequest,
    EmployeeAddressHistory,
    EmployeeCompensationUpdateRequest,
    EmployeeCreateRequest,
    EmployeeFederalTaxes,
    EmployeeHomeAddressChange,
    EmployeeJobList,
    EmployeeOnboardingBlockerList,
    EmployeeRehireRequest,
    EmployeeStateTaxList,
    EmployeeWorkLocationAssign,
    PatchedCompensationSalaryRequest,
    PatchedContractorUpdateRequest,
    PatchedEmployeeUpdateRequest,
    PatchedPayrollLinesUpdateRequest,
    PayScheduleList,
    PayrollActivityPage,
    PayrollAlertList,
    PayrollAlertResolve,
    PayrollAlertResolveRequest,
    PayrollAlertsListParams,
    PayrollCancelRequest,
    PayrollCapabilities,
    PayrollCommand,
    PayrollCommandAccepted,
    PayrollContractorPaymentsListParams,
    PayrollDocumentContentRetrieveParams,
    PayrollDocumentDownload,
    PayrollDocumentDownloadUrlRetrieveParams,
    PayrollDocumentLibrary,
    PayrollDocumentList,
    PayrollDocumentsLibraryListParams,
    PayrollDocumentsListParams,
    PayrollErrorResponse,
    PayrollFunding,
    PayrollNudge,
    PayrollNudgeRespondRequest,
    PayrollOffCycleRequest,
    PayrollOverview,
    PayrollPayrollsListParams,
    PayrollPeriodDuplicates,
    PayrollPrepare,
    PayrollProviderYtdRetrieveParams,
    PayrollReceipt,
    PayrollRunDetail,
    PayrollSignatureQueue,
    PayrollSkipRequest,
    PayrollSubmitRequest,
    PayrollTaxPayments,
    PayrollTeamListParams,
    PayrollTimesheetPrefill,
    PayrollTimesheetsListParams,
    PayrollYtd,
    PayrollYtdRetrieveParams,
    ProviderWebhookAck,
    ProviderYtd,
    SetupBankAccountRequest,
    SetupComplete,
    SetupDocumentList,
    SetupDocumentSignRequest,
    SetupPayScheduleRequest,
    SetupState,
    SetupStepRequest,
    TeamFamily,
    TeamFamilyRequest,
    TeamList,
    TimesheetCreateRequest,
    TimesheetDetail,
    TimesheetEntriesUpdateRequest,
    TimesheetPage,
    TimesheetRejectRequest,
    WorkLocationCreateAccepted,
    WorkLocationCreateRequest,
    WorkLocationList,
    WorkerBankAccountList,
    WorkerConfirmRequest,
    WorkerDetail,
    WorkerGarnishmentList,
    WorkerInvitationList,
    WorkerInviteRequest,
    WorkerOnboardingLink,
    WorkerPaymentMethod,
    WorkerPaymentMethodUpdateRequest,
    WorkerTerminateRequest,
} from './model';

import { payrollApiMutator } from '../../payrollApiMutator';
import type { ErrorType } from '../../payrollApiMutator';
import payrollQueryDefaults from '../../payrollQueryDefaults';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
    const result = { queryKey } as T & { queryKey: K };
    for (const key of Object.keys(query)) {
        // The explicit queryKey always wins, matching the previous
        // `{ ...query, queryKey }` spread where it was set last.
        if (key === 'queryKey') continue;
        Object.defineProperty(result, key, {
            enumerable: true,
            configurable: true,
            get: () => (query as Record<string, unknown>)[key],
        });
    }
    return result;
};

/**
 * Open alerts for the member's payroll company: `blocking` first, then `warning`, then `info`, oldest first within each severity. An alert that is still true keeps its row and its `opened_at`.
 *
 * `sync` reports how fresh the data behind the list is. An empty `results` beside a stale entity does not mean nothing needs attention.
 *
 * Paged with `limit` and `offset`; follow `next` and `previous`. `count` and `blocking_count` describe every open alert, not only the page.
 * @summary List alerts needing attention
 */
export const payrollAlertsList = (
    params?: PayrollAlertsListParams,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollAlertList>(
        { url: `/api/payroll/v1/alerts/`, method: 'GET', params, signal },
        options
    );
};

export const getPayrollAlertsListQueryKey = (params?: PayrollAlertsListParams) => {
    return [`/api/payroll/v1/alerts/`, ...(params ? [params] : [])] as const;
};

export const usePayrollAlertsListQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollAlertsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollAlertsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAlertsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollAlertsListQueryKey(params);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollAlertsList>>> = ({ signal }) =>
        payrollAlertsList(params, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollAlertsList>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollAlertsListQueryResult = NonNullable<Awaited<ReturnType<typeof payrollAlertsList>>>;
export type PayrollAlertsListQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollAlertsList<
    TData = Awaited<ReturnType<typeof payrollAlertsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params: undefined | PayrollAlertsListParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAlertsList>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollAlertsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollAlertsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollAlertsList<
    TData = Awaited<ReturnType<typeof payrollAlertsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollAlertsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAlertsList>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollAlertsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollAlertsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollAlertsList<
    TData = Awaited<ReturnType<typeof payrollAlertsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollAlertsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAlertsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List alerts needing attention
 */

export function usePayrollAlertsList<
    TData = Awaited<ReturnType<typeof payrollAlertsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollAlertsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAlertsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollAlertsListQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Mint a provider flow link, select one of the alert's unblock options, or acknowledge. Minting a link does not resolve the alert — reconciliation does, when the underlying problem stops being true.
 *
 * `acknowledge` stamps `acknowledged_at` and keeps the alert in the list. Repeating it answers 200 with the first timestamp, and so does acknowledging an alert that has already cleared. `mint_provider_link` answers a fresh link every time.
 * @summary Resolve an alert
 */
export const payrollAlertResolve = (
    alertUuid: string,
    payrollAlertResolveRequest: PayrollAlertResolveRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollAlertResolve>(
        {
            url: `/api/payroll/v1/alerts/${alertUuid}/resolve/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: payrollAlertResolveRequest,
            signal,
        },
        options
    );
};

export const getPayrollAlertResolveQueryKey = (
    alertUuid: string,
    payrollAlertResolveRequest?: PayrollAlertResolveRequest
) => {
    return ['POST', `/api/payroll/v1/alerts/${alertUuid}/resolve/`, payrollAlertResolveRequest] as const;
};

export const usePayrollAlertResolveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollAlertResolve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    alertUuid: string,
    payrollAlertResolveRequest: PayrollAlertResolveRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAlertResolve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollAlertResolveQueryKey(alertUuid, payrollAlertResolveRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollAlertResolve>>> = ({ signal }) =>
        payrollAlertResolve(alertUuid, payrollAlertResolveRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: alertUuid !== null && alertUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollAlertResolve>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollAlertResolveQueryResult = NonNullable<Awaited<ReturnType<typeof payrollAlertResolve>>>;
export type PayrollAlertResolveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollAlertResolve<
    TData = Awaited<ReturnType<typeof payrollAlertResolve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    alertUuid: string,
    payrollAlertResolveRequest: PayrollAlertResolveRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAlertResolve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollAlertResolve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollAlertResolve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollAlertResolve<
    TData = Awaited<ReturnType<typeof payrollAlertResolve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    alertUuid: string,
    payrollAlertResolveRequest: PayrollAlertResolveRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAlertResolve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollAlertResolve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollAlertResolve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollAlertResolve<
    TData = Awaited<ReturnType<typeof payrollAlertResolve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    alertUuid: string,
    payrollAlertResolveRequest: PayrollAlertResolveRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAlertResolve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Resolve an alert
 */

export function usePayrollAlertResolve<
    TData = Awaited<ReturnType<typeof payrollAlertResolve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    alertUuid: string,
    payrollAlertResolveRequest: PayrollAlertResolveRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAlertResolve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollAlertResolveQueryOptions(alertUuid, payrollAlertResolveRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * AutoPayroll is configured per pay schedule, and a company may have more than one. When schedules disagree, `state` is `mixed` — an ordinary result, not an error.
 *
 * `requested_enabled` records what was asked for and `effective_enabled` whether it will actually run; they disagree when AutoPayroll has been switched off automatically, and `auto_disabled_reason` says why.
 * @summary Retrieve AutoPayroll state for each pay schedule
 */
export const payrollAutoPayrollRetrieve = (
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<AutoPayrollState>(
        { url: `/api/payroll/v1/auto-payroll/`, method: 'GET', signal },
        options
    );
};

export const getPayrollAutoPayrollRetrieveQueryKey = () => {
    return [`/api/payroll/v1/auto-payroll/`] as const;
};

export const usePayrollAutoPayrollRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollAutoPayrollRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAutoPayrollRetrieve>>, TError, TData>>;
    request?: SecondParameter<typeof payrollApiMutator>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollAutoPayrollRetrieveQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollAutoPayrollRetrieve>>> = ({ signal }) =>
        payrollAutoPayrollRetrieve(requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollAutoPayrollRetrieve>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollAutoPayrollRetrieveQueryResult = NonNullable<Awaited<ReturnType<typeof payrollAutoPayrollRetrieve>>>;
export type PayrollAutoPayrollRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollAutoPayrollRetrieve<
    TData = Awaited<ReturnType<typeof payrollAutoPayrollRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAutoPayrollRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollAutoPayrollRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollAutoPayrollRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollAutoPayrollRetrieve<
    TData = Awaited<ReturnType<typeof payrollAutoPayrollRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAutoPayrollRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollAutoPayrollRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollAutoPayrollRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollAutoPayrollRetrieve<
    TData = Awaited<ReturnType<typeof payrollAutoPayrollRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAutoPayrollRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve AutoPayroll state for each pay schedule
 */

export function usePayrollAutoPayrollRetrieve<
    TData = Awaited<ReturnType<typeof payrollAutoPayrollRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAutoPayrollRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollAutoPayrollRetrieveQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * A safety action, so it is idempotent by key: a member who clicks twice gets the first outcome back rather than a second write.
 *
 * This is a member turning AutoPayroll off, which is not the same fact as the provider turning it off. `auto_disabled_reason` is cleared here for that reason -- a reason left over from a failed debit would tell the member their provider stopped something they stopped themselves.
 *
 * A provider refusal is 422 `COMMAND_REFUSED` with `details.reason` of `provider_refused`, `message` the provider's own reason, and `details.provider_messages` the same as a list.
 * @summary Disable AutoPayroll
 */
export const payrollAutoPayrollDisable = (
    autoPayrollToggleRequest?: AutoPayrollToggleRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/auto-payroll/disable/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: autoPayrollToggleRequest,
            signal,
        },
        options
    );
};

export const getPayrollAutoPayrollDisableQueryKey = (autoPayrollToggleRequest?: AutoPayrollToggleRequest) => {
    return ['POST', `/api/payroll/v1/auto-payroll/disable/`, autoPayrollToggleRequest] as const;
};

export const usePayrollAutoPayrollDisableQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollAutoPayrollDisable>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    autoPayrollToggleRequest?: AutoPayrollToggleRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAutoPayrollDisable>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollAutoPayrollDisableQueryKey(autoPayrollToggleRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollAutoPayrollDisable>>> = ({ signal }) =>
        payrollAutoPayrollDisable(autoPayrollToggleRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollAutoPayrollDisable>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollAutoPayrollDisableQueryResult = NonNullable<Awaited<ReturnType<typeof payrollAutoPayrollDisable>>>;
export type PayrollAutoPayrollDisableQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollAutoPayrollDisable<
    TData = Awaited<ReturnType<typeof payrollAutoPayrollDisable>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    autoPayrollToggleRequest: undefined | AutoPayrollToggleRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAutoPayrollDisable>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollAutoPayrollDisable>>,
                    TError,
                    Awaited<ReturnType<typeof payrollAutoPayrollDisable>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollAutoPayrollDisable<
    TData = Awaited<ReturnType<typeof payrollAutoPayrollDisable>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    autoPayrollToggleRequest?: AutoPayrollToggleRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAutoPayrollDisable>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollAutoPayrollDisable>>,
                    TError,
                    Awaited<ReturnType<typeof payrollAutoPayrollDisable>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollAutoPayrollDisable<
    TData = Awaited<ReturnType<typeof payrollAutoPayrollDisable>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    autoPayrollToggleRequest?: AutoPayrollToggleRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAutoPayrollDisable>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Disable AutoPayroll
 */

export function usePayrollAutoPayrollDisable<
    TData = Awaited<ReturnType<typeof payrollAutoPayrollDisable>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    autoPayrollToggleRequest?: AutoPayrollToggleRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAutoPayrollDisable>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollAutoPayrollDisableQueryOptions(autoPayrollToggleRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Defaults to the primary schedule. The only feature that moves money with nobody watching, so it follows the manual loop rather than leading it, and every run it triggers still sends a reminder first and can still be stopped.
 *
 * A company with more than one pay schedule must name the one it means: AutoPayroll is per schedule, and defaulting would switch the wrong workers' payroll to unattended.
 *
 * A provider refusal is 422 `COMMAND_REFUSED` with `details.reason` of `auto_payroll_blocked`. `message` is the provider's own reason for refusing -- its error sentences joined, or the schedule's blockers spelt out when the refusal carried none -- and `details.provider_messages` is the same sentences as a list. `details.blockers` carries the schedule's enablement blockers in the shape `GET /pay-schedules/` serves them, and `details.blockers_known` says whether that list is the whole answer -- render the keyed reasons only when it is true.
 * @summary Enable AutoPayroll
 */
export const payrollAutoPayrollEnable = (
    autoPayrollToggleRequest?: AutoPayrollToggleRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/auto-payroll/enable/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: autoPayrollToggleRequest,
            signal,
        },
        options
    );
};

export const getPayrollAutoPayrollEnableQueryKey = (autoPayrollToggleRequest?: AutoPayrollToggleRequest) => {
    return ['POST', `/api/payroll/v1/auto-payroll/enable/`, autoPayrollToggleRequest] as const;
};

export const usePayrollAutoPayrollEnableQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollAutoPayrollEnable>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    autoPayrollToggleRequest?: AutoPayrollToggleRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAutoPayrollEnable>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollAutoPayrollEnableQueryKey(autoPayrollToggleRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollAutoPayrollEnable>>> = ({ signal }) =>
        payrollAutoPayrollEnable(autoPayrollToggleRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollAutoPayrollEnable>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollAutoPayrollEnableQueryResult = NonNullable<Awaited<ReturnType<typeof payrollAutoPayrollEnable>>>;
export type PayrollAutoPayrollEnableQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollAutoPayrollEnable<
    TData = Awaited<ReturnType<typeof payrollAutoPayrollEnable>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    autoPayrollToggleRequest: undefined | AutoPayrollToggleRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAutoPayrollEnable>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollAutoPayrollEnable>>,
                    TError,
                    Awaited<ReturnType<typeof payrollAutoPayrollEnable>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollAutoPayrollEnable<
    TData = Awaited<ReturnType<typeof payrollAutoPayrollEnable>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    autoPayrollToggleRequest?: AutoPayrollToggleRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAutoPayrollEnable>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollAutoPayrollEnable>>,
                    TError,
                    Awaited<ReturnType<typeof payrollAutoPayrollEnable>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollAutoPayrollEnable<
    TData = Awaited<ReturnType<typeof payrollAutoPayrollEnable>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    autoPayrollToggleRequest?: AutoPayrollToggleRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAutoPayrollEnable>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Enable AutoPayroll
 */

export function usePayrollAutoPayrollEnable<
    TData = Awaited<ReturnType<typeof payrollAutoPayrollEnable>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    autoPayrollToggleRequest?: AutoPayrollToggleRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollAutoPayrollEnable>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollAutoPayrollEnableQueryOptions(autoPayrollToggleRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Recorded server-side so a member is not re-prompted every session. A snooze must carry `snooze_until`; the prompt returns once that passes, and a dismissal stops it for good.
 *
 * Accepting records the answer only. The toggle it points at is a separate, explicit call, so an accepted prompt never turns AutoPayroll on by itself.
 * @summary Respond to an AutoPayroll nudge
 */
export const payrollNudgeRespond = (
    nudgeUuid: string,
    payrollNudgeRespondRequest: PayrollNudgeRespondRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollNudge>(
        {
            url: `/api/payroll/v1/auto-payroll/nudge/${nudgeUuid}/respond/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: payrollNudgeRespondRequest,
            signal,
        },
        options
    );
};

export const getPayrollNudgeRespondQueryKey = (
    nudgeUuid: string,
    payrollNudgeRespondRequest?: PayrollNudgeRespondRequest
) => {
    return ['POST', `/api/payroll/v1/auto-payroll/nudge/${nudgeUuid}/respond/`, payrollNudgeRespondRequest] as const;
};

export const usePayrollNudgeRespondQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollNudgeRespond>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    nudgeUuid: string,
    payrollNudgeRespondRequest: PayrollNudgeRespondRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollNudgeRespond>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollNudgeRespondQueryKey(nudgeUuid, payrollNudgeRespondRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollNudgeRespond>>> = ({ signal }) =>
        payrollNudgeRespond(nudgeUuid, payrollNudgeRespondRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: nudgeUuid !== null && nudgeUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollNudgeRespond>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollNudgeRespondQueryResult = NonNullable<Awaited<ReturnType<typeof payrollNudgeRespond>>>;
export type PayrollNudgeRespondQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollNudgeRespond<
    TData = Awaited<ReturnType<typeof payrollNudgeRespond>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    nudgeUuid: string,
    payrollNudgeRespondRequest: PayrollNudgeRespondRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollNudgeRespond>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollNudgeRespond>>,
                    TError,
                    Awaited<ReturnType<typeof payrollNudgeRespond>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollNudgeRespond<
    TData = Awaited<ReturnType<typeof payrollNudgeRespond>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    nudgeUuid: string,
    payrollNudgeRespondRequest: PayrollNudgeRespondRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollNudgeRespond>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollNudgeRespond>>,
                    TError,
                    Awaited<ReturnType<typeof payrollNudgeRespond>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollNudgeRespond<
    TData = Awaited<ReturnType<typeof payrollNudgeRespond>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    nudgeUuid: string,
    payrollNudgeRespondRequest: PayrollNudgeRespondRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollNudgeRespond>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Respond to an AutoPayroll nudge
 */

export function usePayrollNudgeRespond<
    TData = Awaited<ReturnType<typeof payrollNudgeRespond>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    nudgeUuid: string,
    payrollNudgeRespondRequest: PayrollNudgeRespondRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollNudgeRespond>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollNudgeRespondQueryOptions(nudgeUuid, payrollNudgeRespondRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Which payroll operations this company can perform today.
 *
 * A key under `supported` can be called; one under `unsupported` answers 422. Check here before offering an operation rather than calling it to find out.
 * @summary List available payroll operations
 */
export const payrollCapabilitiesRetrieve = (
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCapabilities>(
        { url: `/api/payroll/v1/capabilities/`, method: 'GET', signal },
        options
    );
};

export const getPayrollCapabilitiesRetrieveQueryKey = () => {
    return [`/api/payroll/v1/capabilities/`] as const;
};

export const usePayrollCapabilitiesRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollCapabilitiesRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCapabilitiesRetrieve>>, TError, TData>>;
    request?: SecondParameter<typeof payrollApiMutator>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollCapabilitiesRetrieveQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollCapabilitiesRetrieve>>> = ({ signal }) =>
        payrollCapabilitiesRetrieve(requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollCapabilitiesRetrieve>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollCapabilitiesRetrieveQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollCapabilitiesRetrieve>>
>;
export type PayrollCapabilitiesRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollCapabilitiesRetrieve<
    TData = Awaited<ReturnType<typeof payrollCapabilitiesRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCapabilitiesRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCapabilitiesRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCapabilitiesRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCapabilitiesRetrieve<
    TData = Awaited<ReturnType<typeof payrollCapabilitiesRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCapabilitiesRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCapabilitiesRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCapabilitiesRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCapabilitiesRetrieve<
    TData = Awaited<ReturnType<typeof payrollCapabilitiesRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCapabilitiesRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List available payroll operations
 */

export function usePayrollCapabilitiesRetrieve<
    TData = Awaited<ReturnType<typeof payrollCapabilitiesRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCapabilitiesRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollCapabilitiesRetrieveQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Polled after a 202, and after a 409 `COMMAND_IN_FLIGHT`. Never re-POST the command with a fresh idempotency key: that turns one intent into two, and one of them moves money.
 * @summary Retrieve the outcome of a command
 */
export const payrollCommandRetrieve = (
    commandUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommand>(
        { url: `/api/payroll/v1/commands/${commandUuid}/`, method: 'GET', signal },
        options
    );
};

export const getPayrollCommandRetrieveQueryKey = (commandUuid: string) => {
    return [`/api/payroll/v1/commands/${commandUuid}/`] as const;
};

export const usePayrollCommandRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollCommandRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    commandUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCommandRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollCommandRetrieveQueryKey(commandUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollCommandRetrieve>>> = ({ signal }) =>
        payrollCommandRetrieve(commandUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: commandUuid !== null && commandUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollCommandRetrieve>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollCommandRetrieveQueryResult = NonNullable<Awaited<ReturnType<typeof payrollCommandRetrieve>>>;
export type PayrollCommandRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollCommandRetrieve<
    TData = Awaited<ReturnType<typeof payrollCommandRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    commandUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCommandRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCommandRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCommandRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCommandRetrieve<
    TData = Awaited<ReturnType<typeof payrollCommandRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    commandUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCommandRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCommandRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCommandRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCommandRetrieve<
    TData = Awaited<ReturnType<typeof payrollCommandRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    commandUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCommandRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve the outcome of a command
 */

export function usePayrollCommandRetrieve<
    TData = Awaited<ReturnType<typeof payrollCommandRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    commandUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCommandRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollCommandRetrieveQueryOptions(commandUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * The latest valid benchmark and progress against it, including the on-track verdict.
 *
 * `is_on_track` and `message` are authoritative — present them as given rather than deriving a second verdict from the figures beside them.
 *
 * **404 means no verdict exists**, which is not the same as being off track: an owner who has not set a salary has nothing to be measured against.
 *
 * **A 200 can carry no verdict too.** With no payroll company, or no employee on it matched to the owner, `paid_to_date` and `is_on_track` are null and `verdict_unavailable_reason` says which. Null is not off track.
 * @summary Retrieve reasonable-compensation progress
 */
export const payrollCompensationRetrieve = (
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<CompensationReport>(
        { url: `/api/payroll/v1/compensation/`, method: 'GET', signal },
        options
    );
};

export const getPayrollCompensationRetrieveQueryKey = () => {
    return [`/api/payroll/v1/compensation/`] as const;
};

export const usePayrollCompensationRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollCompensationRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationRetrieve>>, TError, TData>>;
    request?: SecondParameter<typeof payrollApiMutator>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollCompensationRetrieveQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollCompensationRetrieve>>> = ({ signal }) =>
        payrollCompensationRetrieve(requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationRetrieve>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollCompensationRetrieveQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollCompensationRetrieve>>
>;
export type PayrollCompensationRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollCompensationRetrieve<
    TData = Awaited<ReturnType<typeof payrollCompensationRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCompensationRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCompensationRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCompensationRetrieve<
    TData = Awaited<ReturnType<typeof payrollCompensationRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCompensationRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCompensationRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCompensationRetrieve<
    TData = Awaited<ReturnType<typeof payrollCompensationRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve reasonable-compensation progress
 */

export function usePayrollCompensationRetrieve<
    TData = Awaited<ReturnType<typeof payrollCompensationRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollCompensationRetrieveQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Recomputes the benchmark and records that it was recomputed. The record is the point: a member who explores a salary change and abandons it leaves no trace today, so the next conversation starts from nothing and 'nobody uses this' cannot be told apart from 'it loses people at the last step'.
 *
 * Moves no money and takes no idempotency key — asking twice is a second look, not a duplicate write, and both looks are worth recording. Supplying `revenue` and `expenses` updates the figures the band is derived from before recomputing; expenses at or above revenue is refused rather than quietly worked around.
 * @summary Recalculate the compensation band
 */
export const payrollCompensationBenchmark = (
    compensationBenchmarkRequest?: CompensationBenchmarkRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<CompensationBenchmark>(
        {
            url: `/api/payroll/v1/compensation/benchmark/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: compensationBenchmarkRequest,
            signal,
        },
        options
    );
};

export const getPayrollCompensationBenchmarkQueryKey = (
    compensationBenchmarkRequest?: CompensationBenchmarkRequest
) => {
    return ['POST', `/api/payroll/v1/compensation/benchmark/`, compensationBenchmarkRequest] as const;
};

export const usePayrollCompensationBenchmarkQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollCompensationBenchmark>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    compensationBenchmarkRequest?: CompensationBenchmarkRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationBenchmark>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollCompensationBenchmarkQueryKey(compensationBenchmarkRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollCompensationBenchmark>>> = ({ signal }) =>
        payrollCompensationBenchmark(compensationBenchmarkRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationBenchmark>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollCompensationBenchmarkQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollCompensationBenchmark>>
>;
export type PayrollCompensationBenchmarkQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollCompensationBenchmark<
    TData = Awaited<ReturnType<typeof payrollCompensationBenchmark>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    compensationBenchmarkRequest: undefined | CompensationBenchmarkRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationBenchmark>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCompensationBenchmark>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCompensationBenchmark>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCompensationBenchmark<
    TData = Awaited<ReturnType<typeof payrollCompensationBenchmark>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    compensationBenchmarkRequest?: CompensationBenchmarkRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationBenchmark>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCompensationBenchmark>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCompensationBenchmark>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCompensationBenchmark<
    TData = Awaited<ReturnType<typeof payrollCompensationBenchmark>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    compensationBenchmarkRequest?: CompensationBenchmarkRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationBenchmark>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Recalculate the compensation band
 */

export function usePayrollCompensationBenchmark<
    TData = Awaited<ReturnType<typeof payrollCompensationBenchmark>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    compensationBenchmarkRequest?: CompensationBenchmarkRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationBenchmark>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollCompensationBenchmarkQueryOptions(compensationBenchmarkRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Creates an off-cycle run that pays the owner toward the difference between what an even schedule would have paid by now and what they have actually been paid, and funds it in the same command — a created off-cycle run is otherwise empty.
 *
 * **Payable in instalments.** `amount` is how much of the shortfall to pay now, not a confirmation of the whole of it: a member far enough behind may not be able to fund the full run at once. Anything from a cent up to what is still owed is accepted; more than that is refused as `catch_up_amount_too_large`.
 *
 * **What is still owed already nets off catch-up runs that have not paid yet**, so `GET /compensation/` and this endpoint agree and repeating the call walks the gap down rather than re-spending it. Once the whole gap is committed, further requests answer `nothing_to_catch_up`.
 *
 * The run is left as a **draft**. It pays the catch-up once submitted, which is a separate command, so this endpoint never moves money on its own.
 * @summary Create a catch-up payment
 */
export const payrollCompensationCatchUp = (
    compensationCatchUpRequest: CompensationCatchUpRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/compensation/catch-up/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: compensationCatchUpRequest,
            signal,
        },
        options
    );
};

export const getPayrollCompensationCatchUpQueryKey = (compensationCatchUpRequest?: CompensationCatchUpRequest) => {
    return ['POST', `/api/payroll/v1/compensation/catch-up/`, compensationCatchUpRequest] as const;
};

export const usePayrollCompensationCatchUpQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollCompensationCatchUp>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    compensationCatchUpRequest: CompensationCatchUpRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationCatchUp>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollCompensationCatchUpQueryKey(compensationCatchUpRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollCompensationCatchUp>>> = ({ signal }) =>
        payrollCompensationCatchUp(compensationCatchUpRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationCatchUp>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollCompensationCatchUpQueryResult = NonNullable<Awaited<ReturnType<typeof payrollCompensationCatchUp>>>;
export type PayrollCompensationCatchUpQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollCompensationCatchUp<
    TData = Awaited<ReturnType<typeof payrollCompensationCatchUp>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    compensationCatchUpRequest: CompensationCatchUpRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationCatchUp>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCompensationCatchUp>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCompensationCatchUp>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCompensationCatchUp<
    TData = Awaited<ReturnType<typeof payrollCompensationCatchUp>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    compensationCatchUpRequest: CompensationCatchUpRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationCatchUp>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCompensationCatchUp>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCompensationCatchUp>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCompensationCatchUp<
    TData = Awaited<ReturnType<typeof payrollCompensationCatchUp>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    compensationCatchUpRequest: CompensationCatchUpRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationCatchUp>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create a catch-up payment
 */

export function usePayrollCompensationCatchUp<
    TData = Awaited<ReturnType<typeof payrollCompensationCatchUp>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    compensationCatchUpRequest: CompensationCatchUpRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationCatchUp>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollCompensationCatchUpQueryOptions(compensationCatchUpRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * The owner's own direct-deposit arrangement, for the compensation page.
 *
 * **A live read**, like the per-worker route it shares a service with. Owner-scoped on purpose: the compensation page knows the member, not a worker uuid, and making it read the whole team roster to find one row would be a page-load cost and a second place the owner could be picked wrongly.
 *
 * **An empty `results` with `count` of `0` is the ordinary mid-onboarding answer**, and so is a member whose owner employee is not identified yet. Render either as "not set up yet" rather than as an error.
 *
 * **Carries no routing number and no institution name.** The provider sends a full `routing_number` on this resource and it is dropped at the raw schema, so it is absent here by construction. No institution id or brand exists on the resource at all, so a client wanting a mark has nothing here to key one off. `name` is the worker's own nickname for the account, and `hidden_account_number` is the provider's mask — the reference documents no masking format, so render it as given rather than cutting a last four out of it.
 * @summary Retrieve the owner's deposit account
 */
export const payrollCompensationDepositAccount = (
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<WorkerBankAccountList>(
        { url: `/api/payroll/v1/compensation/deposit-account/`, method: 'GET', signal },
        options
    );
};

export const getPayrollCompensationDepositAccountQueryKey = () => {
    return [`/api/payroll/v1/compensation/deposit-account/`] as const;
};

export const usePayrollCompensationDepositAccountQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollCompensationDepositAccount>>,
    TError = ErrorType<PayrollErrorResponse>
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationDepositAccount>>, TError, TData>>;
    request?: SecondParameter<typeof payrollApiMutator>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollCompensationDepositAccountQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollCompensationDepositAccount>>> = ({ signal }) =>
        payrollCompensationDepositAccount(requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollCompensationDepositAccount>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollCompensationDepositAccountQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollCompensationDepositAccount>>
>;
export type PayrollCompensationDepositAccountQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollCompensationDepositAccount<
    TData = Awaited<ReturnType<typeof payrollCompensationDepositAccount>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationDepositAccount>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCompensationDepositAccount>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCompensationDepositAccount>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCompensationDepositAccount<
    TData = Awaited<ReturnType<typeof payrollCompensationDepositAccount>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationDepositAccount>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCompensationDepositAccount>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCompensationDepositAccount>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCompensationDepositAccount<
    TData = Awaited<ReturnType<typeof payrollCompensationDepositAccount>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationDepositAccount>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve the owner's deposit account
 */

export function usePayrollCompensationDepositAccount<
    TData = Awaited<ReturnType<typeof payrollCompensationDepositAccount>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationDepositAccount>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollCompensationDepositAccountQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Point your own pay at accounts the provider already holds. The owner-scoped counterpart of the team route, on the same path the deposit card is read from.
 *
 * **Carries no routing number and no account number.** Each account is named by the id this route's `GET` returned for it. Registering a *new* account is a different operation and is not served here.
 *
 * `check` clears the splits. A `direct_deposit` needs `split_by` and at least one split: percentages must total 100, and an `amount` split needs exactly one account with a null amount to take the remainder.
 *
 * 404 when no person on your payroll could be identified as you — resolved by support, not by retrying.
 * @summary Move your own pay to a different account
 */
export const payrollCompensationDepositAccountUpdate = (
    workerPaymentMethodUpdateRequest: WorkerPaymentMethodUpdateRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/compensation/deposit-account/`,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            data: workerPaymentMethodUpdateRequest,
            signal,
        },
        options
    );
};

export const getPayrollCompensationDepositAccountUpdateQueryKey = (
    workerPaymentMethodUpdateRequest?: WorkerPaymentMethodUpdateRequest
) => {
    return ['PUT', `/api/payroll/v1/compensation/deposit-account/`, workerPaymentMethodUpdateRequest] as const;
};

export const usePayrollCompensationDepositAccountUpdateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollCompensationDepositAccountUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerPaymentMethodUpdateRequest: WorkerPaymentMethodUpdateRequest,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationDepositAccountUpdate>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getPayrollCompensationDepositAccountUpdateQueryKey(workerPaymentMethodUpdateRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollCompensationDepositAccountUpdate>>> = ({ signal }) =>
        payrollCompensationDepositAccountUpdate(workerPaymentMethodUpdateRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollCompensationDepositAccountUpdate>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollCompensationDepositAccountUpdateQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollCompensationDepositAccountUpdate>>
>;
export type PayrollCompensationDepositAccountUpdateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollCompensationDepositAccountUpdate<
    TData = Awaited<ReturnType<typeof payrollCompensationDepositAccountUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerPaymentMethodUpdateRequest: WorkerPaymentMethodUpdateRequest,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationDepositAccountUpdate>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCompensationDepositAccountUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCompensationDepositAccountUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCompensationDepositAccountUpdate<
    TData = Awaited<ReturnType<typeof payrollCompensationDepositAccountUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerPaymentMethodUpdateRequest: WorkerPaymentMethodUpdateRequest,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationDepositAccountUpdate>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCompensationDepositAccountUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCompensationDepositAccountUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCompensationDepositAccountUpdate<
    TData = Awaited<ReturnType<typeof payrollCompensationDepositAccountUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerPaymentMethodUpdateRequest: WorkerPaymentMethodUpdateRequest,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationDepositAccountUpdate>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Move your own pay to a different account
 */

export function usePayrollCompensationDepositAccountUpdate<
    TData = Awaited<ReturnType<typeof payrollCompensationDepositAccountUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerPaymentMethodUpdateRequest: WorkerPaymentMethodUpdateRequest,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationDepositAccountUpdate>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollCompensationDepositAccountUpdateQueryOptions(
        workerPaymentMethodUpdateRequest,
        options
    );

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Base for every stub: the live views' auth and member scoping, and no handler body.
 *
 * Subclasses set :attr:`operation` and implement their verbs as ``raise
 * self.not_implemented()``. The verb methods exist so an unsupported verb answers 405.
 * @summary Retrieve the financial data behind the band
 */
export const payrollCompensationFinancialDataRetrieve = (
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<CompensationFinancialData>(
        { url: `/api/payroll/v1/compensation/financial-data/`, method: 'GET', signal },
        options
    );
};

export const getPayrollCompensationFinancialDataRetrieveQueryKey = () => {
    return [`/api/payroll/v1/compensation/financial-data/`] as const;
};

export const usePayrollCompensationFinancialDataRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollCompensationFinancialDataRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(options?: {
    query?: Partial<
        UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationFinancialDataRetrieve>>, TError, TData>
    >;
    request?: SecondParameter<typeof payrollApiMutator>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollCompensationFinancialDataRetrieveQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollCompensationFinancialDataRetrieve>>> = ({ signal }) =>
        payrollCompensationFinancialDataRetrieve(requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollCompensationFinancialDataRetrieve>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollCompensationFinancialDataRetrieveQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollCompensationFinancialDataRetrieve>>
>;
export type PayrollCompensationFinancialDataRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollCompensationFinancialDataRetrieve<
    TData = Awaited<ReturnType<typeof payrollCompensationFinancialDataRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationFinancialDataRetrieve>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCompensationFinancialDataRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCompensationFinancialDataRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCompensationFinancialDataRetrieve<
    TData = Awaited<ReturnType<typeof payrollCompensationFinancialDataRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationFinancialDataRetrieve>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCompensationFinancialDataRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCompensationFinancialDataRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCompensationFinancialDataRetrieve<
    TData = Awaited<ReturnType<typeof payrollCompensationFinancialDataRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationFinancialDataRetrieve>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve the financial data behind the band
 */

export function usePayrollCompensationFinancialDataRetrieve<
    TData = Awaited<ReturnType<typeof payrollCompensationFinancialDataRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationFinancialDataRetrieve>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollCompensationFinancialDataRetrieveQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Base for every stub: the live views' auth and member scoping, and no handler body.
 *
 * Subclasses set :attr:`operation` and implement their verbs as ``raise
 * self.not_implemented()``. The verb methods exist so an unsupported verb answers 405.
 * @summary Update revenue and expenses, and recalculate the band
 */
export const payrollCompensationFinancialDataUpdate = (
    compensationFinancialData: CompensationFinancialData,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<CompensationBenchmark>(
        {
            url: `/api/payroll/v1/compensation/financial-data/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: compensationFinancialData,
            signal,
        },
        options
    );
};

export const getPayrollCompensationFinancialDataUpdateQueryKey = (
    compensationFinancialData?: CompensationFinancialData
) => {
    return ['POST', `/api/payroll/v1/compensation/financial-data/`, compensationFinancialData] as const;
};

export const usePayrollCompensationFinancialDataUpdateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollCompensationFinancialDataUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    compensationFinancialData: CompensationFinancialData,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationFinancialDataUpdate>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getPayrollCompensationFinancialDataUpdateQueryKey(compensationFinancialData);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollCompensationFinancialDataUpdate>>> = ({ signal }) =>
        payrollCompensationFinancialDataUpdate(compensationFinancialData, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollCompensationFinancialDataUpdate>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollCompensationFinancialDataUpdateQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollCompensationFinancialDataUpdate>>
>;
export type PayrollCompensationFinancialDataUpdateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollCompensationFinancialDataUpdate<
    TData = Awaited<ReturnType<typeof payrollCompensationFinancialDataUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    compensationFinancialData: CompensationFinancialData,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationFinancialDataUpdate>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCompensationFinancialDataUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCompensationFinancialDataUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCompensationFinancialDataUpdate<
    TData = Awaited<ReturnType<typeof payrollCompensationFinancialDataUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    compensationFinancialData: CompensationFinancialData,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationFinancialDataUpdate>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCompensationFinancialDataUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCompensationFinancialDataUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCompensationFinancialDataUpdate<
    TData = Awaited<ReturnType<typeof payrollCompensationFinancialDataUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    compensationFinancialData: CompensationFinancialData,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationFinancialDataUpdate>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update revenue and expenses, and recalculate the band
 */

export function usePayrollCompensationFinancialDataUpdate<
    TData = Awaited<ReturnType<typeof payrollCompensationFinancialDataUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    compensationFinancialData: CompensationFinancialData,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationFinancialDataUpdate>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollCompensationFinancialDataUpdateQueryOptions(compensationFinancialData, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * `pushed_to_provider_at` is set only on a confirmed write, never on the attempt. A salary we recorded and failed to push is a member who believes they changed their pay and did not — so the choice is stored after the provider agrees, and the command row is what support reads to answer 'did this actually reach the provider'.
 *
 * The provider holds one active compensation per job and compare-and-swaps on its version, so the current token is read immediately before the write rather than cached.
 * @summary Update the owner's salary
 */
export const payrollCompensationSalaryUpdate = (
    patchedCompensationSalaryRequest?: PatchedCompensationSalaryRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/compensation/salary/`,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            data: patchedCompensationSalaryRequest,
            signal,
        },
        options
    );
};

export const getPayrollCompensationSalaryUpdateQueryKey = (
    patchedCompensationSalaryRequest?: PatchedCompensationSalaryRequest
) => {
    return ['PATCH', `/api/payroll/v1/compensation/salary/`, patchedCompensationSalaryRequest] as const;
};

export const usePayrollCompensationSalaryUpdateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollCompensationSalaryUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    patchedCompensationSalaryRequest?: PatchedCompensationSalaryRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationSalaryUpdate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getPayrollCompensationSalaryUpdateQueryKey(patchedCompensationSalaryRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollCompensationSalaryUpdate>>> = ({ signal }) =>
        payrollCompensationSalaryUpdate(patchedCompensationSalaryRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollCompensationSalaryUpdate>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollCompensationSalaryUpdateQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollCompensationSalaryUpdate>>
>;
export type PayrollCompensationSalaryUpdateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollCompensationSalaryUpdate<
    TData = Awaited<ReturnType<typeof payrollCompensationSalaryUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    patchedCompensationSalaryRequest: undefined | PatchedCompensationSalaryRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationSalaryUpdate>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCompensationSalaryUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCompensationSalaryUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCompensationSalaryUpdate<
    TData = Awaited<ReturnType<typeof payrollCompensationSalaryUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    patchedCompensationSalaryRequest?: PatchedCompensationSalaryRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationSalaryUpdate>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCompensationSalaryUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCompensationSalaryUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCompensationSalaryUpdate<
    TData = Awaited<ReturnType<typeof payrollCompensationSalaryUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    patchedCompensationSalaryRequest?: PatchedCompensationSalaryRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationSalaryUpdate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update the owner's salary
 */

export function usePayrollCompensationSalaryUpdate<
    TData = Awaited<ReturnType<typeof payrollCompensationSalaryUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    patchedCompensationSalaryRequest?: PatchedCompensationSalaryRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationSalaryUpdate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollCompensationSalaryUpdateQueryOptions(patchedCompensationSalaryRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Records that the owner reviewed their salary and is keeping it, which counts as a recent choice and closes the reminder to review it.
 *
 * Nothing is sent to the payroll provider — the salary is unchanged, so no idempotency key is taken and confirming twice just records the later review.
 *
 * **404 when no salary has been chosen yet**, on the same terms as `GET /compensation/`: offer this only when that read returns 200.
 * @summary Keep the owner's current salary
 */
export const payrollCompensationSalaryConfirm = (
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<void>(
        { url: `/api/payroll/v1/compensation/salary/confirm/`, method: 'POST', signal },
        options
    );
};

export const getPayrollCompensationSalaryConfirmQueryKey = () => {
    return ['POST', `/api/payroll/v1/compensation/salary/confirm/`] as const;
};

export const usePayrollCompensationSalaryConfirmQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollCompensationSalaryConfirm>>,
    TError = ErrorType<PayrollErrorResponse>
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationSalaryConfirm>>, TError, TData>>;
    request?: SecondParameter<typeof payrollApiMutator>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollCompensationSalaryConfirmQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollCompensationSalaryConfirm>>> = ({ signal }) =>
        payrollCompensationSalaryConfirm(requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollCompensationSalaryConfirm>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollCompensationSalaryConfirmQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollCompensationSalaryConfirm>>
>;
export type PayrollCompensationSalaryConfirmQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollCompensationSalaryConfirm<
    TData = Awaited<ReturnType<typeof payrollCompensationSalaryConfirm>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationSalaryConfirm>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCompensationSalaryConfirm>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCompensationSalaryConfirm>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCompensationSalaryConfirm<
    TData = Awaited<ReturnType<typeof payrollCompensationSalaryConfirm>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationSalaryConfirm>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollCompensationSalaryConfirm>>,
                    TError,
                    Awaited<ReturnType<typeof payrollCompensationSalaryConfirm>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollCompensationSalaryConfirm<
    TData = Awaited<ReturnType<typeof payrollCompensationSalaryConfirm>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationSalaryConfirm>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Keep the owner's current salary
 */

export function usePayrollCompensationSalaryConfirm<
    TData = Awaited<ReturnType<typeof payrollCompensationSalaryConfirm>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollCompensationSalaryConfirm>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollCompensationSalaryConfirmQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * A batch and the payments funded by its single debit. Read live, because the batch detail is the only place the provider states which payments belong to it -- no payment payload carries a batch reference.
 *
 * `may_cancel` is true only while **every** payment in the batch can still be cancelled: the provider cancels batches whole, so one settled payment closes the window for all of them.
 * @summary Retrieve a contractor payment group
 */
export const payrollContractorPaymentGroupRetrieve = (
    groupUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<ContractorPaymentGroup>(
        { url: `/api/payroll/v1/contractor-payment-groups/${groupUuid}/`, method: 'GET', signal },
        options
    );
};

export const getPayrollContractorPaymentGroupRetrieveQueryKey = (groupUuid: string) => {
    return [`/api/payroll/v1/contractor-payment-groups/${groupUuid}/`] as const;
};

export const usePayrollContractorPaymentGroupRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollContractorPaymentGroupRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    groupUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentGroupRetrieve>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollContractorPaymentGroupRetrieveQueryKey(groupUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollContractorPaymentGroupRetrieve>>> = ({ signal }) =>
        payrollContractorPaymentGroupRetrieve(groupUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: groupUuid !== null && groupUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollContractorPaymentGroupRetrieve>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollContractorPaymentGroupRetrieveQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollContractorPaymentGroupRetrieve>>
>;
export type PayrollContractorPaymentGroupRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollContractorPaymentGroupRetrieve<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentGroupRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    groupUuid: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentGroupRetrieve>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorPaymentGroupRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorPaymentGroupRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorPaymentGroupRetrieve<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentGroupRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    groupUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentGroupRetrieve>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorPaymentGroupRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorPaymentGroupRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorPaymentGroupRetrieve<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentGroupRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    groupUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentGroupRetrieve>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve a contractor payment group
 */

export function usePayrollContractorPaymentGroupRetrieve<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentGroupRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    groupUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentGroupRetrieve>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollContractorPaymentGroupRetrieveQueryOptions(groupUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Cancels the batch and every payment in it, in **one** provider call. Not the same as cancelling each payment in turn: the provider applies this atomically, whereas a loop over the per-payment route leaves half a batch cancelled the moment one call fails.
 *
 * Gated on the batch's `may_cancel`, which is true only while **every** payment in it can still be cancelled -- the provider requires the whole batch to be unfunded, so one settled payment closes the window for all of them. A batch that can no longer be cancelled is 422 `COMMAND_REFUSED` with `details.reason` of `not_cancellable`.
 *
 * Accepted rather than done: poll `GET /commands/{command_uuid}/` for the outcome.
 * @summary Cancel a whole batch
 */
export const payrollContractorPaymentGroupCancel = (
    groupUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        { url: `/api/payroll/v1/contractor-payment-groups/${groupUuid}/cancel/`, method: 'POST', signal },
        options
    );
};

export const getPayrollContractorPaymentGroupCancelQueryKey = (groupUuid: string) => {
    return ['POST', `/api/payroll/v1/contractor-payment-groups/${groupUuid}/cancel/`] as const;
};

export const usePayrollContractorPaymentGroupCancelQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollContractorPaymentGroupCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    groupUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentGroupCancel>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollContractorPaymentGroupCancelQueryKey(groupUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollContractorPaymentGroupCancel>>> = ({ signal }) =>
        payrollContractorPaymentGroupCancel(groupUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: groupUuid !== null && groupUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollContractorPaymentGroupCancel>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollContractorPaymentGroupCancelQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollContractorPaymentGroupCancel>>
>;
export type PayrollContractorPaymentGroupCancelQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollContractorPaymentGroupCancel<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentGroupCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    groupUuid: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentGroupCancel>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorPaymentGroupCancel>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorPaymentGroupCancel>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorPaymentGroupCancel<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentGroupCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    groupUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentGroupCancel>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorPaymentGroupCancel>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorPaymentGroupCancel>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorPaymentGroupCancel<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentGroupCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    groupUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentGroupCancel>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Cancel a whole batch
 */

export function usePayrollContractorPaymentGroupCancel<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentGroupCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    groupUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentGroupCancel>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollContractorPaymentGroupCancelQueryOptions(groupUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Contractor payments, newest first. Three columns and no withholding -- a contractor payment carries no line rows, and never appears in a payroll run.
 *
 * An omitted window reads two years either side of today, which covers the current and prior tax year in full plus payments scheduled ahead; a `start_date` alone reads forward two years from `max(today, start_date)`, scheduled payments included. `contractor_uuid` and `status` filter the window rather than widening it.
 *
 * **`contractor_payment_uuid` is null on a row with no detail route yet.** The payment is real and is listed; only the per-payment read is unavailable. Rows in that state appear in `provider` mode only.
 * @summary List contractor payments
 */
export const payrollContractorPaymentsList = (
    params?: PayrollContractorPaymentsListParams,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<ContractorPaymentList>(
        { url: `/api/payroll/v1/contractor-payments/`, method: 'GET', params, signal },
        options
    );
};

export const getPayrollContractorPaymentsListQueryKey = (params?: PayrollContractorPaymentsListParams) => {
    return [`/api/payroll/v1/contractor-payments/`, ...(params ? [params] : [])] as const;
};

export const usePayrollContractorPaymentsListQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollContractorPaymentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollContractorPaymentsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollContractorPaymentsListQueryKey(params);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollContractorPaymentsList>>> = ({ signal }) =>
        payrollContractorPaymentsList(params, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollContractorPaymentsList>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollContractorPaymentsListQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollContractorPaymentsList>>
>;
export type PayrollContractorPaymentsListQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollContractorPaymentsList<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params: undefined | PayrollContractorPaymentsListParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentsList>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorPaymentsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorPaymentsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorPaymentsList<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollContractorPaymentsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentsList>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorPaymentsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorPaymentsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorPaymentsList<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollContractorPaymentsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List contractor payments
 */

export function usePayrollContractorPaymentsList<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollContractorPaymentsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollContractorPaymentsListQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Always creates a group, even for a single payment, so the funding warning covers the whole debit rather than one line of it.
 *
 * Which money field a line carries is decided by the **contractor's own** wage type, not by the request: an hourly contractor takes `hours` and a fixed one takes `wage`. Sending the other is 422 `COMMAND_REFUSED` with `details.reason` of `wage_type_mismatch`, naming the person -- along with `unknown_contractors`, `hours_required`, `wage_required` and `empty_batch`.
 *
 * Refused with `details.reason` of `payroll_blocked` and `details.blockers` while `GET /overview/`'s `money_operations_blocked_by` is non-empty, the same refusal a blocked run gets.
 *
 * Idempotent twice over: the `Idempotency-Key` stops a repeat reaching the provider, and a token derived from it stops the provider paying a request whose outcome we never learned. A retry under the same key never pays twice.
 *
 * If `POST /contractor-payments/preview/` reported `submission_blockers`, send the member's chosen options back here in `submission_blockers`. Leaving one unanswered is 422 -- the provider refuses the batch rather than paying around the blocker. Answering the same blocker twice is 422 `COMMAND_REFUSED` with `details.reason` of `duplicate_blocker_selection`, because which answer would win is undefined.
 *
 * A batch the provider itself refuses -- an unanswered blocker, an option that blocker never offered -- is 422 `BATCH_REFUSED` with `details.reason` of `provider_refused`. Preview again to see the current blockers and their options.
 * @summary Create contractor payments
 */
export const payrollContractorPaymentsCreate = (
    contractorPaymentCreateRequest: ContractorPaymentCreateRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/contractor-payments/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: contractorPaymentCreateRequest,
            signal,
        },
        options
    );
};

export const getPayrollContractorPaymentsCreateQueryKey = (
    contractorPaymentCreateRequest?: ContractorPaymentCreateRequest
) => {
    return ['POST', `/api/payroll/v1/contractor-payments/`, contractorPaymentCreateRequest] as const;
};

export const usePayrollContractorPaymentsCreateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollContractorPaymentsCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentCreateRequest: ContractorPaymentCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentsCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getPayrollContractorPaymentsCreateQueryKey(contractorPaymentCreateRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollContractorPaymentsCreate>>> = ({ signal }) =>
        payrollContractorPaymentsCreate(contractorPaymentCreateRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollContractorPaymentsCreate>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollContractorPaymentsCreateQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollContractorPaymentsCreate>>
>;
export type PayrollContractorPaymentsCreateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollContractorPaymentsCreate<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentsCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentCreateRequest: ContractorPaymentCreateRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentsCreate>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorPaymentsCreate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorPaymentsCreate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorPaymentsCreate<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentsCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentCreateRequest: ContractorPaymentCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentsCreate>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorPaymentsCreate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorPaymentsCreate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorPaymentsCreate<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentsCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentCreateRequest: ContractorPaymentCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentsCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create contractor payments
 */

export function usePayrollContractorPaymentsCreate<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentsCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentCreateRequest: ContractorPaymentCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentsCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollContractorPaymentsCreateQueryOptions(contractorPaymentCreateRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * No side effect, so it is safe to call on every form change. Returns the batch total, the debit date and the funding check for the whole debit -- three payments that each clear individually can together exceed the balance, which is why the group is the primitive.
 *
 * Every figure is the provider's. A client-computed total is a discrepancy waiting to happen, so nothing here is derived on this side.
 *
 * `funding_sufficient` is null when the live balance could not be established, which is **not** the same as insufficient -- a member is never told a payment will bounce because a bank read timed out.
 *
 * Validation matches the create, so a form that previews cleanly will not be refused on submit for a reason it could have shown.
 * @summary Preview contractor payments without creating them
 */
export const payrollContractorPaymentsPreview = (
    contractorPaymentPreviewRequest: ContractorPaymentPreviewRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<ContractorPaymentPreview>(
        {
            url: `/api/payroll/v1/contractor-payments/preview/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: contractorPaymentPreviewRequest,
            signal,
        },
        options
    );
};

export const getPayrollContractorPaymentsPreviewQueryKey = (
    contractorPaymentPreviewRequest?: ContractorPaymentPreviewRequest
) => {
    return ['POST', `/api/payroll/v1/contractor-payments/preview/`, contractorPaymentPreviewRequest] as const;
};

export const usePayrollContractorPaymentsPreviewQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollContractorPaymentsPreview>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentPreviewRequest: ContractorPaymentPreviewRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentsPreview>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getPayrollContractorPaymentsPreviewQueryKey(contractorPaymentPreviewRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollContractorPaymentsPreview>>> = ({ signal }) =>
        payrollContractorPaymentsPreview(contractorPaymentPreviewRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollContractorPaymentsPreview>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollContractorPaymentsPreviewQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollContractorPaymentsPreview>>
>;
export type PayrollContractorPaymentsPreviewQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollContractorPaymentsPreview<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentsPreview>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentPreviewRequest: ContractorPaymentPreviewRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentsPreview>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorPaymentsPreview>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorPaymentsPreview>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorPaymentsPreview<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentsPreview>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentPreviewRequest: ContractorPaymentPreviewRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentsPreview>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorPaymentsPreview>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorPaymentsPreview>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorPaymentsPreview<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentsPreview>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentPreviewRequest: ContractorPaymentPreviewRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentsPreview>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Preview contractor payments without creating them
 */

export function usePayrollContractorPaymentsPreview<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentsPreview>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentPreviewRequest: ContractorPaymentPreviewRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentsPreview>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollContractorPaymentsPreviewQueryOptions(contractorPaymentPreviewRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Three columns and no withholding -- contractor payments carry no line rows. Served from our own mirror of the payment, projected through the same shape the list uses.
 * @summary Retrieve a contractor payment
 */
export const payrollContractorPaymentRetrieve = (
    contractorPaymentUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<ContractorPayment>(
        { url: `/api/payroll/v1/contractor-payments/${contractorPaymentUuid}/`, method: 'GET', signal },
        options
    );
};

export const getPayrollContractorPaymentRetrieveQueryKey = (contractorPaymentUuid: string) => {
    return [`/api/payroll/v1/contractor-payments/${contractorPaymentUuid}/`] as const;
};

export const usePayrollContractorPaymentRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollContractorPaymentRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollContractorPaymentRetrieveQueryKey(contractorPaymentUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollContractorPaymentRetrieve>>> = ({ signal }) =>
        payrollContractorPaymentRetrieve(contractorPaymentUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: contractorPaymentUuid !== null && contractorPaymentUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollContractorPaymentRetrieve>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollContractorPaymentRetrieveQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollContractorPaymentRetrieve>>
>;
export type PayrollContractorPaymentRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollContractorPaymentRetrieve<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorPaymentRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorPaymentRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorPaymentRetrieve<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorPaymentRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorPaymentRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorPaymentRetrieve<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve a contractor payment
 */

export function usePayrollContractorPaymentRetrieve<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollContractorPaymentRetrieveQueryOptions(contractorPaymentUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Cancels **this payment only**, leaving the rest of its batch alone. To cancel a whole batch use `POST /contractor-payment-groups/{group_uuid}/cancel/`, which the provider applies atomically -- looping over this route instead leaves half a batch cancelled when one call fails, with no way to put the rest back.
 *
 * Gated on the provider's own `may_cancel`, never on a date we compute -- unlike a payroll run, the contractor rail has no submission deadline. A direct deposit stays cancellable until the provider says otherwise; a cheque or historical payment stays cancellable indefinitely, because no money moved by ACH. A payment that can no longer be cancelled is 422 `COMMAND_REFUSED` with `details.reason` of `not_cancellable`.
 *
 * Accepted rather than done: the provider answers immediately while the payment settles out of band, so poll `GET /commands/{command_uuid}/` for the outcome.
 * @summary Cancel a contractor payment
 */
export const payrollContractorPaymentCancel = (
    contractorPaymentUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        { url: `/api/payroll/v1/contractor-payments/${contractorPaymentUuid}/cancel/`, method: 'POST', signal },
        options
    );
};

export const getPayrollContractorPaymentCancelQueryKey = (contractorPaymentUuid: string) => {
    return ['POST', `/api/payroll/v1/contractor-payments/${contractorPaymentUuid}/cancel/`] as const;
};

export const usePayrollContractorPaymentCancelQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollContractorPaymentCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentCancel>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollContractorPaymentCancelQueryKey(contractorPaymentUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollContractorPaymentCancel>>> = ({ signal }) =>
        payrollContractorPaymentCancel(contractorPaymentUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: contractorPaymentUuid !== null && contractorPaymentUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollContractorPaymentCancel>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollContractorPaymentCancelQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollContractorPaymentCancel>>
>;
export type PayrollContractorPaymentCancelQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollContractorPaymentCancel<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentCancel>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorPaymentCancel>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorPaymentCancel>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorPaymentCancel<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentCancel>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorPaymentCancel>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorPaymentCancel>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorPaymentCancel<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentCancel>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Cancel a contractor payment
 */

export function usePayrollContractorPaymentCancel<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentCancel>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollContractorPaymentCancelQueryOptions(contractorPaymentUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * The same receipt as `/receipt/`, as a PDF. Streamed straight from the provider and never stored: we hold the reference, not the bytes.
 *
 * Same availability rule -- only a funded direct deposit has one.
 * @summary Download a contractor payment receipt as PDF
 */
export const payrollContractorPaymentPdfRetrieve = (
    contractorPaymentUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<Blob>(
        {
            url: `/api/payroll/v1/contractor-payments/${contractorPaymentUuid}/pdf/`,
            method: 'GET',
            responseType: 'blob',
            signal,
        },
        options
    );
};

export const getPayrollContractorPaymentPdfRetrieveQueryKey = (contractorPaymentUuid: string) => {
    return [`/api/payroll/v1/contractor-payments/${contractorPaymentUuid}/pdf/`] as const;
};

export const usePayrollContractorPaymentPdfRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollContractorPaymentPdfRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentPdfRetrieve>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollContractorPaymentPdfRetrieveQueryKey(contractorPaymentUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollContractorPaymentPdfRetrieve>>> = ({ signal }) =>
        payrollContractorPaymentPdfRetrieve(contractorPaymentUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: contractorPaymentUuid !== null && contractorPaymentUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollContractorPaymentPdfRetrieve>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollContractorPaymentPdfRetrieveQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollContractorPaymentPdfRetrieve>>
>;
export type PayrollContractorPaymentPdfRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollContractorPaymentPdfRetrieve<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentPdfRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentPdfRetrieve>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorPaymentPdfRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorPaymentPdfRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorPaymentPdfRetrieve<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentPdfRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentPdfRetrieve>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorPaymentPdfRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorPaymentPdfRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorPaymentPdfRetrieve<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentPdfRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentPdfRetrieve>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Download a contractor payment receipt as PDF
 */

export function usePayrollContractorPaymentPdfRetrieve<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentPdfRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentPdfRetrieve>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollContractorPaymentPdfRetrieveQueryOptions(contractorPaymentUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * The money-transmitter receipt for a contractor payment. A live read.
 *
 * Only a **funded direct deposit** has one. A cheque, or a deposit still in flight, answers 404 `RECEIPT_UNAVAILABLE` — the payment exists and no receipt does yet, so retry once it has funded rather than treating it as a missing payment.
 *
 * `payment_date` is the **debit** date -- when the money left the account, which differs from the check date by the company's ACH speed.
 * @summary Retrieve a contractor payment receipt
 */
export const payrollContractorPaymentReceiptRetrieve = (
    contractorPaymentUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<ContractorPaymentReceipt>(
        { url: `/api/payroll/v1/contractor-payments/${contractorPaymentUuid}/receipt/`, method: 'GET', signal },
        options
    );
};

export const getPayrollContractorPaymentReceiptRetrieveQueryKey = (contractorPaymentUuid: string) => {
    return [`/api/payroll/v1/contractor-payments/${contractorPaymentUuid}/receipt/`] as const;
};

export const usePayrollContractorPaymentReceiptRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollContractorPaymentReceiptRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentReceiptRetrieve>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getPayrollContractorPaymentReceiptRetrieveQueryKey(contractorPaymentUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollContractorPaymentReceiptRetrieve>>> = ({ signal }) =>
        payrollContractorPaymentReceiptRetrieve(contractorPaymentUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: contractorPaymentUuid !== null && contractorPaymentUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollContractorPaymentReceiptRetrieve>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollContractorPaymentReceiptRetrieveQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollContractorPaymentReceiptRetrieve>>
>;
export type PayrollContractorPaymentReceiptRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollContractorPaymentReceiptRetrieve<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentReceiptRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentReceiptRetrieve>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorPaymentReceiptRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorPaymentReceiptRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorPaymentReceiptRetrieve<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentReceiptRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentReceiptRetrieve>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorPaymentReceiptRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorPaymentReceiptRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorPaymentReceiptRetrieve<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentReceiptRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentReceiptRetrieve>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve a contractor payment receipt
 */

export function usePayrollContractorPaymentReceiptRetrieve<
    TData = Awaited<ReturnType<typeof payrollContractorPaymentReceiptRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorPaymentUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollContractorPaymentReceiptRetrieve>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollContractorPaymentReceiptRetrieveQueryOptions(contractorPaymentUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * The documents belonging to one owner — the company by default, or the worker named by `worker_uuid`. `requires_signature=true` is the outstanding-signature queue. A live read: an empty list means there are no documents, and an unreachable payroll system is a 503 rather than an empty page.
 * @summary List payroll documents
 */
export const payrollDocumentsList = (
    params?: PayrollDocumentsListParams,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollDocumentList>(
        { url: `/api/payroll/v1/documents/`, method: 'GET', params, signal },
        options
    );
};

export const getPayrollDocumentsListQueryKey = (params?: PayrollDocumentsListParams) => {
    return [`/api/payroll/v1/documents/`, ...(params ? [params] : [])] as const;
};

export const usePayrollDocumentsListQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollDocumentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollDocumentsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollDocumentsListQueryKey(params);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollDocumentsList>>> = ({ signal }) =>
        payrollDocumentsList(params, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentsList>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollDocumentsListQueryResult = NonNullable<Awaited<ReturnType<typeof payrollDocumentsList>>>;
export type PayrollDocumentsListQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollDocumentsList<
    TData = Awaited<ReturnType<typeof payrollDocumentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params: undefined | PayrollDocumentsListParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentsList>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollDocumentsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollDocumentsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollDocumentsList<
    TData = Awaited<ReturnType<typeof payrollDocumentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollDocumentsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentsList>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollDocumentsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollDocumentsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollDocumentsList<
    TData = Awaited<ReturnType<typeof payrollDocumentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollDocumentsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List payroll documents
 */

export function usePayrollDocumentsList<
    TData = Awaited<ReturnType<typeof payrollDocumentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollDocumentsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollDocumentsListQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * The company's own documents and every employee's and contractor's, with where each stands on signing; `scope=member` narrows it to the company's and the member's own. One provider read per owner, bounded per request: a roster too large for one read, or an owner whose read failed, comes back `incomplete` with the owners missing from `results`. A 503 means the payroll system could not be reached for anyone.
 * @summary List every payroll document across the company
 */
export const payrollDocumentsLibraryList = (
    params?: PayrollDocumentsLibraryListParams,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollDocumentLibrary>(
        { url: `/api/payroll/v1/documents/library/`, method: 'GET', params, signal },
        options
    );
};

export const getPayrollDocumentsLibraryListQueryKey = (params?: PayrollDocumentsLibraryListParams) => {
    return [`/api/payroll/v1/documents/library/`, ...(params ? [params] : [])] as const;
};

export const usePayrollDocumentsLibraryListQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollDocumentsLibraryList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollDocumentsLibraryListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentsLibraryList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollDocumentsLibraryListQueryKey(params);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollDocumentsLibraryList>>> = ({ signal }) =>
        payrollDocumentsLibraryList(params, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentsLibraryList>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollDocumentsLibraryListQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollDocumentsLibraryList>>
>;
export type PayrollDocumentsLibraryListQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollDocumentsLibraryList<
    TData = Awaited<ReturnType<typeof payrollDocumentsLibraryList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params: undefined | PayrollDocumentsLibraryListParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentsLibraryList>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollDocumentsLibraryList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollDocumentsLibraryList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollDocumentsLibraryList<
    TData = Awaited<ReturnType<typeof payrollDocumentsLibraryList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollDocumentsLibraryListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentsLibraryList>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollDocumentsLibraryList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollDocumentsLibraryList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollDocumentsLibraryList<
    TData = Awaited<ReturnType<typeof payrollDocumentsLibraryList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollDocumentsLibraryListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentsLibraryList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List every payroll document across the company
 */

export function usePayrollDocumentsLibraryList<
    TData = Awaited<ReturnType<typeof payrollDocumentsLibraryList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollDocumentsLibraryListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentsLibraryList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollDocumentsLibraryListQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * The company's forms awaiting a signature (the member signs as its signatory) and the member's own employee forms. Each carries `signing`, what the Gusto React SDK needs to sign it. Other workers sign their own documents, so theirs are not here. A live read: an empty list means nothing is waiting, and an unreachable payroll system is a 503.
 * @summary List the documents the member can sign
 */
export const payrollDocumentsSignatureQueueList = (
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollSignatureQueue>(
        { url: `/api/payroll/v1/documents/signature-queue/`, method: 'GET', signal },
        options
    );
};

export const getPayrollDocumentsSignatureQueueListQueryKey = () => {
    return [`/api/payroll/v1/documents/signature-queue/`] as const;
};

export const usePayrollDocumentsSignatureQueueListQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollDocumentsSignatureQueueList>>,
    TError = ErrorType<PayrollErrorResponse>
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentsSignatureQueueList>>, TError, TData>>;
    request?: SecondParameter<typeof payrollApiMutator>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollDocumentsSignatureQueueListQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollDocumentsSignatureQueueList>>> = ({ signal }) =>
        payrollDocumentsSignatureQueueList(requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollDocumentsSignatureQueueList>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollDocumentsSignatureQueueListQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollDocumentsSignatureQueueList>>
>;
export type PayrollDocumentsSignatureQueueListQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollDocumentsSignatureQueueList<
    TData = Awaited<ReturnType<typeof payrollDocumentsSignatureQueueList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentsSignatureQueueList>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollDocumentsSignatureQueueList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollDocumentsSignatureQueueList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollDocumentsSignatureQueueList<
    TData = Awaited<ReturnType<typeof payrollDocumentsSignatureQueueList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentsSignatureQueueList>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollDocumentsSignatureQueueList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollDocumentsSignatureQueueList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollDocumentsSignatureQueueList<
    TData = Awaited<ReturnType<typeof payrollDocumentsSignatureQueueList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentsSignatureQueueList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List the documents the member can sign
 */

export function usePayrollDocumentsSignatureQueueList<
    TData = Awaited<ReturnType<typeof payrollDocumentsSignatureQueueList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentsSignatureQueueList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollDocumentsSignatureQueueListQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * The document itself, streamed through from the provider — for a browser, which cannot read the provider's storage directly. Ownership is re-checked exactly as on the download URL, and the file is read afresh on every request, so it reflects any signature added since the list was read. Pass the `worker_uuid` and `category` the list was read with.
 * @summary Retrieve a document's file
 */
export const payrollDocumentContentRetrieve = (
    documentUuid: string,
    params?: PayrollDocumentContentRetrieveParams,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<Blob>(
        {
            url: `/api/payroll/v1/documents/${documentUuid}/content/`,
            method: 'GET',
            params,
            responseType: 'blob',
            signal,
        },
        options
    );
};

export const getPayrollDocumentContentRetrieveQueryKey = (
    documentUuid: string,
    params?: PayrollDocumentContentRetrieveParams
) => {
    return [`/api/payroll/v1/documents/${documentUuid}/content/`, ...(params ? [params] : [])] as const;
};

export const usePayrollDocumentContentRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollDocumentContentRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    documentUuid: string,
    params?: PayrollDocumentContentRetrieveParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentContentRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollDocumentContentRetrieveQueryKey(documentUuid, params);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollDocumentContentRetrieve>>> = ({ signal }) =>
        payrollDocumentContentRetrieve(documentUuid, params, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: documentUuid !== null && documentUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollDocumentContentRetrieve>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollDocumentContentRetrieveQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollDocumentContentRetrieve>>
>;
export type PayrollDocumentContentRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollDocumentContentRetrieve<
    TData = Awaited<ReturnType<typeof payrollDocumentContentRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    documentUuid: string,
    params: undefined | PayrollDocumentContentRetrieveParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentContentRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollDocumentContentRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollDocumentContentRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollDocumentContentRetrieve<
    TData = Awaited<ReturnType<typeof payrollDocumentContentRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    documentUuid: string,
    params?: PayrollDocumentContentRetrieveParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentContentRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollDocumentContentRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollDocumentContentRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollDocumentContentRetrieve<
    TData = Awaited<ReturnType<typeof payrollDocumentContentRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    documentUuid: string,
    params?: PayrollDocumentContentRetrieveParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentContentRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve a document's file
 */

export function usePayrollDocumentContentRetrieve<
    TData = Awaited<ReturnType<typeof payrollDocumentContentRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    documentUuid: string,
    params?: PayrollDocumentContentRetrieveParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentContentRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollDocumentContentRetrieveQueryOptions(documentUuid, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Ownership is re-checked when the URL is issued, not only when the list was read: the identifier is derived from this company's own documents, so one minted elsewhere resolves against nothing. Pass the `worker_uuid` the list was read with when the document belongs to a worker. The URL is short-lived and minted per request — never store it.
 * @summary Retrieve a signed download URL for a document
 */
export const payrollDocumentDownloadUrlRetrieve = (
    documentUuid: string,
    params?: PayrollDocumentDownloadUrlRetrieveParams,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollDocumentDownload>(
        { url: `/api/payroll/v1/documents/${documentUuid}/download-url/`, method: 'GET', params, signal },
        options
    );
};

export const getPayrollDocumentDownloadUrlRetrieveQueryKey = (
    documentUuid: string,
    params?: PayrollDocumentDownloadUrlRetrieveParams
) => {
    return [`/api/payroll/v1/documents/${documentUuid}/download-url/`, ...(params ? [params] : [])] as const;
};

export const usePayrollDocumentDownloadUrlRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollDocumentDownloadUrlRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    documentUuid: string,
    params?: PayrollDocumentDownloadUrlRetrieveParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentDownloadUrlRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollDocumentDownloadUrlRetrieveQueryKey(documentUuid, params);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollDocumentDownloadUrlRetrieve>>> = ({ signal }) =>
        payrollDocumentDownloadUrlRetrieve(documentUuid, params, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: documentUuid !== null && documentUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollDocumentDownloadUrlRetrieve>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollDocumentDownloadUrlRetrieveQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollDocumentDownloadUrlRetrieve>>
>;
export type PayrollDocumentDownloadUrlRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollDocumentDownloadUrlRetrieve<
    TData = Awaited<ReturnType<typeof payrollDocumentDownloadUrlRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    documentUuid: string,
    params: undefined | PayrollDocumentDownloadUrlRetrieveParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentDownloadUrlRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollDocumentDownloadUrlRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollDocumentDownloadUrlRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollDocumentDownloadUrlRetrieve<
    TData = Awaited<ReturnType<typeof payrollDocumentDownloadUrlRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    documentUuid: string,
    params?: PayrollDocumentDownloadUrlRetrieveParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentDownloadUrlRetrieve>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollDocumentDownloadUrlRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollDocumentDownloadUrlRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollDocumentDownloadUrlRetrieve<
    TData = Awaited<ReturnType<typeof payrollDocumentDownloadUrlRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    documentUuid: string,
    params?: PayrollDocumentDownloadUrlRetrieveParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentDownloadUrlRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve a signed download URL for a document
 */

export function usePayrollDocumentDownloadUrlRetrieve<
    TData = Awaited<ReturnType<typeof payrollDocumentDownloadUrlRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    documentUuid: string,
    params?: PayrollDocumentDownloadUrlRetrieveParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollDocumentDownloadUrlRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollDocumentDownloadUrlRetrieveQueryOptions(documentUuid, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * The account payroll debits from. Which account it is comes from the payroll provider; how much is in it comes from the bank via Plaid. A reading taken recently enough is reused rather than re-read, so `balance_synced_at` says when the bank reported the figure shown and is not necessarily the moment of this request. `balance` is null whenever no figure could be established.
 * @summary Retrieve the funding account and balance
 */
export const payrollFundingRetrieve = (options?: SecondParameter<typeof payrollApiMutator>, signal?: AbortSignal) => {
    return payrollApiMutator<PayrollFunding>({ url: `/api/payroll/v1/funding/`, method: 'GET', signal }, options);
};

export const getPayrollFundingRetrieveQueryKey = () => {
    return [`/api/payroll/v1/funding/`] as const;
};

export const usePayrollFundingRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollFundingRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollFundingRetrieve>>, TError, TData>>;
    request?: SecondParameter<typeof payrollApiMutator>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollFundingRetrieveQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollFundingRetrieve>>> = ({ signal }) =>
        payrollFundingRetrieve(requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollFundingRetrieve>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollFundingRetrieveQueryResult = NonNullable<Awaited<ReturnType<typeof payrollFundingRetrieve>>>;
export type PayrollFundingRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollFundingRetrieve<
    TData = Awaited<ReturnType<typeof payrollFundingRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollFundingRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollFundingRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollFundingRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollFundingRetrieve<
    TData = Awaited<ReturnType<typeof payrollFundingRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollFundingRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollFundingRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollFundingRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollFundingRetrieve<
    TData = Awaited<ReturnType<typeof payrollFundingRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollFundingRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve the funding account and balance
 */

export function usePayrollFundingRetrieve<
    TData = Awaited<ReturnType<typeof payrollFundingRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollFundingRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollFundingRetrieveQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * The next scheduled run, AutoPayroll state, reasonable-compensation progress, team counts and company documents. Served from our own tables; `as_of` says when that projection last agreed with the provider.
 * @summary Retrieve the payroll overview
 */
export const payrollOverviewRetrieve = (options?: SecondParameter<typeof payrollApiMutator>, signal?: AbortSignal) => {
    return payrollApiMutator<PayrollOverview>({ url: `/api/payroll/v1/overview/`, method: 'GET', signal }, options);
};

export const getPayrollOverviewRetrieveQueryKey = () => {
    return [`/api/payroll/v1/overview/`] as const;
};

export const usePayrollOverviewRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollOverviewRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollOverviewRetrieve>>, TError, TData>>;
    request?: SecondParameter<typeof payrollApiMutator>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollOverviewRetrieveQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollOverviewRetrieve>>> = ({ signal }) =>
        payrollOverviewRetrieve(requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollOverviewRetrieve>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollOverviewRetrieveQueryResult = NonNullable<Awaited<ReturnType<typeof payrollOverviewRetrieve>>>;
export type PayrollOverviewRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollOverviewRetrieve<
    TData = Awaited<ReturnType<typeof payrollOverviewRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollOverviewRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollOverviewRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollOverviewRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollOverviewRetrieve<
    TData = Awaited<ReturnType<typeof payrollOverviewRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollOverviewRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollOverviewRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollOverviewRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollOverviewRetrieve<
    TData = Awaited<ReturnType<typeof payrollOverviewRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollOverviewRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve the payroll overview
 */

export function usePayrollOverviewRetrieve<
    TData = Awaited<ReturnType<typeof payrollOverviewRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollOverviewRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollOverviewRetrieveQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Every pay schedule the company pays on, with how many employees sit on each and whether AutoPayroll can run it.
 *
 * A live read, so it can answer 503, or 422 when the provider connection has expired and only reconnecting it helps. `anchor_pay_date` can move without anyone editing it — a company that misses its first pay date has the anchor reset — and `version` is the token an edit has to send back.
 *
 * `employee_count` is resolved from the provider's assignment arrangement on each read -- one company-wide call whatever the headcount -- so it is right even before anyone has been linked to a schedule in our own tables. If that call fails the mirrored count stands in rather than the read failing.
 *
 * `auto_payroll_blockers` says why AutoPayroll cannot be switched on, but only when `auto_payroll_blockers_known` is true -- the provider carries the blockers on its single-schedule read rather than its list, so a failure there leaves the reason unknown rather than empty. A schedule we have never written down is omitted: every route here is addressed by our identifier, and a schedule without one cannot be acted on.
 * @summary List pay schedules
 */
export const payrollPaySchedulesList = (options?: SecondParameter<typeof payrollApiMutator>, signal?: AbortSignal) => {
    return payrollApiMutator<PayScheduleList>(
        { url: `/api/payroll/v1/pay-schedules/`, method: 'GET', signal },
        options
    );
};

export const getPayrollPaySchedulesListQueryKey = () => {
    return [`/api/payroll/v1/pay-schedules/`] as const;
};

export const usePayrollPaySchedulesListQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollPaySchedulesList>>,
    TError = ErrorType<PayrollErrorResponse>
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPaySchedulesList>>, TError, TData>>;
    request?: SecondParameter<typeof payrollApiMutator>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollPaySchedulesListQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollPaySchedulesList>>> = ({ signal }) =>
        payrollPaySchedulesList(requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollPaySchedulesList>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollPaySchedulesListQueryResult = NonNullable<Awaited<ReturnType<typeof payrollPaySchedulesList>>>;
export type PayrollPaySchedulesListQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollPaySchedulesList<
    TData = Awaited<ReturnType<typeof payrollPaySchedulesList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPaySchedulesList>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollPaySchedulesList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollPaySchedulesList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollPaySchedulesList<
    TData = Awaited<ReturnType<typeof payrollPaySchedulesList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPaySchedulesList>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollPaySchedulesList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollPaySchedulesList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollPaySchedulesList<
    TData = Awaited<ReturnType<typeof payrollPaySchedulesList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPaySchedulesList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List pay schedules
 */

export function usePayrollPaySchedulesList<
    TData = Awaited<ReturnType<typeof payrollPaySchedulesList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPaySchedulesList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollPaySchedulesListQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Payroll runs, newest first. `display_status` is computed server-side because it depends on ordering across the whole list — exactly one run is `NEXT`, and paging cannot change which one.
 * @summary List payroll runs
 */
export const payrollPayrollsList = (
    params?: PayrollPayrollsListParams,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollActivityPage>(
        { url: `/api/payroll/v1/payrolls/`, method: 'GET', params, signal },
        options
    );
};

export const getPayrollPayrollsListQueryKey = (params?: PayrollPayrollsListParams) => {
    return [`/api/payroll/v1/payrolls/`, ...(params ? [params] : [])] as const;
};

export const usePayrollPayrollsListQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollPayrollsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollPayrollsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPayrollsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollPayrollsListQueryKey(params);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollPayrollsList>>> = ({ signal }) =>
        payrollPayrollsList(params, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollPayrollsList>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollPayrollsListQueryResult = NonNullable<Awaited<ReturnType<typeof payrollPayrollsList>>>;
export type PayrollPayrollsListQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollPayrollsList<
    TData = Awaited<ReturnType<typeof payrollPayrollsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params: undefined | PayrollPayrollsListParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPayrollsList>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollPayrollsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollPayrollsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollPayrollsList<
    TData = Awaited<ReturnType<typeof payrollPayrollsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollPayrollsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPayrollsList>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollPayrollsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollPayrollsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollPayrollsList<
    TData = Awaited<ReturnType<typeof payrollPayrollsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollPayrollsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPayrollsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List payroll runs
 */

export function usePayrollPayrollsList<
    TData = Awaited<ReturnType<typeof payrollPayrollsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollPayrollsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPayrollsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollPayrollsListQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * An extra run outside the schedule, and the single path for money that moves off schedule -- the reasonable-compensation catch-up payment wraps this rather than adding a second write path, so one set of guards covers both.
 *
 * An omitted `worker_uuids` means everyone still employed on the schedule; the roster is expanded server-side because the provider requires the list explicitly. A named worker is included even if terminated, which is how a final paycheque is run.
 *
 * `pay_period_start` and `pay_period_end` default to `check_date`. `reason` is mapped onto the provider's closed set and falls back to its catch-all rather than failing.
 *
 * A refusal is 422 `COMMAND_REFUSED` with `details.reason` -- `payroll_not_connected` when payroll is not set up for the business, `unknown_pay_schedule`, `empty_roster` or `unknown_workers`. This route names no run, so it never answers 404 for a missing one.
 *
 * Synchronous at the provider, so unlike the other three this command is confirmed before the 202 returns.
 * @summary Create an off-cycle run
 */
export const payrollOffCycleCreate = (
    payrollOffCycleRequest: PayrollOffCycleRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/payrolls/off-cycle/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: payrollOffCycleRequest,
            signal,
        },
        options
    );
};

export const getPayrollOffCycleCreateQueryKey = (payrollOffCycleRequest?: PayrollOffCycleRequest) => {
    return ['POST', `/api/payroll/v1/payrolls/off-cycle/`, payrollOffCycleRequest] as const;
};

export const usePayrollOffCycleCreateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollOffCycleCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollOffCycleRequest: PayrollOffCycleRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollOffCycleCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollOffCycleCreateQueryKey(payrollOffCycleRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollOffCycleCreate>>> = ({ signal }) =>
        payrollOffCycleCreate(payrollOffCycleRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollOffCycleCreate>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollOffCycleCreateQueryResult = NonNullable<Awaited<ReturnType<typeof payrollOffCycleCreate>>>;
export type PayrollOffCycleCreateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollOffCycleCreate<
    TData = Awaited<ReturnType<typeof payrollOffCycleCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollOffCycleRequest: PayrollOffCycleRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollOffCycleCreate>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollOffCycleCreate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollOffCycleCreate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollOffCycleCreate<
    TData = Awaited<ReturnType<typeof payrollOffCycleCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollOffCycleRequest: PayrollOffCycleRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollOffCycleCreate>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollOffCycleCreate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollOffCycleCreate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollOffCycleCreate<
    TData = Awaited<ReturnType<typeof payrollOffCycleCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollOffCycleRequest: PayrollOffCycleRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollOffCycleCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create an off-cycle run
 */

export function usePayrollOffCycleCreate<
    TData = Awaited<ReturnType<typeof payrollOffCycleCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollOffCycleRequest: PayrollOffCycleRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollOffCycleCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollOffCycleCreateQueryOptions(payrollOffCycleRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * One run and a line per worker on it. A run routinely covers several people, so treat the per-worker lines as the substance of the response rather than a detail.
 *
 * A run that has not been prepared or reconciled carries no people yet, so `items` is empty while the run itself and its totals are real. `blockers` is empty here: a blocker is something the provider says stands in the way, and this read does not ask it.
 * @summary Retrieve one run with its people and their lines
 */
export const payrollRunRetrieve = (
    payrollUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollRunDetail>(
        { url: `/api/payroll/v1/payrolls/${payrollUuid}/`, method: 'GET', signal },
        options
    );
};

export const getPayrollRunRetrieveQueryKey = (payrollUuid: string) => {
    return [`/api/payroll/v1/payrolls/${payrollUuid}/`] as const;
};

export const usePayrollRunRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollRunRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollRunRetrieveQueryKey(payrollUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollRunRetrieve>>> = ({ signal }) =>
        payrollRunRetrieve(payrollUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: payrollUuid !== null && payrollUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollRunRetrieve>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollRunRetrieveQueryResult = NonNullable<Awaited<ReturnType<typeof payrollRunRetrieve>>>;
export type PayrollRunRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollRunRetrieve<
    TData = Awaited<ReturnType<typeof payrollRunRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunRetrieve<
    TData = Awaited<ReturnType<typeof payrollRunRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunRetrieve<
    TData = Awaited<ReturnType<typeof payrollRunRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve one run with its people and their lines
 */

export function usePayrollRunRetrieve<
    TData = Awaited<ReturnType<typeof payrollRunRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollRunRetrieveQueryOptions(payrollUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Asynchronous at the provider, which accepts the request and finishes out of band. A confirmed command here means accepted, not calculated -- poll the command, and the totals and `debit_date` land on the run when the provider reports them.
 *
 * A run past its deadline is prepared first, so it is priced for the pay day the provider moved it to. Re-read the run for the new `pay_date` and deadline.
 * @summary Calculate a payroll run
 */
export const payrollRunCalculate = (
    payrollUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        { url: `/api/payroll/v1/payrolls/${payrollUuid}/calculate/`, method: 'POST', signal },
        options
    );
};

export const getPayrollRunCalculateQueryKey = (payrollUuid: string) => {
    return ['POST', `/api/payroll/v1/payrolls/${payrollUuid}/calculate/`] as const;
};

export const usePayrollRunCalculateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollRunCalculate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunCalculate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollRunCalculateQueryKey(payrollUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollRunCalculate>>> = ({ signal }) =>
        payrollRunCalculate(payrollUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: payrollUuid !== null && payrollUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollRunCalculate>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollRunCalculateQueryResult = NonNullable<Awaited<ReturnType<typeof payrollRunCalculate>>>;
export type PayrollRunCalculateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollRunCalculate<
    TData = Awaited<ReturnType<typeof payrollRunCalculate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunCalculate>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunCalculate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunCalculate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunCalculate<
    TData = Awaited<ReturnType<typeof payrollRunCalculate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunCalculate>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunCalculate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunCalculate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunCalculate<
    TData = Awaited<ReturnType<typeof payrollRunCalculate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunCalculate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Calculate a payroll run
 */

export function usePayrollRunCalculate<
    TData = Awaited<ReturnType<typeof payrollRunCalculate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunCalculate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollRunCalculateQueryOptions(payrollUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Closes at 3:30pm PT, half an hour earlier than submit, and both boundaries are read off the run's own `payroll_deadline` rather than rebuilt from a wall clock.
 *
 * After the window a run can only be reversed, and no provider endpoint reverses one -- that is a support process raised as an alert action, deliberately not offered here as a button that would fail. A late cancel is 422 `COMMAND_REFUSED` with `details.reason = "deadline_passed"`.
 * @summary Cancel a submitted run
 */
export const payrollRunCancel = (
    payrollUuid: string,
    payrollCancelRequest?: PayrollCancelRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/payrolls/${payrollUuid}/cancel/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: payrollCancelRequest,
            signal,
        },
        options
    );
};

export const getPayrollRunCancelQueryKey = (payrollUuid: string, payrollCancelRequest?: PayrollCancelRequest) => {
    return ['POST', `/api/payroll/v1/payrolls/${payrollUuid}/cancel/`, payrollCancelRequest] as const;
};

export const usePayrollRunCancelQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollRunCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    payrollCancelRequest?: PayrollCancelRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunCancel>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollRunCancelQueryKey(payrollUuid, payrollCancelRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollRunCancel>>> = ({ signal }) =>
        payrollRunCancel(payrollUuid, payrollCancelRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: payrollUuid !== null && payrollUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollRunCancel>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollRunCancelQueryResult = NonNullable<Awaited<ReturnType<typeof payrollRunCancel>>>;
export type PayrollRunCancelQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollRunCancel<
    TData = Awaited<ReturnType<typeof payrollRunCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    payrollCancelRequest: undefined | PayrollCancelRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunCancel>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunCancel>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunCancel>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunCancel<
    TData = Awaited<ReturnType<typeof payrollRunCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    payrollCancelRequest?: PayrollCancelRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunCancel>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunCancel>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunCancel>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunCancel<
    TData = Awaited<ReturnType<typeof payrollRunCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    payrollCancelRequest?: PayrollCancelRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunCancel>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Cancel a submitted run
 */

export function usePayrollRunCancel<
    TData = Awaited<ReturnType<typeof payrollRunCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    payrollCancelRequest?: PayrollCancelRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunCancel>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollRunCancelQueryOptions(payrollUuid, payrollCancelRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Deletes an unprocessed run at the provider, for a pay period it holds more than one payroll for. The member keeps the other. Checked live first: a run that has been submitted is 422 `already_processed`, and a run that is the only payroll for its period is 422 `no_duplicate`, so this can never leave a period with no payroll.
 *
 * Synchronous at the provider, so the command is confirmed before the 202 returns. A run the provider has already deleted counts as done. 422 `provider_refused` when the provider will not delete it, with the provider's own sentences in `details.provider_messages` -- the other payroll can still be removed instead, which also clears the period. `unavailable` while this is not enabled for the member.
 * @summary Remove a run the provider holds a duplicate of
 */
export const payrollRunDiscard = (
    payrollUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        { url: `/api/payroll/v1/payrolls/${payrollUuid}/discard/`, method: 'POST', signal },
        options
    );
};

export const getPayrollRunDiscardQueryKey = (payrollUuid: string) => {
    return ['POST', `/api/payroll/v1/payrolls/${payrollUuid}/discard/`] as const;
};

export const usePayrollRunDiscardQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollRunDiscard>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunDiscard>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollRunDiscardQueryKey(payrollUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollRunDiscard>>> = ({ signal }) =>
        payrollRunDiscard(payrollUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: payrollUuid !== null && payrollUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollRunDiscard>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollRunDiscardQueryResult = NonNullable<Awaited<ReturnType<typeof payrollRunDiscard>>>;
export type PayrollRunDiscardQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollRunDiscard<
    TData = Awaited<ReturnType<typeof payrollRunDiscard>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunDiscard>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunDiscard>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunDiscard>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunDiscard<
    TData = Awaited<ReturnType<typeof payrollRunDiscard>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunDiscard>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunDiscard>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunDiscard>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunDiscard<
    TData = Awaited<ReturnType<typeof payrollRunDiscard>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunDiscard>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Remove a run the provider holds a duplicate of
 */

export function usePayrollRunDiscard<
    TData = Awaited<ReturnType<typeof payrollRunDiscard>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunDiscard>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollRunDiscardQueryOptions(payrollUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Each item carries the token `prepare` handed back for that person. Chunked at 100 compensations per provider request on whole-person boundaries; `chunk_total` and `chunk_completed` on the command make a partly-applied edit visible.
 *
 * Omit `excluded` to leave it alone -- sending it always would assert an exclusion from a value the client may not have refreshed.
 * @summary Update a run's hours, bonuses, PTO and exclusions
 */
export const payrollRunUpdateLines = (
    payrollUuid: string,
    patchedPayrollLinesUpdateRequest?: PatchedPayrollLinesUpdateRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/payrolls/${payrollUuid}/lines/`,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            data: patchedPayrollLinesUpdateRequest,
            signal,
        },
        options
    );
};

export const getPayrollRunUpdateLinesQueryKey = (
    payrollUuid: string,
    patchedPayrollLinesUpdateRequest?: PatchedPayrollLinesUpdateRequest
) => {
    return ['PATCH', `/api/payroll/v1/payrolls/${payrollUuid}/lines/`, patchedPayrollLinesUpdateRequest] as const;
};

export const usePayrollRunUpdateLinesQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollRunUpdateLines>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    patchedPayrollLinesUpdateRequest?: PatchedPayrollLinesUpdateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunUpdateLines>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getPayrollRunUpdateLinesQueryKey(payrollUuid, patchedPayrollLinesUpdateRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollRunUpdateLines>>> = ({ signal }) =>
        payrollRunUpdateLines(payrollUuid, patchedPayrollLinesUpdateRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: payrollUuid !== null && payrollUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollRunUpdateLines>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollRunUpdateLinesQueryResult = NonNullable<Awaited<ReturnType<typeof payrollRunUpdateLines>>>;
export type PayrollRunUpdateLinesQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollRunUpdateLines<
    TData = Awaited<ReturnType<typeof payrollRunUpdateLines>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    patchedPayrollLinesUpdateRequest: undefined | PatchedPayrollLinesUpdateRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunUpdateLines>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunUpdateLines>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunUpdateLines>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunUpdateLines<
    TData = Awaited<ReturnType<typeof payrollRunUpdateLines>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    patchedPayrollLinesUpdateRequest?: PatchedPayrollLinesUpdateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunUpdateLines>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunUpdateLines>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunUpdateLines>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunUpdateLines<
    TData = Awaited<ReturnType<typeof payrollRunUpdateLines>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    patchedPayrollLinesUpdateRequest?: PatchedPayrollLinesUpdateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunUpdateLines>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update a run's hours, bonuses, PTO and exclusions
 */

export function usePayrollRunUpdateLines<
    TData = Awaited<ReturnType<typeof payrollRunUpdateLines>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    patchedPayrollLinesUpdateRequest?: PatchedPayrollLinesUpdateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunUpdateLines>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollRunUpdateLinesQueryOptions(payrollUuid, patchedPayrollLinesUpdateRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * For a run whose `period_conflict` is true. Read live from the provider, because the answer leads to a delete and our own copy of a run's status can lag it.
 *
 * One payroll is `recommended`: a processed one if there is one, since it paid the period, otherwise the one the provider did not refuse. Remove the others with POST /payrolls/{payroll_uuid}/discard/. `resolved` is true when the provider holds only one payroll for the period, and the run's `period_conflict` clears.
 *
 * 404 while this is not enabled for the member.
 * @summary List the payrolls the provider holds for a run's pay period
 */
export const payrollPeriodDuplicatesRetrieve = (
    payrollUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollPeriodDuplicates>(
        { url: `/api/payroll/v1/payrolls/${payrollUuid}/period-duplicates/`, method: 'GET', signal },
        options
    );
};

export const getPayrollPeriodDuplicatesRetrieveQueryKey = (payrollUuid: string) => {
    return [`/api/payroll/v1/payrolls/${payrollUuid}/period-duplicates/`] as const;
};

export const usePayrollPeriodDuplicatesRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollPeriodDuplicatesRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPeriodDuplicatesRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollPeriodDuplicatesRetrieveQueryKey(payrollUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollPeriodDuplicatesRetrieve>>> = ({ signal }) =>
        payrollPeriodDuplicatesRetrieve(payrollUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: payrollUuid !== null && payrollUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollPeriodDuplicatesRetrieve>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollPeriodDuplicatesRetrieveQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollPeriodDuplicatesRetrieve>>
>;
export type PayrollPeriodDuplicatesRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollPeriodDuplicatesRetrieve<
    TData = Awaited<ReturnType<typeof payrollPeriodDuplicatesRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPeriodDuplicatesRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollPeriodDuplicatesRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollPeriodDuplicatesRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollPeriodDuplicatesRetrieve<
    TData = Awaited<ReturnType<typeof payrollPeriodDuplicatesRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPeriodDuplicatesRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollPeriodDuplicatesRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollPeriodDuplicatesRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollPeriodDuplicatesRetrieve<
    TData = Awaited<ReturnType<typeof payrollPeriodDuplicatesRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPeriodDuplicatesRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List the payrolls the provider holds for a run's pay period
 */

export function usePayrollPeriodDuplicatesRetrieve<
    TData = Awaited<ReturnType<typeof payrollPeriodDuplicatesRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPeriodDuplicatesRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollPeriodDuplicatesRetrieveQueryOptions(payrollUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Mandatory before any edit. Returns one token per worker plus one for the run, so concurrent edits stay independent, and how a client recovers from a 412.
 *
 * **Not free to repeat.** Preparing nullifies the run's `calculated_at` and totals at the provider, so a second call discards a calculation and the run must be calculated again. The per-worker grid comes from `GET /payrolls/{payroll_uuid}/`, not from here.
 * @summary Prepare a payroll run for editing
 */
export const payrollRunPrepare = (
    payrollUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollPrepare>(
        { url: `/api/payroll/v1/payrolls/${payrollUuid}/prepare/`, method: 'POST', signal },
        options
    );
};

export const getPayrollRunPrepareQueryKey = (payrollUuid: string) => {
    return ['POST', `/api/payroll/v1/payrolls/${payrollUuid}/prepare/`] as const;
};

export const usePayrollRunPrepareQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollRunPrepare>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunPrepare>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollRunPrepareQueryKey(payrollUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollRunPrepare>>> = ({ signal }) =>
        payrollRunPrepare(payrollUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: payrollUuid !== null && payrollUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollRunPrepare>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollRunPrepareQueryResult = NonNullable<Awaited<ReturnType<typeof payrollRunPrepare>>>;
export type PayrollRunPrepareQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollRunPrepare<
    TData = Awaited<ReturnType<typeof payrollRunPrepare>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunPrepare>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunPrepare>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunPrepare>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunPrepare<
    TData = Awaited<ReturnType<typeof payrollRunPrepare>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunPrepare>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunPrepare>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunPrepare>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunPrepare<
    TData = Awaited<ReturnType<typeof payrollRunPrepare>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunPrepare>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Prepare a payroll run for editing
 */

export function usePayrollRunPrepare<
    TData = Awaited<ReturnType<typeof payrollRunPrepare>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunPrepare>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollRunPrepareQueryOptions(payrollUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * The receipt for a processed run. Available once the run has been processed, and never for a cancelled or skipped run.
 *
 * Two parts. `payroll_uuid` through `as_of` summarise the run from our records, and agree with the run detail. The rest is the payroll provider's receipt, which must be shown whole: the licensee block and legal text are served verbatim. If the provider cannot be reached the whole read fails rather than returning a receipt without them.
 *
 * `receipt_fetched_at` says when the provider's part was read. Once a run can no longer be cancelled, its receipt is kept and reused until the run's status changes.
 * @summary Retrieve a payroll run receipt
 */
export const payrollRunReceiptRetrieve = (
    payrollUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollReceipt>(
        { url: `/api/payroll/v1/payrolls/${payrollUuid}/receipt/`, method: 'GET', signal },
        options
    );
};

export const getPayrollRunReceiptRetrieveQueryKey = (payrollUuid: string) => {
    return [`/api/payroll/v1/payrolls/${payrollUuid}/receipt/`] as const;
};

export const usePayrollRunReceiptRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollRunReceiptRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunReceiptRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollRunReceiptRetrieveQueryKey(payrollUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollRunReceiptRetrieve>>> = ({ signal }) =>
        payrollRunReceiptRetrieve(payrollUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: payrollUuid !== null && payrollUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollRunReceiptRetrieve>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollRunReceiptRetrieveQueryResult = NonNullable<Awaited<ReturnType<typeof payrollRunReceiptRetrieve>>>;
export type PayrollRunReceiptRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollRunReceiptRetrieve<
    TData = Awaited<ReturnType<typeof payrollRunReceiptRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunReceiptRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunReceiptRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunReceiptRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunReceiptRetrieve<
    TData = Awaited<ReturnType<typeof payrollRunReceiptRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunReceiptRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunReceiptRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunReceiptRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunReceiptRetrieve<
    TData = Awaited<ReturnType<typeof payrollRunReceiptRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunReceiptRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve a payroll run receipt
 */

export function usePayrollRunReceiptRetrieve<
    TData = Awaited<ReturnType<typeof payrollRunReceiptRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunReceiptRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollRunReceiptRetrieveQueryOptions(payrollUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Records the period as a processed run at $0. **A normal outcome, not a failure:** a processed run can carry a zero gross, and nothing may treat `gross_pay == 0` as an error.
 *
 * One asynchronous call at the provider, addressed by pay period rather than by run. Guarded exactly as submit is, because it is the same money decision made the other way, and confirmed by the same `payroll.processed` event.
 *
 * `reason` is recorded on the command and not sent to the provider, which has no field for one.
 *
 * A regular skip whose pay period has not started is 422 `COMMAND_REFUSED` with `details.reason = "period_not_started"`, refused before any call to the provider.
 *
 * A period the provider no longer lists as a regular period on the run's schedule, processed or not -- a draft a pay-schedule change left behind -- is 422 `COMMAND_REFUSED` with `details.reason = "period_not_on_schedule"`, refused before the skip is sent. Only support can remove such a draft. A failed pay-period read does not refuse.
 *
 * An otherwise submittable, unprocessed off-cycle run is 422 `COMMAND_REFUSED` with `details.reason = "off_cycle"`, also refused before any call to the provider: the provider's skip endpoint has no off-cycle payroll type (https://docs.gusto.com/embedded-payroll/reference/post-companies-payroll-skip-company_uuid).
 * @summary Skip the period
 */
export const payrollRunSkip = (
    payrollUuid: string,
    payrollSkipRequest?: PayrollSkipRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/payrolls/${payrollUuid}/skip/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: payrollSkipRequest,
            signal,
        },
        options
    );
};

export const getPayrollRunSkipQueryKey = (payrollUuid: string, payrollSkipRequest?: PayrollSkipRequest) => {
    return ['POST', `/api/payroll/v1/payrolls/${payrollUuid}/skip/`, payrollSkipRequest] as const;
};

export const usePayrollRunSkipQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollRunSkip>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    payrollSkipRequest?: PayrollSkipRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunSkip>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollRunSkipQueryKey(payrollUuid, payrollSkipRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollRunSkip>>> = ({ signal }) =>
        payrollRunSkip(payrollUuid, payrollSkipRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: payrollUuid !== null && payrollUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollRunSkip>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollRunSkipQueryResult = NonNullable<Awaited<ReturnType<typeof payrollRunSkip>>>;
export type PayrollRunSkipQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollRunSkip<
    TData = Awaited<ReturnType<typeof payrollRunSkip>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    payrollSkipRequest: undefined | PayrollSkipRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunSkip>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunSkip>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunSkip>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunSkip<
    TData = Awaited<ReturnType<typeof payrollRunSkip>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    payrollSkipRequest?: PayrollSkipRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunSkip>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunSkip>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunSkip>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunSkip<
    TData = Awaited<ReturnType<typeof payrollRunSkip>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    payrollSkipRequest?: PayrollSkipRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunSkip>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Skip the period
 */

export function usePayrollRunSkip<
    TData = Awaited<ReturnType<typeof payrollRunSkip>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    payrollSkipRequest?: PayrollSkipRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunSkip>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollRunSkipQueryOptions(payrollUuid, payrollSkipRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * **The call that debits.** Guarded server-side on the run's state and on there being no open blocking alert -- the latter re-reads the provider's own blocker list, because the Overview's `can_submit` is allowed to be briefly optimistic and this is where that is made safe.
 *
 * A run past its deadline is prepared before it is refused, so the provider can move its pay day and deadline. When it does, the run is recalculated and refused as `recalculation_required` with `details.pay_date` set to the new pay day: show the new totals and pay day before the member submits again. When the provider does not move the dates, submit returns `deadline_passed`.
 *
 * Accepted, not paid. The provider answers 202 and finishes out of band, so the command stays `sent` until `payroll.processed` confirms it. Poll `/commands/{id}/`; never re-POST with a fresh idempotency key, because that is how one payroll becomes two debits.
 *
 * A refusal is 422 `COMMAND_REFUSED` with `details.reason` -- `deadline_passed`, `payroll_blocked` (with `details.blockers`), `already_processed` or `wrong_status`. A blocked submit is a normal path, not an exception.
 *
 * `recalculation_required` means the provider no longer accepts the run's calculation even though the run still reads as calculated. `details.recalculation_started` says whether a recalculation was started, and `details.recalculation_command_uuid` names it to poll. If it was, wait for that command, re-read the run and show its new totals before the member submits again; until that calculation lands, a submit is refused as `calculation_in_flight`. If it was not, the member can submit again to retry.
 *
 * A run over the company's fast-ACH limit carries a submission blocker the member must answer: `wire_in` or `move_to_four_day`. Without an answer the submit is refused with `details.reason = "submission_blocker_unanswered"` and `details.blockers`, each with its `unblock_options`; re-submit with `submission_blockers`. An answer the run no longer offers is refused as `unblock_option_not_offered`, and a blocker answered twice as `duplicate_blocker_selection`.
 * @summary Submit the run
 */
export const payrollRunSubmit = (
    payrollUuid: string,
    payrollSubmitRequest?: PayrollSubmitRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/payrolls/${payrollUuid}/submit/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: payrollSubmitRequest,
            signal,
        },
        options
    );
};

export const getPayrollRunSubmitQueryKey = (payrollUuid: string, payrollSubmitRequest?: PayrollSubmitRequest) => {
    return ['POST', `/api/payroll/v1/payrolls/${payrollUuid}/submit/`, payrollSubmitRequest] as const;
};

export const usePayrollRunSubmitQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollRunSubmit>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    payrollSubmitRequest?: PayrollSubmitRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunSubmit>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollRunSubmitQueryKey(payrollUuid, payrollSubmitRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollRunSubmit>>> = ({ signal }) =>
        payrollRunSubmit(payrollUuid, payrollSubmitRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: payrollUuid !== null && payrollUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollRunSubmit>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollRunSubmitQueryResult = NonNullable<Awaited<ReturnType<typeof payrollRunSubmit>>>;
export type PayrollRunSubmitQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollRunSubmit<
    TData = Awaited<ReturnType<typeof payrollRunSubmit>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    payrollSubmitRequest: undefined | PayrollSubmitRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunSubmit>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunSubmit>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunSubmit>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunSubmit<
    TData = Awaited<ReturnType<typeof payrollRunSubmit>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    payrollSubmitRequest?: PayrollSubmitRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunSubmit>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunSubmit>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunSubmit>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunSubmit<
    TData = Awaited<ReturnType<typeof payrollRunSubmit>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    payrollSubmitRequest?: PayrollSubmitRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunSubmit>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Submit the run
 */

export function usePayrollRunSubmit<
    TData = Awaited<ReturnType<typeof payrollRunSubmit>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    payrollSubmitRequest?: PayrollSubmitRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunSubmit>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollRunSubmitQueryOptions(payrollUuid, payrollSubmitRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Approved hours for this run's period, in the shape `PATCH /payrolls/{payroll_uuid}/lines/` accepts. A read; `unapproved_worker_count` prompts a review.
 * @summary List approved hours for a run
 */
export const payrollRunTimesheetHoursRetrieve = (
    payrollUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollTimesheetPrefill>(
        { url: `/api/payroll/v1/payrolls/${payrollUuid}/timesheet-hours/`, method: 'GET', signal },
        options
    );
};

export const getPayrollRunTimesheetHoursRetrieveQueryKey = (payrollUuid: string) => {
    return [`/api/payroll/v1/payrolls/${payrollUuid}/timesheet-hours/`] as const;
};

export const usePayrollRunTimesheetHoursRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollRunTimesheetHoursRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunTimesheetHoursRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollRunTimesheetHoursRetrieveQueryKey(payrollUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollRunTimesheetHoursRetrieve>>> = ({ signal }) =>
        payrollRunTimesheetHoursRetrieve(payrollUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: payrollUuid !== null && payrollUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollRunTimesheetHoursRetrieve>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollRunTimesheetHoursRetrieveQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollRunTimesheetHoursRetrieve>>
>;
export type PayrollRunTimesheetHoursRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollRunTimesheetHoursRetrieve<
    TData = Awaited<ReturnType<typeof payrollRunTimesheetHoursRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunTimesheetHoursRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunTimesheetHoursRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunTimesheetHoursRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunTimesheetHoursRetrieve<
    TData = Awaited<ReturnType<typeof payrollRunTimesheetHoursRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunTimesheetHoursRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollRunTimesheetHoursRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollRunTimesheetHoursRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollRunTimesheetHoursRetrieve<
    TData = Awaited<ReturnType<typeof payrollRunTimesheetHoursRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunTimesheetHoursRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List approved hours for a run
 */

export function usePayrollRunTimesheetHoursRetrieve<
    TData = Awaited<ReturnType<typeof payrollRunTimesheetHoursRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollRunTimesheetHoursRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollRunTimesheetHoursRetrieveQueryOptions(payrollUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Streamed PDF, fetched from the provider. Ownership is re-checked on the way through: both the run and the worker are resolved under the caller's own company. Employees only — a contractor is paid through a payment and has a receipt instead, so a contractor's identifier is a 404 here.
 * @summary Retrieve a worker's pay stub for a run
 */
export const payrollPayStubRetrieve = (
    payrollUuid: string,
    workerUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<Blob>(
        {
            url: `/api/payroll/v1/payrolls/${payrollUuid}/workers/${workerUuid}/pay-stub/`,
            method: 'GET',
            responseType: 'blob',
            signal,
        },
        options
    );
};

export const getPayrollPayStubRetrieveQueryKey = (payrollUuid: string, workerUuid: string) => {
    return [`/api/payroll/v1/payrolls/${payrollUuid}/workers/${workerUuid}/pay-stub/`] as const;
};

export const usePayrollPayStubRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollPayStubRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPayStubRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollPayStubRetrieveQueryKey(payrollUuid, workerUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollPayStubRetrieve>>> = ({ signal }) =>
        payrollPayStubRetrieve(payrollUuid, workerUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: payrollUuid !== null && payrollUuid !== undefined && workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollPayStubRetrieve>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollPayStubRetrieveQueryResult = NonNullable<Awaited<ReturnType<typeof payrollPayStubRetrieve>>>;
export type PayrollPayStubRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollPayStubRetrieve<
    TData = Awaited<ReturnType<typeof payrollPayStubRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    workerUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPayStubRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollPayStubRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollPayStubRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollPayStubRetrieve<
    TData = Awaited<ReturnType<typeof payrollPayStubRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPayStubRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollPayStubRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollPayStubRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollPayStubRetrieve<
    TData = Awaited<ReturnType<typeof payrollPayStubRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPayStubRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve a worker's pay stub for a run
 */

export function usePayrollPayStubRetrieve<
    TData = Awaited<ReturnType<typeof payrollPayStubRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    payrollUuid: string,
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollPayStubRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollPayStubRetrieveQueryOptions(payrollUuid, workerUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Year-to-date totals broken down by state and by employee.
 *
 * These are filed figures, and they can exceed the totals from `/ytd/`: they include runs recorded before this account was connected. Use them for reporting and reconciliation; use `/ytd/` for what this account has paid.
 *
 * **Not computed on request.** The response carries the totals last collected and `as_of` says when they were true. A read never blocks on a refresh, and starts one in the background when a payroll has run since. Defaults to the current year in US/Pacific.
 * @summary Retrieve year-to-date totals by state and employee
 */
export const payrollProviderYtdRetrieve = (
    params?: PayrollProviderYtdRetrieveParams,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<ProviderYtd>(
        { url: `/api/payroll/v1/provider-ytd/`, method: 'GET', params, signal },
        options
    );
};

export const getPayrollProviderYtdRetrieveQueryKey = (params?: PayrollProviderYtdRetrieveParams) => {
    return [`/api/payroll/v1/provider-ytd/`, ...(params ? [params] : [])] as const;
};

export const usePayrollProviderYtdRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollProviderYtdRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollProviderYtdRetrieveParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollProviderYtdRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollProviderYtdRetrieveQueryKey(params);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollProviderYtdRetrieve>>> = ({ signal }) =>
        payrollProviderYtdRetrieve(params, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollProviderYtdRetrieve>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollProviderYtdRetrieveQueryResult = NonNullable<Awaited<ReturnType<typeof payrollProviderYtdRetrieve>>>;
export type PayrollProviderYtdRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollProviderYtdRetrieve<
    TData = Awaited<ReturnType<typeof payrollProviderYtdRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params: undefined | PayrollProviderYtdRetrieveParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollProviderYtdRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollProviderYtdRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollProviderYtdRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollProviderYtdRetrieve<
    TData = Awaited<ReturnType<typeof payrollProviderYtdRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollProviderYtdRetrieveParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollProviderYtdRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollProviderYtdRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollProviderYtdRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollProviderYtdRetrieve<
    TData = Awaited<ReturnType<typeof payrollProviderYtdRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollProviderYtdRetrieveParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollProviderYtdRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve year-to-date totals by state and employee
 */

export function usePayrollProviderYtdRetrieve<
    TData = Awaited<ReturnType<typeof payrollProviderYtdRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollProviderYtdRetrieveParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollProviderYtdRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollProviderYtdRetrieveQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Where setup has got to, across both halves of onboarding: the steps the member walks through, and the provisioning we run for them at the provider. `onboarding_status` is `in_review` when the member has finished everything they can see and our side has not — the state that reaches support as "details are still being finalized" — and also while the provider is still reviewing the company after onboarding, which can take up to two business days. `provisioning.is_stalled` says our side has stopped moving and needs a re-drive rather than more waiting.
 * @summary Retrieve payroll setup progress
 */
export const payrollSetupRetrieve = (options?: SecondParameter<typeof payrollApiMutator>, signal?: AbortSignal) => {
    return payrollApiMutator<SetupState>({ url: `/api/payroll/v1/setup/`, method: 'GET', signal }, options);
};

export const getPayrollSetupRetrieveQueryKey = () => {
    return [`/api/payroll/v1/setup/`] as const;
};

export const usePayrollSetupRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollSetupRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupRetrieve>>, TError, TData>>;
    request?: SecondParameter<typeof payrollApiMutator>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollSetupRetrieveQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollSetupRetrieve>>> = ({ signal }) =>
        payrollSetupRetrieve(requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollSetupRetrieve>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollSetupRetrieveQueryResult = NonNullable<Awaited<ReturnType<typeof payrollSetupRetrieve>>>;
export type PayrollSetupRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollSetupRetrieve<
    TData = Awaited<ReturnType<typeof payrollSetupRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollSetupRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollSetupRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollSetupRetrieve<
    TData = Awaited<ReturnType<typeof payrollSetupRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollSetupRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollSetupRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollSetupRetrieve<
    TData = Awaited<ReturnType<typeof payrollSetupRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve payroll setup progress
 */

export function usePayrollSetupRetrieve<
    TData = Awaited<ReturnType<typeof payrollSetupRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollSetupRetrieveQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Mints a Plaid processor token for the chosen account and posts it to the provider, then records the link between that account and our own `PlaidAccount` row -- at the moment it is known, rather than reconciling later by matching last-4.
 *
 * **This replaces the current funding account.** The provider keeps a single default, so sending this for a company that already has one moves payroll onto the new account and retires the old. There is no separate confirm step; say so before you post.
 *
 * The account must already be connected to Collective: this takes Plaid's identifiers for an existing connection, not a Plaid public token. Exchange the public token through the usual Plaid flow first, then name one of the accounts it turned up.
 *
 * A bank Collective cannot reach is 422 `BANK_NOT_CONNECTED` or `BANK_RECONNECT_REQUIRED`, both of which the member fixes themselves in Plaid. A payroll provider connection that has expired or been revoked is 422 `PROVIDER_NOT_CONNECTED`, fixed by reconnecting it. A provider refusal is 422 `COMMAND_REFUSED` -- some institutions cannot mint a processor token at all, and that is the answer a client falls back to manual account entry on. A provider that could not be reached, or did not answer in time, is 503 `PROVIDER_UNAVAILABLE`.
 * @summary Set the account payroll debits
 */
export const payrollSetupBankAccountCreate = (
    setupBankAccountRequest: SetupBankAccountRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/setup/company/bank-accounts/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: setupBankAccountRequest,
            signal,
        },
        options
    );
};

export const getPayrollSetupBankAccountCreateQueryKey = (setupBankAccountRequest?: SetupBankAccountRequest) => {
    return ['POST', `/api/payroll/v1/setup/company/bank-accounts/`, setupBankAccountRequest] as const;
};

export const usePayrollSetupBankAccountCreateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollSetupBankAccountCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    setupBankAccountRequest: SetupBankAccountRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupBankAccountCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollSetupBankAccountCreateQueryKey(setupBankAccountRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollSetupBankAccountCreate>>> = ({ signal }) =>
        payrollSetupBankAccountCreate(setupBankAccountRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollSetupBankAccountCreate>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollSetupBankAccountCreateQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollSetupBankAccountCreate>>
>;
export type PayrollSetupBankAccountCreateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollSetupBankAccountCreate<
    TData = Awaited<ReturnType<typeof payrollSetupBankAccountCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    setupBankAccountRequest: SetupBankAccountRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupBankAccountCreate>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollSetupBankAccountCreate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollSetupBankAccountCreate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollSetupBankAccountCreate<
    TData = Awaited<ReturnType<typeof payrollSetupBankAccountCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    setupBankAccountRequest: SetupBankAccountRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupBankAccountCreate>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollSetupBankAccountCreate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollSetupBankAccountCreate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollSetupBankAccountCreate<
    TData = Awaited<ReturnType<typeof payrollSetupBankAccountCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    setupBankAccountRequest: SetupBankAccountRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupBankAccountCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Set the account payroll debits
 */

export function usePayrollSetupBankAccountCreate<
    TData = Awaited<ReturnType<typeof payrollSetupBankAccountCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    setupBankAccountRequest: SetupBankAccountRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupBankAccountCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollSetupBankAccountCreateQueryOptions(setupBankAccountRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Moves onboarding to `in_review`. Not `completed` — something still has to look at it.
 * @summary Complete payroll setup
 */
export const payrollSetupComplete = (options?: SecondParameter<typeof payrollApiMutator>, signal?: AbortSignal) => {
    return payrollApiMutator<SetupComplete>(
        { url: `/api/payroll/v1/setup/complete/`, method: 'POST', signal },
        options
    );
};

export const getPayrollSetupCompleteQueryKey = () => {
    return ['POST', `/api/payroll/v1/setup/complete/`] as const;
};

export const usePayrollSetupCompleteQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollSetupComplete>>,
    TError = ErrorType<PayrollErrorResponse>
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupComplete>>, TError, TData>>;
    request?: SecondParameter<typeof payrollApiMutator>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollSetupCompleteQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollSetupComplete>>> = ({ signal }) =>
        payrollSetupComplete(requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollSetupComplete>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollSetupCompleteQueryResult = NonNullable<Awaited<ReturnType<typeof payrollSetupComplete>>>;
export type PayrollSetupCompleteQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollSetupComplete<
    TData = Awaited<ReturnType<typeof payrollSetupComplete>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupComplete>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollSetupComplete>>,
                    TError,
                    Awaited<ReturnType<typeof payrollSetupComplete>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollSetupComplete<
    TData = Awaited<ReturnType<typeof payrollSetupComplete>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupComplete>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollSetupComplete>>,
                    TError,
                    Awaited<ReturnType<typeof payrollSetupComplete>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollSetupComplete<
    TData = Awaited<ReturnType<typeof payrollSetupComplete>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupComplete>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Complete payroll setup
 */

export function usePayrollSetupComplete<
    TData = Awaited<ReturnType<typeof payrollSetupComplete>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupComplete>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollSetupCompleteQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Base for every stub: the live views' auth and member scoping, and no handler body.
 *
 * Subclasses set :attr:`operation` and implement their verbs as ``raise
 * self.not_implemented()``. The verb methods exist so an unsupported verb answers 405.
 * @summary List setup documents awaiting signature
 */
export const payrollSetupDocumentsList = (
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<SetupDocumentList>(
        { url: `/api/payroll/v1/setup/documents/`, method: 'GET', signal },
        options
    );
};

export const getPayrollSetupDocumentsListQueryKey = () => {
    return [`/api/payroll/v1/setup/documents/`] as const;
};

export const usePayrollSetupDocumentsListQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollSetupDocumentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupDocumentsList>>, TError, TData>>;
    request?: SecondParameter<typeof payrollApiMutator>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollSetupDocumentsListQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollSetupDocumentsList>>> = ({ signal }) =>
        payrollSetupDocumentsList(requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollSetupDocumentsList>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollSetupDocumentsListQueryResult = NonNullable<Awaited<ReturnType<typeof payrollSetupDocumentsList>>>;
export type PayrollSetupDocumentsListQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollSetupDocumentsList<
    TData = Awaited<ReturnType<typeof payrollSetupDocumentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupDocumentsList>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollSetupDocumentsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollSetupDocumentsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollSetupDocumentsList<
    TData = Awaited<ReturnType<typeof payrollSetupDocumentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupDocumentsList>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollSetupDocumentsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollSetupDocumentsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollSetupDocumentsList<
    TData = Awaited<ReturnType<typeof payrollSetupDocumentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupDocumentsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List setup documents awaiting signature
 */

export function usePayrollSetupDocumentsList<
    TData = Awaited<ReturnType<typeof payrollSetupDocumentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupDocumentsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollSetupDocumentsListQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Form 8655, W-4, deposit authorisation and state forms.
 * @summary Sign a setup document
 */
export const payrollSetupDocumentSign = (
    documentUuid: string,
    setupDocumentSignRequest: SetupDocumentSignRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/setup/documents/${documentUuid}/sign/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: setupDocumentSignRequest,
            signal,
        },
        options
    );
};

export const getPayrollSetupDocumentSignQueryKey = (
    documentUuid: string,
    setupDocumentSignRequest?: SetupDocumentSignRequest
) => {
    return ['POST', `/api/payroll/v1/setup/documents/${documentUuid}/sign/`, setupDocumentSignRequest] as const;
};

export const usePayrollSetupDocumentSignQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollSetupDocumentSign>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    documentUuid: string,
    setupDocumentSignRequest: SetupDocumentSignRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupDocumentSign>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getPayrollSetupDocumentSignQueryKey(documentUuid, setupDocumentSignRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollSetupDocumentSign>>> = ({ signal }) =>
        payrollSetupDocumentSign(documentUuid, setupDocumentSignRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: documentUuid !== null && documentUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollSetupDocumentSign>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollSetupDocumentSignQueryResult = NonNullable<Awaited<ReturnType<typeof payrollSetupDocumentSign>>>;
export type PayrollSetupDocumentSignQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollSetupDocumentSign<
    TData = Awaited<ReturnType<typeof payrollSetupDocumentSign>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    documentUuid: string,
    setupDocumentSignRequest: SetupDocumentSignRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupDocumentSign>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollSetupDocumentSign>>,
                    TError,
                    Awaited<ReturnType<typeof payrollSetupDocumentSign>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollSetupDocumentSign<
    TData = Awaited<ReturnType<typeof payrollSetupDocumentSign>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    documentUuid: string,
    setupDocumentSignRequest: SetupDocumentSignRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupDocumentSign>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollSetupDocumentSign>>,
                    TError,
                    Awaited<ReturnType<typeof payrollSetupDocumentSign>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollSetupDocumentSign<
    TData = Awaited<ReturnType<typeof payrollSetupDocumentSign>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    documentUuid: string,
    setupDocumentSignRequest: SetupDocumentSignRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupDocumentSign>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Sign a setup document
 */

export function usePayrollSetupDocumentSign<
    TData = Awaited<ReturnType<typeof payrollSetupDocumentSign>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    documentUuid: string,
    setupDocumentSignRequest: SetupDocumentSignRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupDocumentSign>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollSetupDocumentSignQueryOptions(documentUuid, setupDocumentSignRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Frequency, first paycheck and pay period. `PUT` because there is one schedule being defined here, not a collection.
 * @summary Set the pay schedule
 */
export const payrollSetupPayScheduleUpdate = (
    setupPayScheduleRequest: SetupPayScheduleRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/setup/pay-schedule/`,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            data: setupPayScheduleRequest,
            signal,
        },
        options
    );
};

export const getPayrollSetupPayScheduleUpdateQueryKey = (setupPayScheduleRequest?: SetupPayScheduleRequest) => {
    return ['PUT', `/api/payroll/v1/setup/pay-schedule/`, setupPayScheduleRequest] as const;
};

export const usePayrollSetupPayScheduleUpdateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollSetupPayScheduleUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    setupPayScheduleRequest: SetupPayScheduleRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupPayScheduleUpdate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollSetupPayScheduleUpdateQueryKey(setupPayScheduleRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollSetupPayScheduleUpdate>>> = ({ signal }) =>
        payrollSetupPayScheduleUpdate(setupPayScheduleRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollSetupPayScheduleUpdate>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollSetupPayScheduleUpdateQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollSetupPayScheduleUpdate>>
>;
export type PayrollSetupPayScheduleUpdateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollSetupPayScheduleUpdate<
    TData = Awaited<ReturnType<typeof payrollSetupPayScheduleUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    setupPayScheduleRequest: SetupPayScheduleRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupPayScheduleUpdate>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollSetupPayScheduleUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollSetupPayScheduleUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollSetupPayScheduleUpdate<
    TData = Awaited<ReturnType<typeof payrollSetupPayScheduleUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    setupPayScheduleRequest: SetupPayScheduleRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupPayScheduleUpdate>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollSetupPayScheduleUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollSetupPayScheduleUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollSetupPayScheduleUpdate<
    TData = Awaited<ReturnType<typeof payrollSetupPayScheduleUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    setupPayScheduleRequest: SetupPayScheduleRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupPayScheduleUpdate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Set the pay schedule
 */

export function usePayrollSetupPayScheduleUpdate<
    TData = Awaited<ReturnType<typeof payrollSetupPayScheduleUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    setupPayScheduleRequest: SetupPayScheduleRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupPayScheduleUpdate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollSetupPayScheduleUpdateQueryOptions(setupPayScheduleRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * One endpoint with a serializer registry keyed by `step`, rather than fifty endpoints. PII in the payload is forwarded to the provider and never persisted.
 * @summary Submit one setup step
 */
export const payrollSetupStepSubmit = (
    step: string,
    setupStepRequest: SetupStepRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<SetupState>(
        {
            url: `/api/payroll/v1/setup/steps/${step}/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: setupStepRequest,
            signal,
        },
        options
    );
};

export const getPayrollSetupStepSubmitQueryKey = (step: string, setupStepRequest?: SetupStepRequest) => {
    return ['POST', `/api/payroll/v1/setup/steps/${step}/`, setupStepRequest] as const;
};

export const usePayrollSetupStepSubmitQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollSetupStepSubmit>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    step: string,
    setupStepRequest: SetupStepRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupStepSubmit>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollSetupStepSubmitQueryKey(step, setupStepRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollSetupStepSubmit>>> = ({ signal }) =>
        payrollSetupStepSubmit(step, setupStepRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: step !== null && step !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollSetupStepSubmit>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollSetupStepSubmitQueryResult = NonNullable<Awaited<ReturnType<typeof payrollSetupStepSubmit>>>;
export type PayrollSetupStepSubmitQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollSetupStepSubmit<
    TData = Awaited<ReturnType<typeof payrollSetupStepSubmit>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    step: string,
    setupStepRequest: SetupStepRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupStepSubmit>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollSetupStepSubmit>>,
                    TError,
                    Awaited<ReturnType<typeof payrollSetupStepSubmit>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollSetupStepSubmit<
    TData = Awaited<ReturnType<typeof payrollSetupStepSubmit>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    step: string,
    setupStepRequest: SetupStepRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupStepSubmit>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollSetupStepSubmit>>,
                    TError,
                    Awaited<ReturnType<typeof payrollSetupStepSubmit>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollSetupStepSubmit<
    TData = Awaited<ReturnType<typeof payrollSetupStepSubmit>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    step: string,
    setupStepRequest: SetupStepRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupStepSubmit>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Submit one setup step
 */

export function usePayrollSetupStepSubmit<
    TData = Awaited<ReturnType<typeof payrollSetupStepSubmit>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    step: string,
    setupStepRequest: SetupStepRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollSetupStepSubmit>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollSetupStepSubmitQueryOptions(step, setupStepRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Every payroll tax payment the provider has made, or scheduled, to a tax agency for this company, most recent first.
 *
 * **This is a payments ledger, not a filing record.** This endpoint reports no filing status — no Form 941, no quarterly return, no acceptance — so nothing here says whether a return was filed or accepted, and no field can be rendered as though it did. `state` describes the payment: `sent` or `scheduled`.
 *
 * Served live on every request, because no table of ours holds these. An empty `results` list therefore always means there is nothing to show; a provider we could not reach is a 503.
 * @summary List payroll tax payments
 */
export const payrollTaxPaymentsList = (options?: SecondParameter<typeof payrollApiMutator>, signal?: AbortSignal) => {
    return payrollApiMutator<PayrollTaxPayments>(
        { url: `/api/payroll/v1/tax-payments/`, method: 'GET', signal },
        options
    );
};

export const getPayrollTaxPaymentsListQueryKey = () => {
    return [`/api/payroll/v1/tax-payments/`] as const;
};

export const usePayrollTaxPaymentsListQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTaxPaymentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTaxPaymentsList>>, TError, TData>>;
    request?: SecondParameter<typeof payrollApiMutator>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTaxPaymentsListQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTaxPaymentsList>>> = ({ signal }) =>
        payrollTaxPaymentsList(requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollTaxPaymentsList>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollTaxPaymentsListQueryResult = NonNullable<Awaited<ReturnType<typeof payrollTaxPaymentsList>>>;
export type PayrollTaxPaymentsListQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTaxPaymentsList<
    TData = Awaited<ReturnType<typeof payrollTaxPaymentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTaxPaymentsList>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTaxPaymentsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTaxPaymentsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTaxPaymentsList<
    TData = Awaited<ReturnType<typeof payrollTaxPaymentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTaxPaymentsList>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTaxPaymentsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTaxPaymentsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTaxPaymentsList<
    TData = Awaited<ReturnType<typeof payrollTaxPaymentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTaxPaymentsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List payroll tax payments
 */

export function usePayrollTaxPaymentsList<
    TData = Awaited<ReturnType<typeof payrollTaxPaymentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTaxPaymentsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTaxPaymentsListQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Employees and contractors in one list, ordered by display name. The list unifies how the two are presented; they remain separate resources, and each row's `worker_type` says which detail route addresses it. Rows carry no first/last name: most contractors are companies, which have neither.
 * @summary List the team
 */
export const payrollTeamList = (
    params?: PayrollTeamListParams,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<TeamList>({ url: `/api/payroll/v1/team/`, method: 'GET', params, signal }, options);
};

export const getPayrollTeamListQueryKey = (params?: PayrollTeamListParams) => {
    return [`/api/payroll/v1/team/`, ...(params ? [params] : [])] as const;
};

export const usePayrollTeamListQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollTeamListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamListQueryKey(params);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamList>>> = ({ signal }) =>
        payrollTeamList(params, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollTeamList>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollTeamListQueryResult = NonNullable<Awaited<ReturnType<typeof payrollTeamList>>>;
export type PayrollTeamListQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamList<
    TData = Awaited<ReturnType<typeof payrollTeamList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params: undefined | PayrollTeamListParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamList>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamList<
    TData = Awaited<ReturnType<typeof payrollTeamList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollTeamListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamList>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamList<
    TData = Awaited<ReturnType<typeof payrollTeamList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollTeamListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List the team
 */

export function usePayrollTeamList<
    TData = Awaited<ReturnType<typeof payrollTeamList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollTeamListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamListQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Creates the contractor at the provider and invites them to onboard themselves: a name or a business, an email and a pay type. Their tax identifier and bank account are theirs to supply.
 *
 * Bills at the contractor add-on rate, and only when they are paid. As with an employee, the billing follows from the provider's `marked_for_self_onboarding` event rather than from this call.
 * @summary Add a 1099 contractor
 */
export const payrollTeamContractorCreate = (
    contractorCreateRequest: ContractorCreateRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/team/contractors/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: contractorCreateRequest,
            signal,
        },
        options
    );
};

export const getPayrollTeamContractorCreateQueryKey = (contractorCreateRequest?: ContractorCreateRequest) => {
    return ['POST', `/api/payroll/v1/team/contractors/`, contractorCreateRequest] as const;
};

export const usePayrollTeamContractorCreateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamContractorCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorCreateRequest: ContractorCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamContractorCreateQueryKey(contractorCreateRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamContractorCreate>>> = ({ signal }) =>
        payrollTeamContractorCreate(contractorCreateRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorCreate>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollTeamContractorCreateQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTeamContractorCreate>>
>;
export type PayrollTeamContractorCreateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamContractorCreate<
    TData = Awaited<ReturnType<typeof payrollTeamContractorCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorCreateRequest: ContractorCreateRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorCreate>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamContractorCreate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamContractorCreate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamContractorCreate<
    TData = Awaited<ReturnType<typeof payrollTeamContractorCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorCreateRequest: ContractorCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorCreate>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamContractorCreate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamContractorCreate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamContractorCreate<
    TData = Awaited<ReturnType<typeof payrollTeamContractorCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorCreateRequest: ContractorCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Add a 1099 contractor
 */

export function usePayrollTeamContractorCreate<
    TData = Awaited<ReturnType<typeof payrollTeamContractorCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    contractorCreateRequest: ContractorCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamContractorCreateQueryOptions(contractorCreateRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Removes the contractor entirely. **This is not how an engagement ends** - that is `terminate`, which keeps them on file with their payment history.
 *
 * Permitted only for a contractor with **no payments at all**; anyone who has ever been paid returns 422. That restriction is tax work rather than bookkeeping: a contractor paid this year is owed a 1099, and deleting them would remove the counterparty from a filing that still has to happen.
 *
 * **Clients must warn before calling this.** A contractor added and then deleted inside the same tax year leaves no trace on the team surface, and the member is the only one who knows whether a payment is about to be made outside the platform. Offer `terminate` as the default and keep deletion off the primary path.
 * @summary Delete a contractor
 */
export const payrollContractorDelete = (
    workerUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        { url: `/api/payroll/v1/team/contractors/${workerUuid}/`, method: 'DELETE', signal },
        options
    );
};

export const getPayrollContractorDeleteQueryKey = (workerUuid: string) => {
    return ['DELETE', `/api/payroll/v1/team/contractors/${workerUuid}/`] as const;
};

export const usePayrollContractorDeleteQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollContractorDelete>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorDelete>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollContractorDeleteQueryKey(workerUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollContractorDelete>>> = ({ signal }) =>
        payrollContractorDelete(workerUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollContractorDelete>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollContractorDeleteQueryResult = NonNullable<Awaited<ReturnType<typeof payrollContractorDelete>>>;
export type PayrollContractorDeleteQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollContractorDelete<
    TData = Awaited<ReturnType<typeof payrollContractorDelete>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorDelete>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorDelete>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorDelete>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorDelete<
    TData = Awaited<ReturnType<typeof payrollContractorDelete>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorDelete>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorDelete>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorDelete>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorDelete<
    TData = Awaited<ReturnType<typeof payrollContractorDelete>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorDelete>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete a contractor
 */

export function usePayrollContractorDelete<
    TData = Awaited<ReturnType<typeof payrollContractorDelete>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorDelete>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollContractorDeleteQueryOptions(workerUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * A 1099 worker, which may be a person or a company. All three name fields are blankable; `entity_kind` says which of them is the real one.
 * @summary Retrieve a contractor
 */
export const payrollTeamContractorRetrieve = (
    workerUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<WorkerDetail>(
        { url: `/api/payroll/v1/team/contractors/${workerUuid}/`, method: 'GET', signal },
        options
    );
};

export const getPayrollTeamContractorRetrieveQueryKey = (workerUuid: string) => {
    return [`/api/payroll/v1/team/contractors/${workerUuid}/`] as const;
};

export const usePayrollTeamContractorRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamContractorRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamContractorRetrieveQueryKey(workerUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamContractorRetrieve>>> = ({ signal }) =>
        payrollTeamContractorRetrieve(workerUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollTeamContractorRetrieve>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollTeamContractorRetrieveQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTeamContractorRetrieve>>
>;
export type PayrollTeamContractorRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamContractorRetrieve<
    TData = Awaited<ReturnType<typeof payrollTeamContractorRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamContractorRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamContractorRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamContractorRetrieve<
    TData = Awaited<ReturnType<typeof payrollTeamContractorRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamContractorRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamContractorRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamContractorRetrieve<
    TData = Awaited<ReturnType<typeof payrollTeamContractorRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve a contractor
 */

export function usePayrollTeamContractorRetrieve<
    TData = Awaited<ReturnType<typeof payrollTeamContractorRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamContractorRetrieveQueryOptions(workerUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Names, business name, email and **wage**. The contractor *type* cannot be changed: the provider warns that switching between business and individual leaves one 1099 filed across two identities.
 *
 * **Wage.** `wage_type` and `hourly_rate` change how the contractor is paid from now on, with no effective date. Switching to `hourly` needs a rate, sent or already on file. A rate for a `fixed` contractor, or for one switching to `fixed`, is refused. Both are 400 `INVALID_REQUEST` naming `hourly_rate`, and nothing is sent. Payments that already exist keep the wage type and rate they were created with; to pay one at the new rate, cancel it and create it again. New payment lines must match the new `wage_type`, or they are refused with `wage_type_mismatch`.
 *
 * A wage change reads the contractor back before answering, so `synced: true` means the team read already shows it. `synced: false` means it is confirmed but our copy is still catching up: refetch in a moment.
 *
 * **412 `STALE_TOKEN`** means the contractor changed at the provider between our read and our write; reload and retry. **422 `COMMAND_REFUSED`** is the provider refusing the change on its own terms, which is where a missing rate lands if our copy thought one was on file; see `message`.
 *
 * Ending an engagement is not an edit and is not here. That is `POST /team/contractors/{worker_uuid}/terminate/`, which has its own preconditions and emits the provider's deactivation event; coming back is `.../rehire/`.
 * @summary Update a contractor
 */
export const payrollTeamContractorUpdate = (
    workerUuid: string,
    patchedContractorUpdateRequest?: PatchedContractorUpdateRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/team/contractors/${workerUuid}/`,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            data: patchedContractorUpdateRequest,
            signal,
        },
        options
    );
};

export const getPayrollTeamContractorUpdateQueryKey = (
    workerUuid: string,
    patchedContractorUpdateRequest?: PatchedContractorUpdateRequest
) => {
    return ['PATCH', `/api/payroll/v1/team/contractors/${workerUuid}/`, patchedContractorUpdateRequest] as const;
};

export const usePayrollTeamContractorUpdateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamContractorUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    patchedContractorUpdateRequest?: PatchedContractorUpdateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorUpdate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getPayrollTeamContractorUpdateQueryKey(workerUuid, patchedContractorUpdateRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamContractorUpdate>>> = ({ signal }) =>
        payrollTeamContractorUpdate(workerUuid, patchedContractorUpdateRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorUpdate>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollTeamContractorUpdateQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTeamContractorUpdate>>
>;
export type PayrollTeamContractorUpdateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamContractorUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamContractorUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    patchedContractorUpdateRequest: undefined | PatchedContractorUpdateRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorUpdate>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamContractorUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamContractorUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamContractorUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamContractorUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    patchedContractorUpdateRequest?: PatchedContractorUpdateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorUpdate>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamContractorUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamContractorUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamContractorUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamContractorUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    patchedContractorUpdateRequest?: PatchedContractorUpdateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorUpdate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update a contractor
 */

export function usePayrollTeamContractorUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamContractorUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    patchedContractorUpdateRequest?: PatchedContractorUpdateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorUpdate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamContractorUpdateQueryOptions(
        workerUuid,
        patchedContractorUpdateRequest,
        options
    );

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * How this 1099 worker is paid. The same payload as the employee route -- the provider documents the two identically -- from the contractor's own resource.
 * @summary Retrieve a contractor's payment method
 */
export const payrollTeamContractorPaymentMethod = (
    workerUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<WorkerPaymentMethod>(
        { url: `/api/payroll/v1/team/contractors/${workerUuid}/payment-method/`, method: 'GET', signal },
        options
    );
};

export const getPayrollTeamContractorPaymentMethodQueryKey = (workerUuid: string) => {
    return [`/api/payroll/v1/team/contractors/${workerUuid}/payment-method/`] as const;
};

export const usePayrollTeamContractorPaymentMethodQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamContractorPaymentMethod>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorPaymentMethod>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamContractorPaymentMethodQueryKey(workerUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamContractorPaymentMethod>>> = ({ signal }) =>
        payrollTeamContractorPaymentMethod(workerUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollTeamContractorPaymentMethod>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollTeamContractorPaymentMethodQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTeamContractorPaymentMethod>>
>;
export type PayrollTeamContractorPaymentMethodQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamContractorPaymentMethod<
    TData = Awaited<ReturnType<typeof payrollTeamContractorPaymentMethod>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorPaymentMethod>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamContractorPaymentMethod>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamContractorPaymentMethod>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamContractorPaymentMethod<
    TData = Awaited<ReturnType<typeof payrollTeamContractorPaymentMethod>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorPaymentMethod>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamContractorPaymentMethod>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamContractorPaymentMethod>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamContractorPaymentMethod<
    TData = Awaited<ReturnType<typeof payrollTeamContractorPaymentMethod>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorPaymentMethod>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve a contractor's payment method
 */

export function usePayrollTeamContractorPaymentMethod<
    TData = Awaited<ReturnType<typeof payrollTeamContractorPaymentMethod>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorPaymentMethod>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamContractorPaymentMethodQueryOptions(workerUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Point this worker's pay at accounts the provider already holds.
 *
 * **Carries no routing number and no account number.** Each account is named by the id `GET .../bank-accounts/` returned for it, so this endpoint cannot be the place either leaks. Registering a *new* account is a different operation and is not served here.
 *
 * `check` clears the splits. A `direct_deposit` needs `split_by` and at least one split: percentages must total 100, and an `amount` split needs exactly one account with a null amount to take the remainder.
 *
 * **The provider holds at most one bank account per contractor**, so in practice this switches between direct deposit and check rather than choosing between accounts. Moving a contractor's pay to a different account means registering one, which replaces the existing account rather than adding to it.
 * @summary Update a contractor's payment method
 */
export const payrollTeamContractorPaymentMethodUpdate = (
    workerUuid: string,
    workerPaymentMethodUpdateRequest: WorkerPaymentMethodUpdateRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/team/contractors/${workerUuid}/payment-method/`,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            data: workerPaymentMethodUpdateRequest,
            signal,
        },
        options
    );
};

export const getPayrollTeamContractorPaymentMethodUpdateQueryKey = (
    workerUuid: string,
    workerPaymentMethodUpdateRequest?: WorkerPaymentMethodUpdateRequest
) => {
    return [
        'PUT',
        `/api/payroll/v1/team/contractors/${workerUuid}/payment-method/`,
        workerPaymentMethodUpdateRequest,
    ] as const;
};

export const usePayrollTeamContractorPaymentMethodUpdateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamContractorPaymentMethodUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerPaymentMethodUpdateRequest: WorkerPaymentMethodUpdateRequest,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorPaymentMethodUpdate>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ??
        getPayrollTeamContractorPaymentMethodUpdateQueryKey(workerUuid, workerPaymentMethodUpdateRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamContractorPaymentMethodUpdate>>> = ({ signal }) =>
        payrollTeamContractorPaymentMethodUpdate(workerUuid, workerPaymentMethodUpdateRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollTeamContractorPaymentMethodUpdate>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollTeamContractorPaymentMethodUpdateQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTeamContractorPaymentMethodUpdate>>
>;
export type PayrollTeamContractorPaymentMethodUpdateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamContractorPaymentMethodUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamContractorPaymentMethodUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerPaymentMethodUpdateRequest: WorkerPaymentMethodUpdateRequest,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorPaymentMethodUpdate>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamContractorPaymentMethodUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamContractorPaymentMethodUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamContractorPaymentMethodUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamContractorPaymentMethodUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerPaymentMethodUpdateRequest: WorkerPaymentMethodUpdateRequest,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorPaymentMethodUpdate>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamContractorPaymentMethodUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamContractorPaymentMethodUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamContractorPaymentMethodUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamContractorPaymentMethodUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerPaymentMethodUpdateRequest: WorkerPaymentMethodUpdateRequest,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorPaymentMethodUpdate>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update a contractor's payment method
 */

export function usePayrollTeamContractorPaymentMethodUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamContractorPaymentMethodUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerPaymentMethodUpdateRequest: WorkerPaymentMethodUpdateRequest,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamContractorPaymentMethodUpdate>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamContractorPaymentMethodUpdateQueryOptions(
        workerUuid,
        workerPaymentMethodUpdateRequest,
        options
    );

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Refused unless the contractor is currently dismissed, and refused again if a future engagement is already scheduled.
 * @summary Rehire a contractor
 */
export const payrollContractorRehire = (
    workerUuid: string,
    contractorRehireRequest: ContractorRehireRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/team/contractors/${workerUuid}/rehire/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: contractorRehireRequest,
            signal,
        },
        options
    );
};

export const getPayrollContractorRehireQueryKey = (
    workerUuid: string,
    contractorRehireRequest?: ContractorRehireRequest
) => {
    return ['POST', `/api/payroll/v1/team/contractors/${workerUuid}/rehire/`, contractorRehireRequest] as const;
};

export const usePayrollContractorRehireQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollContractorRehire>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    contractorRehireRequest: ContractorRehireRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorRehire>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollContractorRehireQueryKey(workerUuid, contractorRehireRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollContractorRehire>>> = ({ signal }) =>
        payrollContractorRehire(workerUuid, contractorRehireRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollContractorRehire>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollContractorRehireQueryResult = NonNullable<Awaited<ReturnType<typeof payrollContractorRehire>>>;
export type PayrollContractorRehireQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollContractorRehire<
    TData = Awaited<ReturnType<typeof payrollContractorRehire>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    contractorRehireRequest: ContractorRehireRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorRehire>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorRehire>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorRehire>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorRehire<
    TData = Awaited<ReturnType<typeof payrollContractorRehire>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    contractorRehireRequest: ContractorRehireRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorRehire>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorRehire>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorRehire>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorRehire<
    TData = Awaited<ReturnType<typeof payrollContractorRehire>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    contractorRehireRequest: ContractorRehireRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorRehire>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Rehire a contractor
 */

export function usePayrollContractorRehire<
    TData = Awaited<ReturnType<typeof payrollContractorRehire>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    contractorRehireRequest: ContractorRehireRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorRehire>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollContractorRehireQueryOptions(workerUuid, contractorRehireRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Records a 1099 worker's last day. Unlike the employee equivalent this takes only a date: there is no final-wage run to schedule, and the provider keeps no cancellable termination record, so the way back is `rehire` rather than a cancellation.
 *
 * The contractor stays on file with their payment history, because a 1099 is still owed for whatever they were paid this year.
 * @summary Terminate a contractor
 */
export const payrollContractorTerminate = (
    workerUuid: string,
    contractorTerminateRequest: ContractorTerminateRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/team/contractors/${workerUuid}/terminate/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: contractorTerminateRequest,
            signal,
        },
        options
    );
};

export const getPayrollContractorTerminateQueryKey = (
    workerUuid: string,
    contractorTerminateRequest?: ContractorTerminateRequest
) => {
    return ['POST', `/api/payroll/v1/team/contractors/${workerUuid}/terminate/`, contractorTerminateRequest] as const;
};

export const usePayrollContractorTerminateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollContractorTerminate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    contractorTerminateRequest: ContractorTerminateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorTerminate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getPayrollContractorTerminateQueryKey(workerUuid, contractorTerminateRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollContractorTerminate>>> = ({ signal }) =>
        payrollContractorTerminate(workerUuid, contractorTerminateRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollContractorTerminate>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollContractorTerminateQueryResult = NonNullable<Awaited<ReturnType<typeof payrollContractorTerminate>>>;
export type PayrollContractorTerminateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollContractorTerminate<
    TData = Awaited<ReturnType<typeof payrollContractorTerminate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    contractorTerminateRequest: ContractorTerminateRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorTerminate>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorTerminate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorTerminate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorTerminate<
    TData = Awaited<ReturnType<typeof payrollContractorTerminate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    contractorTerminateRequest: ContractorTerminateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorTerminate>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollContractorTerminate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollContractorTerminate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollContractorTerminate<
    TData = Awaited<ReturnType<typeof payrollContractorTerminate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    contractorTerminateRequest: ContractorTerminateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorTerminate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Terminate a contractor
 */

export function usePayrollContractorTerminate<
    TData = Awaited<ReturnType<typeof payrollContractorTerminate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    contractorTerminateRequest: ContractorTerminateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollContractorTerminate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollContractorTerminateQueryOptions(workerUuid, contractorTerminateRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Creates the employee at the provider and invites them to onboard themselves. Only what the employer owns is taken here -- who the person is, where they work, what they are paid. The SSN, date of birth, home address and bank account are the worker's to supply, and the invitation is what asks them to.
 *
 * Costs more to add than a contractor because a work location can trigger a state registration, and bills as an add-on whether or not payroll runs. Neither is this endpoint's doing: both follow from the provider's own `marked_for_self_onboarding` event, which the existing webhook handlers act on.
 *
 * The 202 carries no worker identifier. The new worker appears on the next `GET /team/`, which is what makes it addressable on the detail route.
 *
 * **Works from home.** Send `work_from_home` and `work_state` instead of `work_location_uuid`. The employee is created with no work address; once they enter their home address while onboarding, a company location is opened there and becomes their work address. A `work_state` outside the business's states answers **409 `OUT_OF_STATE_UNCONFIRMED`** (`details.work_state`, `details.business_states`) until `confirm_out_of_state` is sent. Nothing is recorded by the refusal, so the same `Idempotency-Key` can be sent again with the confirmation.
 * @summary Add a W-2 employee
 */
export const payrollTeamEmployeeCreate = (
    employeeCreateRequest: EmployeeCreateRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/team/employees/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: employeeCreateRequest,
            signal,
        },
        options
    );
};

export const getPayrollTeamEmployeeCreateQueryKey = (employeeCreateRequest?: EmployeeCreateRequest) => {
    return ['POST', `/api/payroll/v1/team/employees/`, employeeCreateRequest] as const;
};

export const usePayrollTeamEmployeeCreateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    employeeCreateRequest: EmployeeCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamEmployeeCreateQueryKey(employeeCreateRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamEmployeeCreate>>> = ({ signal }) =>
        payrollTeamEmployeeCreate(employeeCreateRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeCreate>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollTeamEmployeeCreateQueryResult = NonNullable<Awaited<ReturnType<typeof payrollTeamEmployeeCreate>>>;
export type PayrollTeamEmployeeCreateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamEmployeeCreate<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    employeeCreateRequest: EmployeeCreateRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeCreate>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeCreate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeCreate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeCreate<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    employeeCreateRequest: EmployeeCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeCreate>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeCreate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeCreate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeCreate<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    employeeCreateRequest: EmployeeCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Add a W-2 employee
 */

export function usePayrollTeamEmployeeCreate<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    employeeCreateRequest: EmployeeCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamEmployeeCreateQueryOptions(employeeCreateRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Removes a W-2 worker who never finished onboarding. **This is not how an employment ends** - that is `terminate`, which keeps the worker on file so their W-2 still reaches them the following January.
 *
 * Permitted only while the employee is still onboarding; anyone already onboarded returns 422 and has to be terminated instead. So this is the undo for a row added by mistake, not a way to remove somebody who has worked.
 *
 * **Clients should confirm before calling it.** The distinction between deleting a half-added hire and ending a real one is invisible from the button, and only one of them is reversible.
 * @summary Delete an onboarding employee
 */
export const payrollEmployeeDelete = (
    workerUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        { url: `/api/payroll/v1/team/employees/${workerUuid}/`, method: 'DELETE', signal },
        options
    );
};

export const getPayrollEmployeeDeleteQueryKey = (workerUuid: string) => {
    return ['DELETE', `/api/payroll/v1/team/employees/${workerUuid}/`] as const;
};

export const usePayrollEmployeeDeleteQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollEmployeeDelete>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeDelete>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollEmployeeDeleteQueryKey(workerUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollEmployeeDelete>>> = ({ signal }) =>
        payrollEmployeeDelete(workerUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeDelete>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollEmployeeDeleteQueryResult = NonNullable<Awaited<ReturnType<typeof payrollEmployeeDelete>>>;
export type PayrollEmployeeDeleteQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollEmployeeDelete<
    TData = Awaited<ReturnType<typeof payrollEmployeeDelete>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeDelete>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollEmployeeDelete>>,
                    TError,
                    Awaited<ReturnType<typeof payrollEmployeeDelete>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollEmployeeDelete<
    TData = Awaited<ReturnType<typeof payrollEmployeeDelete>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeDelete>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollEmployeeDelete>>,
                    TError,
                    Awaited<ReturnType<typeof payrollEmployeeDelete>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollEmployeeDelete<
    TData = Awaited<ReturnType<typeof payrollEmployeeDelete>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeDelete>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete an onboarding employee
 */

export function usePayrollEmployeeDelete<
    TData = Awaited<ReturnType<typeof payrollEmployeeDelete>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeDelete>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollEmployeeDeleteQueryOptions(workerUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * A W-2 worker. Carries compensation, which a contractor has no equivalent of -- their pay lives on the payment, not on the worker.
 * @summary Retrieve an employee
 */
export const payrollTeamEmployeeRetrieve = (
    workerUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<WorkerDetail>(
        { url: `/api/payroll/v1/team/employees/${workerUuid}/`, method: 'GET', signal },
        options
    );
};

export const getPayrollTeamEmployeeRetrieveQueryKey = (workerUuid: string) => {
    return [`/api/payroll/v1/team/employees/${workerUuid}/`] as const;
};

export const usePayrollTeamEmployeeRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamEmployeeRetrieveQueryKey(workerUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamEmployeeRetrieve>>> = ({ signal }) =>
        payrollTeamEmployeeRetrieve(workerUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeRetrieve>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollTeamEmployeeRetrieveQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTeamEmployeeRetrieve>>
>;
export type PayrollTeamEmployeeRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamEmployeeRetrieve<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeRetrieve<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeRetrieve<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve an employee
 */

export function usePayrollTeamEmployeeRetrieve<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamEmployeeRetrieveQueryOptions(workerUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Identity and job title. Compensation is not here: a rate change is `PATCH /team/employees/{worker_uuid}/compensation/`, or `PATCH /compensation/salary/` for the owner. A title change is a second provider call against a separate resource, so a request carrying one can half-apply; the command records how far it got.
 * @summary Update an employee
 */
export const payrollTeamEmployeeUpdate = (
    workerUuid: string,
    patchedEmployeeUpdateRequest?: PatchedEmployeeUpdateRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/team/employees/${workerUuid}/`,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            data: patchedEmployeeUpdateRequest,
            signal,
        },
        options
    );
};

export const getPayrollTeamEmployeeUpdateQueryKey = (
    workerUuid: string,
    patchedEmployeeUpdateRequest?: PatchedEmployeeUpdateRequest
) => {
    return ['PATCH', `/api/payroll/v1/team/employees/${workerUuid}/`, patchedEmployeeUpdateRequest] as const;
};

export const usePayrollTeamEmployeeUpdateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    patchedEmployeeUpdateRequest?: PatchedEmployeeUpdateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeUpdate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getPayrollTeamEmployeeUpdateQueryKey(workerUuid, patchedEmployeeUpdateRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamEmployeeUpdate>>> = ({ signal }) =>
        payrollTeamEmployeeUpdate(workerUuid, patchedEmployeeUpdateRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeUpdate>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollTeamEmployeeUpdateQueryResult = NonNullable<Awaited<ReturnType<typeof payrollTeamEmployeeUpdate>>>;
export type PayrollTeamEmployeeUpdateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamEmployeeUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    patchedEmployeeUpdateRequest: undefined | PatchedEmployeeUpdateRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeUpdate>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    patchedEmployeeUpdateRequest?: PatchedEmployeeUpdateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeUpdate>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    patchedEmployeeUpdateRequest?: PatchedEmployeeUpdateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeUpdate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update an employee
 */

export function usePayrollTeamEmployeeUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    patchedEmployeeUpdateRequest?: PatchedEmployeeUpdateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeUpdate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamEmployeeUpdateQueryOptions(workerUuid, patchedEmployeeUpdateRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Every home and work address on file for this W-2 worker, each dated and flagged `active` for the one in force, newest first — a live read.
 *
 * Employees only — a contractor's address is a single undated record.
 * @summary Retrieve an employee's address history
 */
export const payrollTeamEmployeeAddressHistory = (
    workerUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<EmployeeAddressHistory>(
        { url: `/api/payroll/v1/team/employees/${workerUuid}/address-history/`, method: 'GET', signal },
        options
    );
};

export const getPayrollTeamEmployeeAddressHistoryQueryKey = (workerUuid: string) => {
    return [`/api/payroll/v1/team/employees/${workerUuid}/address-history/`] as const;
};

export const usePayrollTeamEmployeeAddressHistoryQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeAddressHistory>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeAddressHistory>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamEmployeeAddressHistoryQueryKey(workerUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamEmployeeAddressHistory>>> = ({ signal }) =>
        payrollTeamEmployeeAddressHistory(workerUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollTeamEmployeeAddressHistory>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollTeamEmployeeAddressHistoryQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTeamEmployeeAddressHistory>>
>;
export type PayrollTeamEmployeeAddressHistoryQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamEmployeeAddressHistory<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeAddressHistory>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeAddressHistory>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeAddressHistory>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeAddressHistory>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeAddressHistory<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeAddressHistory>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeAddressHistory>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeAddressHistory>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeAddressHistory>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeAddressHistory<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeAddressHistory>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeAddressHistory>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve an employee's address history
 */

export function usePayrollTeamEmployeeAddressHistory<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeAddressHistory>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeAddressHistory>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamEmployeeAddressHistoryQueryOptions(workerUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * The accounts this W-2 worker is paid into. A live read, and no copy is kept.
 *
 * **This is where the account type lives.** The payment-method route carries the splits and the masked number but not whether an account is checking or savings, so the two routes are read together.
 *
 * **No routing number is published**, on this route or any other.
 *
 * **An empty list is the ordinary answer** for a worker paid by cheque and for one part-way through onboarding. It is not an error.
 * @summary List an employee's bank accounts
 */
export const payrollTeamEmployeeBankAccounts = (
    workerUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<WorkerBankAccountList>(
        { url: `/api/payroll/v1/team/employees/${workerUuid}/bank-accounts/`, method: 'GET', signal },
        options
    );
};

export const getPayrollTeamEmployeeBankAccountsQueryKey = (workerUuid: string) => {
    return [`/api/payroll/v1/team/employees/${workerUuid}/bank-accounts/`] as const;
};

export const usePayrollTeamEmployeeBankAccountsQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeBankAccounts>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeBankAccounts>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamEmployeeBankAccountsQueryKey(workerUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamEmployeeBankAccounts>>> = ({ signal }) =>
        payrollTeamEmployeeBankAccounts(workerUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollTeamEmployeeBankAccounts>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollTeamEmployeeBankAccountsQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTeamEmployeeBankAccounts>>
>;
export type PayrollTeamEmployeeBankAccountsQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamEmployeeBankAccounts<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeBankAccounts>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeBankAccounts>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeBankAccounts>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeBankAccounts>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeBankAccounts<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeBankAccounts>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeBankAccounts>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeBankAccounts>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeBankAccounts>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeBankAccounts<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeBankAccounts>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeBankAccounts>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List an employee's bank accounts
 */

export function usePayrollTeamEmployeeBankAccounts<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeBankAccounts>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeBankAccounts>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamEmployeeBankAccountsQueryOptions(workerUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Sets a new rate and payment unit on the employee's **primary** job, the one `GET .../jobs/` flags `is_primary` and the worker detail quotes. The classification (`flsa_status`) is kept as it is.
 *
 * `effective_date` defaults to today and cannot be in the past.
 *
 * **Not for the company owner.** Their salary is set through `PATCH /compensation/salary/`, which also keeps the reasonable-compensation benchmark and catch-up figures in step, so this route answers 409 `OWNER_COMPENSATION_MANAGED_ELSEWHERE` for them without contacting the provider.
 *
 * A command: poll `poll_url` for the outcome. The change counts as applied only once the command is `confirmed`; re-read `GET .../jobs/` afterwards for the rate in force.
 * @summary Change an employee's pay rate
 */
export const payrollTeamEmployeeCompensationUpdate = (
    workerUuid: string,
    employeeCompensationUpdateRequest: EmployeeCompensationUpdateRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/team/employees/${workerUuid}/compensation/`,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            data: employeeCompensationUpdateRequest,
            signal,
        },
        options
    );
};

export const getPayrollTeamEmployeeCompensationUpdateQueryKey = (
    workerUuid: string,
    employeeCompensationUpdateRequest?: EmployeeCompensationUpdateRequest
) => {
    return [
        'PATCH',
        `/api/payroll/v1/team/employees/${workerUuid}/compensation/`,
        employeeCompensationUpdateRequest,
    ] as const;
};

export const usePayrollTeamEmployeeCompensationUpdateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeCompensationUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeCompensationUpdateRequest: EmployeeCompensationUpdateRequest,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeCompensationUpdate>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ??
        getPayrollTeamEmployeeCompensationUpdateQueryKey(workerUuid, employeeCompensationUpdateRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamEmployeeCompensationUpdate>>> = ({ signal }) =>
        payrollTeamEmployeeCompensationUpdate(workerUuid, employeeCompensationUpdateRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollTeamEmployeeCompensationUpdate>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollTeamEmployeeCompensationUpdateQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTeamEmployeeCompensationUpdate>>
>;
export type PayrollTeamEmployeeCompensationUpdateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamEmployeeCompensationUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeCompensationUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeCompensationUpdateRequest: EmployeeCompensationUpdateRequest,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeCompensationUpdate>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeCompensationUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeCompensationUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeCompensationUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeCompensationUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeCompensationUpdateRequest: EmployeeCompensationUpdateRequest,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeCompensationUpdate>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeCompensationUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeCompensationUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeCompensationUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeCompensationUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeCompensationUpdateRequest: EmployeeCompensationUpdateRequest,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeCompensationUpdate>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Change an employee's pay rate
 */

export function usePayrollTeamEmployeeCompensationUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeCompensationUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeCompensationUpdateRequest: EmployeeCompensationUpdateRequest,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeCompensationUpdate>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamEmployeeCompensationUpdateQueryOptions(
        workerUuid,
        employeeCompensationUpdateRequest,
        options
    );

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * This W-2 worker's W-4, a live read.
 *
 * **Read `w4_data_type` first**: the provider serves one resource in two shapes and they barely overlap, so it says which of the amounts below mean anything. Blank means no W-4 on file, which is an onboarding state rather than an error.
 *
 * Employees only — a contractor files their own taxes and the provider exposes no counterpart resource.
 * @summary Retrieve an employee's federal withholding election
 */
export const payrollTeamEmployeeFederalTaxes = (
    workerUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<EmployeeFederalTaxes>(
        { url: `/api/payroll/v1/team/employees/${workerUuid}/federal-taxes/`, method: 'GET', signal },
        options
    );
};

export const getPayrollTeamEmployeeFederalTaxesQueryKey = (workerUuid: string) => {
    return [`/api/payroll/v1/team/employees/${workerUuid}/federal-taxes/`] as const;
};

export const usePayrollTeamEmployeeFederalTaxesQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeFederalTaxes>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeFederalTaxes>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamEmployeeFederalTaxesQueryKey(workerUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamEmployeeFederalTaxes>>> = ({ signal }) =>
        payrollTeamEmployeeFederalTaxes(workerUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollTeamEmployeeFederalTaxes>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollTeamEmployeeFederalTaxesQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTeamEmployeeFederalTaxes>>
>;
export type PayrollTeamEmployeeFederalTaxesQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamEmployeeFederalTaxes<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeFederalTaxes>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeFederalTaxes>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeFederalTaxes>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeFederalTaxes>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeFederalTaxes<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeFederalTaxes>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeFederalTaxes>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeFederalTaxes>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeFederalTaxes>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeFederalTaxes<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeFederalTaxes>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeFederalTaxes>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve an employee's federal withholding election
 */

export function usePayrollTeamEmployeeFederalTaxes<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeFederalTaxes>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeFederalTaxes>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamEmployeeFederalTaxesQueryOptions(workerUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Court-ordered support, back taxes, or an employer loan being repaid.
 *
 * **A live read.** The order is the provider's record; this endpoint shows it back and keeps no copy, which is why it has no mirrored counterpart and no `as_of`.
 *
 * **An empty list is the ordinary answer** — most workers have none. Render an empty state rather than hiding the section.
 *
 * Employees only: a contractor is paid on an invoice and has nothing withheld.
 * @summary List an employee's withholding orders
 */
export const payrollTeamEmployeeGarnishmentsList = (
    workerUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<WorkerGarnishmentList>(
        { url: `/api/payroll/v1/team/employees/${workerUuid}/garnishments/`, method: 'GET', signal },
        options
    );
};

export const getPayrollTeamEmployeeGarnishmentsListQueryKey = (workerUuid: string) => {
    return [`/api/payroll/v1/team/employees/${workerUuid}/garnishments/`] as const;
};

export const usePayrollTeamEmployeeGarnishmentsListQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeGarnishmentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeGarnishmentsList>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamEmployeeGarnishmentsListQueryKey(workerUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamEmployeeGarnishmentsList>>> = ({ signal }) =>
        payrollTeamEmployeeGarnishmentsList(workerUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollTeamEmployeeGarnishmentsList>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollTeamEmployeeGarnishmentsListQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTeamEmployeeGarnishmentsList>>
>;
export type PayrollTeamEmployeeGarnishmentsListQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamEmployeeGarnishmentsList<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeGarnishmentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeGarnishmentsList>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeGarnishmentsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeGarnishmentsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeGarnishmentsList<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeGarnishmentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeGarnishmentsList>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeGarnishmentsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeGarnishmentsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeGarnishmentsList<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeGarnishmentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeGarnishmentsList>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List an employee's withholding orders
 */

export function usePayrollTeamEmployeeGarnishmentsList<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeGarnishmentsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeGarnishmentsList>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamEmployeeGarnishmentsListQueryOptions(workerUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Records where a W-2 worker lives, on their behalf. Say which change it is, because the two have opposite effects on the worker's history:
 *
 * * **`move`** — they now live somewhere else, from `effective_date`. The old address stays in their history (see `GET .../address-history/`).
 * * **`correction`** — the address on file was wrong. It is fixed in place and keeps its date, so the history reads as though they always lived at the corrected address.
 *
 * A correction for an employee with no address on file is refused with `HOME_ADDRESS_NOT_ON_FILE`: record a move instead. A move dated on an address that a later one has already replaced is refused with `EFFECTIVE_DATE_SUPERSEDED`: choose another date. **Employees only** — a contractor's address has no history at the provider.
 *
 * **Works from home.** For an employee with `works_from_home`, a move also moves their work address: a company location is opened at the new home and they are placed there from `effective_date`. Refusals, before anything is recorded: **409 `OUT_OF_STATE_UNCONFIRMED`** when the new state is outside the business's states and not the one they already work in, until `confirm_out_of_state` is sent; **422 `BUSINESS_PHONE_REQUIRED`** when the business has no phone number for the location.
 *
 * A correction moves the work address too, shortly after rather than in this request: once the provider reports the corrected address, the work address follows it to the corrected home from today. A correction into a state outside the business's states is not followed; it opens a `WORK_STATE_MISMATCH` alert, and the owner confirms it from the Change work location dialog.
 * @summary Change an employee's home address
 */
export const payrollEmployeeHomeAddressChange = (
    workerUuid: string,
    employeeHomeAddressChange: EmployeeHomeAddressChange,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/team/employees/${workerUuid}/home-address/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: employeeHomeAddressChange,
            signal,
        },
        options
    );
};

export const getPayrollEmployeeHomeAddressChangeQueryKey = (
    workerUuid: string,
    employeeHomeAddressChange?: EmployeeHomeAddressChange
) => {
    return ['POST', `/api/payroll/v1/team/employees/${workerUuid}/home-address/`, employeeHomeAddressChange] as const;
};

export const usePayrollEmployeeHomeAddressChangeQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollEmployeeHomeAddressChange>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeHomeAddressChange: EmployeeHomeAddressChange,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeHomeAddressChange>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getPayrollEmployeeHomeAddressChangeQueryKey(workerUuid, employeeHomeAddressChange);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollEmployeeHomeAddressChange>>> = ({ signal }) =>
        payrollEmployeeHomeAddressChange(workerUuid, employeeHomeAddressChange, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollEmployeeHomeAddressChange>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollEmployeeHomeAddressChangeQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollEmployeeHomeAddressChange>>
>;
export type PayrollEmployeeHomeAddressChangeQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollEmployeeHomeAddressChange<
    TData = Awaited<ReturnType<typeof payrollEmployeeHomeAddressChange>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeHomeAddressChange: EmployeeHomeAddressChange,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeHomeAddressChange>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollEmployeeHomeAddressChange>>,
                    TError,
                    Awaited<ReturnType<typeof payrollEmployeeHomeAddressChange>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollEmployeeHomeAddressChange<
    TData = Awaited<ReturnType<typeof payrollEmployeeHomeAddressChange>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeHomeAddressChange: EmployeeHomeAddressChange,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeHomeAddressChange>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollEmployeeHomeAddressChange>>,
                    TError,
                    Awaited<ReturnType<typeof payrollEmployeeHomeAddressChange>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollEmployeeHomeAddressChange<
    TData = Awaited<ReturnType<typeof payrollEmployeeHomeAddressChange>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeHomeAddressChange: EmployeeHomeAddressChange,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeHomeAddressChange>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Change an employee's home address
 */

export function usePayrollEmployeeHomeAddressChange<
    TData = Awaited<ReturnType<typeof payrollEmployeeHomeAddressChange>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeHomeAddressChange: EmployeeHomeAddressChange,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeHomeAddressChange>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollEmployeeHomeAddressChangeQueryOptions(
        workerUuid,
        employeeHomeAddressChange,
        options
    );

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Every job this employee holds, with the pay in force on each. A live read.
 *
 * **Most workers have one job, and this exists for the ones who do not.** The worker detail carries a single `job_title`, `start_date` and pay figure, read from the job flagged `is_primary` here; a second job cannot be shown there at all. Render this when the count is above one, and read `is_primary` to see which row those fields match.
 *
 * `rate` is the compensation **in force**, not the newest on file — a future-dated raise is not reported as current pay.
 *
 * Employees only — a contractor is engaged rather than employed and holds no jobs.
 * @summary List an employee's jobs
 */
export const payrollTeamEmployeeJobsList = (
    workerUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<EmployeeJobList>(
        { url: `/api/payroll/v1/team/employees/${workerUuid}/jobs/`, method: 'GET', signal },
        options
    );
};

export const getPayrollTeamEmployeeJobsListQueryKey = (workerUuid: string) => {
    return [`/api/payroll/v1/team/employees/${workerUuid}/jobs/`] as const;
};

export const usePayrollTeamEmployeeJobsListQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeJobsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeJobsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamEmployeeJobsListQueryKey(workerUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamEmployeeJobsList>>> = ({ signal }) =>
        payrollTeamEmployeeJobsList(workerUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeJobsList>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollTeamEmployeeJobsListQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTeamEmployeeJobsList>>
>;
export type PayrollTeamEmployeeJobsListQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamEmployeeJobsList<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeJobsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeJobsList>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeJobsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeJobsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeJobsList<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeJobsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeJobsList>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeJobsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeJobsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeJobsList<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeJobsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeJobsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List an employee's jobs
 */

export function usePayrollTeamEmployeeJobsList<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeJobsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeJobsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamEmployeeJobsListQueryOptions(workerUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * The provider's required onboarding steps this employee has not finished, as sentences for the review page to list. A live read, and no copy is kept.
 *
 * **Hold Confirm while this is not empty.** `POST /team/{worker_uuid}/confirm/` refuses with `WORKER_ONBOARDING_INCOMPLETE` and the same list in `details.blockers`. Most often it is the employee's own form signing, which they do in their onboarding, not the employer.
 *
 * Two steps are never listed: the confirmation itself, and the new hire report, which the review page answers from `GET /team/employees/{worker_uuid}/state-taxes/`.
 *
 * Employees only.
 * @summary List what an employee still owes before they can be confirmed
 */
export const payrollTeamEmployeeOnboardingBlockers = (
    workerUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<EmployeeOnboardingBlockerList>(
        { url: `/api/payroll/v1/team/employees/${workerUuid}/onboarding-blockers/`, method: 'GET', signal },
        options
    );
};

export const getPayrollTeamEmployeeOnboardingBlockersQueryKey = (workerUuid: string) => {
    return [`/api/payroll/v1/team/employees/${workerUuid}/onboarding-blockers/`] as const;
};

export const usePayrollTeamEmployeeOnboardingBlockersQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeOnboardingBlockers>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeOnboardingBlockers>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamEmployeeOnboardingBlockersQueryKey(workerUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamEmployeeOnboardingBlockers>>> = ({ signal }) =>
        payrollTeamEmployeeOnboardingBlockers(workerUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollTeamEmployeeOnboardingBlockers>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollTeamEmployeeOnboardingBlockersQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTeamEmployeeOnboardingBlockers>>
>;
export type PayrollTeamEmployeeOnboardingBlockersQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamEmployeeOnboardingBlockers<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeOnboardingBlockers>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeOnboardingBlockers>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeOnboardingBlockers>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeOnboardingBlockers>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeOnboardingBlockers<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeOnboardingBlockers>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeOnboardingBlockers>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeOnboardingBlockers>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeOnboardingBlockers>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeOnboardingBlockers<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeOnboardingBlockers>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeOnboardingBlockers>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List what an employee still owes before they can be confirmed
 */

export function usePayrollTeamEmployeeOnboardingBlockers<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeOnboardingBlockers>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeOnboardingBlockers>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamEmployeeOnboardingBlockersQueryOptions(workerUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * How this W-2 worker is paid, and into which accounts. A live read.
 *
 * Account numbers appear only as the provider's mask, and no routing number or account type is published: those live on a separate provider resource this API does not read.
 * @summary Retrieve an employee's payment method
 */
export const payrollTeamEmployeePaymentMethod = (
    workerUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<WorkerPaymentMethod>(
        { url: `/api/payroll/v1/team/employees/${workerUuid}/payment-method/`, method: 'GET', signal },
        options
    );
};

export const getPayrollTeamEmployeePaymentMethodQueryKey = (workerUuid: string) => {
    return [`/api/payroll/v1/team/employees/${workerUuid}/payment-method/`] as const;
};

export const usePayrollTeamEmployeePaymentMethodQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethod>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethod>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamEmployeePaymentMethodQueryKey(workerUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethod>>> = ({ signal }) =>
        payrollTeamEmployeePaymentMethod(workerUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethod>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollTeamEmployeePaymentMethodQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethod>>
>;
export type PayrollTeamEmployeePaymentMethodQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamEmployeePaymentMethod<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethod>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethod>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethod>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethod>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeePaymentMethod<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethod>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethod>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethod>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethod>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeePaymentMethod<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethod>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethod>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve an employee's payment method
 */

export function usePayrollTeamEmployeePaymentMethod<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethod>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethod>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamEmployeePaymentMethodQueryOptions(workerUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Point this worker's pay at accounts the provider already holds.
 *
 * **Carries no routing number and no account number.** Each account is named by the id `GET .../bank-accounts/` returned for it, so this endpoint cannot be the place either leaks. Registering a *new* account is a different operation and is not served here.
 *
 * `check` clears the splits. A `direct_deposit` needs `split_by` and at least one split: percentages must total 100, and an `amount` split needs exactly one account with a null amount to take the remainder.
 * @summary Move an employee's pay to different accounts
 */
export const payrollTeamEmployeePaymentMethodUpdate = (
    workerUuid: string,
    workerPaymentMethodUpdateRequest: WorkerPaymentMethodUpdateRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/team/employees/${workerUuid}/payment-method/`,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            data: workerPaymentMethodUpdateRequest,
            signal,
        },
        options
    );
};

export const getPayrollTeamEmployeePaymentMethodUpdateQueryKey = (
    workerUuid: string,
    workerPaymentMethodUpdateRequest?: WorkerPaymentMethodUpdateRequest
) => {
    return [
        'PUT',
        `/api/payroll/v1/team/employees/${workerUuid}/payment-method/`,
        workerPaymentMethodUpdateRequest,
    ] as const;
};

export const usePayrollTeamEmployeePaymentMethodUpdateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethodUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerPaymentMethodUpdateRequest: WorkerPaymentMethodUpdateRequest,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethodUpdate>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ??
        getPayrollTeamEmployeePaymentMethodUpdateQueryKey(workerUuid, workerPaymentMethodUpdateRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethodUpdate>>> = ({ signal }) =>
        payrollTeamEmployeePaymentMethodUpdate(workerUuid, workerPaymentMethodUpdateRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethodUpdate>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollTeamEmployeePaymentMethodUpdateQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethodUpdate>>
>;
export type PayrollTeamEmployeePaymentMethodUpdateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamEmployeePaymentMethodUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethodUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerPaymentMethodUpdateRequest: WorkerPaymentMethodUpdateRequest,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethodUpdate>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethodUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethodUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeePaymentMethodUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethodUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerPaymentMethodUpdateRequest: WorkerPaymentMethodUpdateRequest,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethodUpdate>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethodUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethodUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeePaymentMethodUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethodUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerPaymentMethodUpdateRequest: WorkerPaymentMethodUpdateRequest,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethodUpdate>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Move an employee's pay to different accounts
 */

export function usePayrollTeamEmployeePaymentMethodUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethodUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerPaymentMethodUpdateRequest: WorkerPaymentMethodUpdateRequest,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeePaymentMethodUpdate>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamEmployeePaymentMethodUpdateQueryOptions(
        workerUuid,
        workerPaymentMethodUpdateRequest,
        options
    );

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Records a terminated W-2 worker's return. Refused by the provider for anyone who was never terminated.
 *
 * **Employees only**, for the same reason termination is: the two rails take different bodies. A contractor is rehired at `POST /team/contractors/{worker_uuid}/rehire/`, which takes a start date and nothing else; this route additionally restores the job the employee returns to.
 * @summary Rehire a terminated employee
 */
export const payrollEmployeeRehire = (
    workerUuid: string,
    employeeRehireRequest: EmployeeRehireRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/team/employees/${workerUuid}/rehire/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: employeeRehireRequest,
            signal,
        },
        options
    );
};

export const getPayrollEmployeeRehireQueryKey = (workerUuid: string, employeeRehireRequest?: EmployeeRehireRequest) => {
    return ['POST', `/api/payroll/v1/team/employees/${workerUuid}/rehire/`, employeeRehireRequest] as const;
};

export const usePayrollEmployeeRehireQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollEmployeeRehire>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeRehireRequest: EmployeeRehireRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeRehire>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollEmployeeRehireQueryKey(workerUuid, employeeRehireRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollEmployeeRehire>>> = ({ signal }) =>
        payrollEmployeeRehire(workerUuid, employeeRehireRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeRehire>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollEmployeeRehireQueryResult = NonNullable<Awaited<ReturnType<typeof payrollEmployeeRehire>>>;
export type PayrollEmployeeRehireQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollEmployeeRehire<
    TData = Awaited<ReturnType<typeof payrollEmployeeRehire>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeRehireRequest: EmployeeRehireRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeRehire>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollEmployeeRehire>>,
                    TError,
                    Awaited<ReturnType<typeof payrollEmployeeRehire>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollEmployeeRehire<
    TData = Awaited<ReturnType<typeof payrollEmployeeRehire>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeRehireRequest: EmployeeRehireRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeRehire>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollEmployeeRehire>>,
                    TError,
                    Awaited<ReturnType<typeof payrollEmployeeRehire>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollEmployeeRehire<
    TData = Awaited<ReturnType<typeof payrollEmployeeRehire>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeRehireRequest: EmployeeRehireRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeRehire>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Rehire a terminated employee
 */

export function usePayrollEmployeeRehire<
    TData = Awaited<ReturnType<typeof payrollEmployeeRehire>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeRehireRequest: EmployeeRehireRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeRehire>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollEmployeeRehireQueryOptions(workerUuid, employeeRehireRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * The questions each state needs answered before this worker's tax can be calculated, with the answers on file. a live read — the wording, the input types and the option lists are theirs and change when a state changes its forms.
 *
 * **Render `answer_label`, not `answer`**: a select question stores a code like `M` and means "Married one income". **`description` is plain text** — the provider's inline HTML is stripped and its links come back in `help_links`.
 *
 * One entry per state, and usually one. An empty list is the ordinary answer for a worker whose work state is not yet known, not an error.
 *
 * Employees only — nothing is withheld from a contractor's invoice.
 * @summary List an employee's state withholding questions
 */
export const payrollTeamEmployeeStateTaxesList = (
    workerUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<EmployeeStateTaxList>(
        { url: `/api/payroll/v1/team/employees/${workerUuid}/state-taxes/`, method: 'GET', signal },
        options
    );
};

export const getPayrollTeamEmployeeStateTaxesListQueryKey = (workerUuid: string) => {
    return [`/api/payroll/v1/team/employees/${workerUuid}/state-taxes/`] as const;
};

export const usePayrollTeamEmployeeStateTaxesListQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeStateTaxesList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeStateTaxesList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamEmployeeStateTaxesListQueryKey(workerUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamEmployeeStateTaxesList>>> = ({ signal }) =>
        payrollTeamEmployeeStateTaxesList(workerUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollTeamEmployeeStateTaxesList>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollTeamEmployeeStateTaxesListQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTeamEmployeeStateTaxesList>>
>;
export type PayrollTeamEmployeeStateTaxesListQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamEmployeeStateTaxesList<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeStateTaxesList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeStateTaxesList>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeStateTaxesList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeStateTaxesList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeStateTaxesList<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeStateTaxesList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeStateTaxesList>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamEmployeeStateTaxesList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamEmployeeStateTaxesList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamEmployeeStateTaxesList<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeStateTaxesList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeStateTaxesList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List an employee's state withholding questions
 */

export function usePayrollTeamEmployeeStateTaxesList<
    TData = Awaited<ReturnType<typeof payrollTeamEmployeeStateTaxesList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamEmployeeStateTaxesList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamEmployeeStateTaxesListQueryOptions(workerUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Records a W-2 worker's last day and how their final wages are paid. The worker stays on file with their pay history, so year-end forms still reach them the following January.
 *
 * **Employees only**, because the two rails take different bodies. A contractor is terminated at `POST /team/contractors/{worker_uuid}/terminate/`, which takes an end date and nothing else; this route additionally carries the final-wage decision. Undoing a contractor's is `.../rehire/` rather than a cancellation.
 * @summary Terminate an employee
 */
export const payrollWorkerTerminate = (
    workerUuid: string,
    workerTerminateRequest: WorkerTerminateRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/team/employees/${workerUuid}/terminate/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: workerTerminateRequest,
            signal,
        },
        options
    );
};

export const getPayrollWorkerTerminateQueryKey = (
    workerUuid: string,
    workerTerminateRequest?: WorkerTerminateRequest
) => {
    return ['POST', `/api/payroll/v1/team/employees/${workerUuid}/terminate/`, workerTerminateRequest] as const;
};

export const usePayrollWorkerTerminateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollWorkerTerminate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerTerminateRequest: WorkerTerminateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerTerminate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollWorkerTerminateQueryKey(workerUuid, workerTerminateRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollWorkerTerminate>>> = ({ signal }) =>
        payrollWorkerTerminate(workerUuid, workerTerminateRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerTerminate>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollWorkerTerminateQueryResult = NonNullable<Awaited<ReturnType<typeof payrollWorkerTerminate>>>;
export type PayrollWorkerTerminateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollWorkerTerminate<
    TData = Awaited<ReturnType<typeof payrollWorkerTerminate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerTerminateRequest: WorkerTerminateRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerTerminate>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollWorkerTerminate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollWorkerTerminate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollWorkerTerminate<
    TData = Awaited<ReturnType<typeof payrollWorkerTerminate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerTerminateRequest: WorkerTerminateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerTerminate>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollWorkerTerminate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollWorkerTerminate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollWorkerTerminate<
    TData = Awaited<ReturnType<typeof payrollWorkerTerminate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerTerminateRequest: WorkerTerminateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerTerminate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Terminate an employee
 */

export function usePayrollWorkerTerminate<
    TData = Awaited<ReturnType<typeof payrollWorkerTerminate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerTerminateRequest: WorkerTerminateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerTerminate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollWorkerTerminateQueryOptions(workerUuid, workerTerminateRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Calls off a termination before its last day, leaving the worker active as though it had never been recorded.
 *
 * **Employees only.** A contractor termination answers with no record, so there is nothing to cancel — the way back on that rail is `POST /team/contractors/{worker_uuid}/rehire/`.
 *
 * `WorkerDetail.termination.cancelable` says whether this is worth attempting, but it is an invitation rather than a guarantee: the provider re-checks when the call arrives and refuses with 422 once the last day has passed, once a termination payroll has run, or while a rehire is scheduled. A client should offer the action from `cancelable` and still handle the refusal.
 * @summary Cancel a scheduled termination
 */
export const payrollEmployeeTerminationCancel = (
    workerUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        { url: `/api/payroll/v1/team/employees/${workerUuid}/terminate/cancel/`, method: 'DELETE', signal },
        options
    );
};

export const getPayrollEmployeeTerminationCancelQueryKey = (workerUuid: string) => {
    return ['DELETE', `/api/payroll/v1/team/employees/${workerUuid}/terminate/cancel/`] as const;
};

export const usePayrollEmployeeTerminationCancelQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollEmployeeTerminationCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeTerminationCancel>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollEmployeeTerminationCancelQueryKey(workerUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollEmployeeTerminationCancel>>> = ({ signal }) =>
        payrollEmployeeTerminationCancel(workerUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollEmployeeTerminationCancel>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollEmployeeTerminationCancelQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollEmployeeTerminationCancel>>
>;
export type PayrollEmployeeTerminationCancelQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollEmployeeTerminationCancel<
    TData = Awaited<ReturnType<typeof payrollEmployeeTerminationCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeTerminationCancel>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollEmployeeTerminationCancel>>,
                    TError,
                    Awaited<ReturnType<typeof payrollEmployeeTerminationCancel>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollEmployeeTerminationCancel<
    TData = Awaited<ReturnType<typeof payrollEmployeeTerminationCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeTerminationCancel>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollEmployeeTerminationCancel>>,
                    TError,
                    Awaited<ReturnType<typeof payrollEmployeeTerminationCancel>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollEmployeeTerminationCancel<
    TData = Awaited<ReturnType<typeof payrollEmployeeTerminationCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeTerminationCancel>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Cancel a scheduled termination
 */

export function usePayrollEmployeeTerminationCancel<
    TData = Awaited<ReturnType<typeof payrollEmployeeTerminationCancel>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeTerminationCancel>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollEmployeeTerminationCancelQueryOptions(workerUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Moves the employee to a different company work location. The location decides which state's tax registration applies, so each move is an explicit action.
 *
 * **Employees only.** A contractor has no work location on the provider; their work state is a flat field on the contractor resource.
 *
 * **Works from home.** Send `work_from_home` instead of `work_location_uuid`. With a home address on file, a company location is opened at it (the business's phone number is its phone) and the employee is moved there today. Without one, send `work_state`: the choice is kept and finished when they enter a home address. Refusals, before anything is recorded: **422 `WORK_STATE_REQUIRED`** when there is no home address and no `work_state`, **422 `BUSINESS_PHONE_REQUIRED`** when the business has no phone number on file, **409 `OUT_OF_STATE_UNCONFIRMED`** when the home (or `work_state`) is in a state other than the one they already work in, outside the business's states, without `confirm_out_of_state`.
 * @summary Assign an employee to a work location
 */
export const payrollEmployeeWorkLocationAssign = (
    workerUuid: string,
    employeeWorkLocationAssign?: EmployeeWorkLocationAssign,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/team/employees/${workerUuid}/work-location/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: employeeWorkLocationAssign,
            signal,
        },
        options
    );
};

export const getPayrollEmployeeWorkLocationAssignQueryKey = (
    workerUuid: string,
    employeeWorkLocationAssign?: EmployeeWorkLocationAssign
) => {
    return ['POST', `/api/payroll/v1/team/employees/${workerUuid}/work-location/`, employeeWorkLocationAssign] as const;
};

export const usePayrollEmployeeWorkLocationAssignQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollEmployeeWorkLocationAssign>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeWorkLocationAssign?: EmployeeWorkLocationAssign,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeWorkLocationAssign>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getPayrollEmployeeWorkLocationAssignQueryKey(workerUuid, employeeWorkLocationAssign);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollEmployeeWorkLocationAssign>>> = ({ signal }) =>
        payrollEmployeeWorkLocationAssign(workerUuid, employeeWorkLocationAssign, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollEmployeeWorkLocationAssign>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollEmployeeWorkLocationAssignQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollEmployeeWorkLocationAssign>>
>;
export type PayrollEmployeeWorkLocationAssignQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollEmployeeWorkLocationAssign<
    TData = Awaited<ReturnType<typeof payrollEmployeeWorkLocationAssign>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeWorkLocationAssign: undefined | EmployeeWorkLocationAssign,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeWorkLocationAssign>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollEmployeeWorkLocationAssign>>,
                    TError,
                    Awaited<ReturnType<typeof payrollEmployeeWorkLocationAssign>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollEmployeeWorkLocationAssign<
    TData = Awaited<ReturnType<typeof payrollEmployeeWorkLocationAssign>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeWorkLocationAssign?: EmployeeWorkLocationAssign,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeWorkLocationAssign>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollEmployeeWorkLocationAssign>>,
                    TError,
                    Awaited<ReturnType<typeof payrollEmployeeWorkLocationAssign>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollEmployeeWorkLocationAssign<
    TData = Awaited<ReturnType<typeof payrollEmployeeWorkLocationAssign>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeWorkLocationAssign?: EmployeeWorkLocationAssign,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeWorkLocationAssign>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Assign an employee to a work location
 */

export function usePayrollEmployeeWorkLocationAssign<
    TData = Awaited<ReturnType<typeof payrollEmployeeWorkLocationAssign>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    employeeWorkLocationAssign?: EmployeeWorkLocationAssign,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollEmployeeWorkLocationAssign>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollEmployeeWorkLocationAssignQueryOptions(
        workerUuid,
        employeeWorkLocationAssign,
        options
    );

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Which employee is the member and which is their spouse.
 *
 * The owner starts as our match on the member's email, then name. The member can correct it, including to say they are not on payroll, and once they have `owner_confirmed` is true and the match never runs again. The spouse is only ever what the member told us. A spouse's W-2 goes to the member's tax preparer when they file jointly.
 * @summary Get the owner and spouse
 */
export const payrollTeamFamilyRetrieve = (
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<TeamFamily>({ url: `/api/payroll/v1/team/family/`, method: 'GET', signal }, options);
};

export const getPayrollTeamFamilyRetrieveQueryKey = () => {
    return [`/api/payroll/v1/team/family/`] as const;
};

export const usePayrollTeamFamilyRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamFamilyRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamFamilyRetrieve>>, TError, TData>>;
    request?: SecondParameter<typeof payrollApiMutator>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamFamilyRetrieveQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamFamilyRetrieve>>> = ({ signal }) =>
        payrollTeamFamilyRetrieve(requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollTeamFamilyRetrieve>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollTeamFamilyRetrieveQueryResult = NonNullable<Awaited<ReturnType<typeof payrollTeamFamilyRetrieve>>>;
export type PayrollTeamFamilyRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamFamilyRetrieve<
    TData = Awaited<ReturnType<typeof payrollTeamFamilyRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamFamilyRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamFamilyRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamFamilyRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamFamilyRetrieve<
    TData = Awaited<ReturnType<typeof payrollTeamFamilyRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamFamilyRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamFamilyRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamFamilyRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamFamilyRetrieve<
    TData = Awaited<ReturnType<typeof payrollTeamFamilyRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamFamilyRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get the owner and spouse
 */

export function usePayrollTeamFamilyRetrieve<
    TData = Awaited<ReturnType<typeof payrollTeamFamilyRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamFamilyRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamFamilyRetrieveQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Which employee is the member and which is their spouse.
 *
 * The owner starts as our match on the member's email, then name. The member can correct it, including to say they are not on payroll, and once they have `owner_confirmed` is true and the match never runs again. The spouse is only ever what the member told us. A spouse's W-2 goes to the member's tax preparer when they file jointly.
 *
 * Replaces both answers. Send null for either to say nobody. Only employees can be named; a contractor is 404 `WORKER_NOT_FOUND`.
 * @summary Set the owner and spouse
 */
export const payrollTeamFamilyUpdate = (
    teamFamilyRequest: TeamFamilyRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<TeamFamily>(
        {
            url: `/api/payroll/v1/team/family/`,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            data: teamFamilyRequest,
            signal,
        },
        options
    );
};

export const getPayrollTeamFamilyUpdateQueryKey = (teamFamilyRequest?: TeamFamilyRequest) => {
    return ['PUT', `/api/payroll/v1/team/family/`, teamFamilyRequest] as const;
};

export const usePayrollTeamFamilyUpdateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamFamilyUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    teamFamilyRequest: TeamFamilyRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamFamilyUpdate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamFamilyUpdateQueryKey(teamFamilyRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamFamilyUpdate>>> = ({ signal }) =>
        payrollTeamFamilyUpdate(teamFamilyRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollTeamFamilyUpdate>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollTeamFamilyUpdateQueryResult = NonNullable<Awaited<ReturnType<typeof payrollTeamFamilyUpdate>>>;
export type PayrollTeamFamilyUpdateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamFamilyUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamFamilyUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    teamFamilyRequest: TeamFamilyRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamFamilyUpdate>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamFamilyUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamFamilyUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamFamilyUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamFamilyUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    teamFamilyRequest: TeamFamilyRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamFamilyUpdate>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamFamilyUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamFamilyUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamFamilyUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamFamilyUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    teamFamilyRequest: TeamFamilyRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamFamilyUpdate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Set the owner and spouse
 */

export function usePayrollTeamFamilyUpdate<
    TData = Awaited<ReturnType<typeof payrollTeamFamilyUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    teamFamilyRequest: TeamFamilyRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamFamilyUpdate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamFamilyUpdateQueryOptions(teamFamilyRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Every invitation this company has sent, newest first, including the cancelled and expired ones -- an invitation that lapsed and one that was never sent look identical to a member who cannot see both.
 *
 * `state` up to `sent` is ours: we minted the link and we know when. From `opened` onward it is the provider's answer, mirrored here from the same `onboarding_state` the team list publishes, so the two never disagree about one worker.
 * @summary List pending self-onboarding invitations
 */
export const payrollTeamInvitationsList = (
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<WorkerInvitationList>(
        { url: `/api/payroll/v1/team/invitations/`, method: 'GET', signal },
        options
    );
};

export const getPayrollTeamInvitationsListQueryKey = () => {
    return [`/api/payroll/v1/team/invitations/`] as const;
};

export const usePayrollTeamInvitationsListQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamInvitationsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamInvitationsList>>, TError, TData>>;
    request?: SecondParameter<typeof payrollApiMutator>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamInvitationsListQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamInvitationsList>>> = ({ signal }) =>
        payrollTeamInvitationsList(requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollTeamInvitationsList>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollTeamInvitationsListQueryResult = NonNullable<Awaited<ReturnType<typeof payrollTeamInvitationsList>>>;
export type PayrollTeamInvitationsListQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamInvitationsList<
    TData = Awaited<ReturnType<typeof payrollTeamInvitationsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamInvitationsList>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamInvitationsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamInvitationsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamInvitationsList<
    TData = Awaited<ReturnType<typeof payrollTeamInvitationsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamInvitationsList>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamInvitationsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamInvitationsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamInvitationsList<
    TData = Awaited<ReturnType<typeof payrollTeamInvitationsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamInvitationsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List pending self-onboarding invitations
 */

export function usePayrollTeamInvitationsList<
    TData = Awaited<ReturnType<typeof payrollTeamInvitationsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamInvitationsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamInvitationsListQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * The premises a new employee can be placed at, each under our own identifier. This is the list `POST /team/employees/` takes its `work_location_uuid` from; a location's state is what decides which registrations the business needs before that worker can be paid.
 *
 * Served from our own rows rather than the provider, so it is never a 503 and is empty until the company's addresses have been read through once.
 * @summary List work locations
 */
export const payrollTeamWorkLocationsList = (
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<WorkLocationList>(
        { url: `/api/payroll/v1/team/work-locations/`, method: 'GET', signal },
        options
    );
};

export const getPayrollTeamWorkLocationsListQueryKey = () => {
    return [`/api/payroll/v1/team/work-locations/`] as const;
};

export const usePayrollTeamWorkLocationsListQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamWorkLocationsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamWorkLocationsList>>, TError, TData>>;
    request?: SecondParameter<typeof payrollApiMutator>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamWorkLocationsListQueryKey();

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamWorkLocationsList>>> = ({ signal }) =>
        payrollTeamWorkLocationsList(requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollTeamWorkLocationsList>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollTeamWorkLocationsListQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTeamWorkLocationsList>>
>;
export type PayrollTeamWorkLocationsListQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamWorkLocationsList<
    TData = Awaited<ReturnType<typeof payrollTeamWorkLocationsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamWorkLocationsList>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamWorkLocationsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamWorkLocationsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamWorkLocationsList<
    TData = Awaited<ReturnType<typeof payrollTeamWorkLocationsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamWorkLocationsList>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamWorkLocationsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamWorkLocationsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamWorkLocationsList<
    TData = Awaited<ReturnType<typeof payrollTeamWorkLocationsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamWorkLocationsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List work locations
 */

export function usePayrollTeamWorkLocationsList<
    TData = Awaited<ReturnType<typeof payrollTeamWorkLocationsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamWorkLocationsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamWorkLocationsListQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Registers a new premises for the company, and it appears on the `GET` above as soon as the command confirms.
 *
 * **The company's address, not a worker's.** These are the employer's own premises; where somebody lives is theirs to give the provider and is not accepted here.
 *
 * A location's `state` decides which registrations the business needs, and opening one in a new state can oblige it to register there. That follows from the employee who is then assigned to the address rather than from this call, which records where the company operates.
 *
 * **An address the company already has is not opened twice.** The request is compared with the active locations (case, punctuation and common abbreviations such as Suite/STE ignored; ZIP on five digits). On a match nothing is created: `work_location_uuid` is that location, and a different phone number is saved to it.
 *
 * The 202 names the new premises as `work_location_uuid`, so a client can create a location and assign somebody to it in one step. On rare occasions `synced` comes back false and `work_location_uuid` null: the provider has it but our own copy failed to save, so the list may not show it yet. Nothing to retry -- a background sweep repairs it -- just refetch the list shortly after.
 * @summary Create a work location
 */
export const payrollTeamWorkLocationsCreate = (
    workLocationCreateRequest: WorkLocationCreateRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<WorkLocationCreateAccepted>(
        {
            url: `/api/payroll/v1/team/work-locations/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: workLocationCreateRequest,
            signal,
        },
        options
    );
};

export const getPayrollTeamWorkLocationsCreateQueryKey = (workLocationCreateRequest?: WorkLocationCreateRequest) => {
    return ['POST', `/api/payroll/v1/team/work-locations/`, workLocationCreateRequest] as const;
};

export const usePayrollTeamWorkLocationsCreateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTeamWorkLocationsCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workLocationCreateRequest: WorkLocationCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamWorkLocationsCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTeamWorkLocationsCreateQueryKey(workLocationCreateRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTeamWorkLocationsCreate>>> = ({ signal }) =>
        payrollTeamWorkLocationsCreate(workLocationCreateRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollTeamWorkLocationsCreate>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollTeamWorkLocationsCreateQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTeamWorkLocationsCreate>>
>;
export type PayrollTeamWorkLocationsCreateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTeamWorkLocationsCreate<
    TData = Awaited<ReturnType<typeof payrollTeamWorkLocationsCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workLocationCreateRequest: WorkLocationCreateRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamWorkLocationsCreate>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamWorkLocationsCreate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamWorkLocationsCreate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamWorkLocationsCreate<
    TData = Awaited<ReturnType<typeof payrollTeamWorkLocationsCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workLocationCreateRequest: WorkLocationCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamWorkLocationsCreate>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTeamWorkLocationsCreate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTeamWorkLocationsCreate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTeamWorkLocationsCreate<
    TData = Awaited<ReturnType<typeof payrollTeamWorkLocationsCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workLocationCreateRequest: WorkLocationCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamWorkLocationsCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create a work location
 */

export function usePayrollTeamWorkLocationsCreate<
    TData = Awaited<ReturnType<typeof payrollTeamWorkLocationsCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workLocationCreateRequest: WorkLocationCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTeamWorkLocationsCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTeamWorkLocationsCreateQueryOptions(workLocationCreateRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * The employer-review gate. A self-onboarded worker supplies their own sensitive details and the employer confirms what they own — who this person is to the business — before any run can include them. Until that happens the worker reads `can_be_paid: false` on `GET /team/`, whatever their `status` says.
 *
 * **An approval rather than an edit.** It takes no details, because the details are already on file: correcting a job title or a rate goes through the worker update endpoints, and this call is the member saying the result is right. `is_owner` is the exception and is not a provider field at all — it is the ownership question the review is the natural moment to answer, on the employee rail only.
 *
 * `file_new_hire_report` is the other: it answers the admin-only state tax question behind a required onboarding step at the provider. The provider lists that question only while the employee is at `admin_onboarding_incomplete` or `self_onboarding_awaiting_admin_review`, and only when their work state requires a new hire report ([state taxes reference](https://docs.gusto.com/embedded-payroll/reference/get-v1-employees-employee_id-state_taxes)). A self-onboarded employee can reach this page before either status, so a review page that finds no question on `GET /team/employees/{worker_uuid}/state-taxes/` must still handle `NEW_HIRE_REPORT_UNANSWERED`.
 *
 * Safe to send twice. A worker already past the gate is answered without a further write, and one confirmed only part-way resumes from where they stopped rather than starting again — which matters because the steps behind this are a state machine whose earlier moves become illegal once made.
 *
 * The 202 is the usual one: the worker becomes payable on the next `GET /team/`, and the command is where a failure shows up.
 *
 * **A remote employee's work address is finished first**, if onboarding has not already done it: a location is opened at their home address. If that home is in a different state from the `work_state` chosen at invite, this answers **409 `WORK_STATE_MISMATCH`** (`details.work_state`, `details.home_state`) and confirms nothing; moving them to working from home through `POST .../work-location/` settles it. **422 `BUSINESS_PHONE_REQUIRED`** when the business has no phone number for the location.
 * @summary Confirm a worker's details and let them be paid
 */
export const payrollWorkerConfirm = (
    workerUuid: string,
    workerConfirmRequest?: WorkerConfirmRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/team/${workerUuid}/confirm/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: workerConfirmRequest,
            signal,
        },
        options
    );
};

export const getPayrollWorkerConfirmQueryKey = (workerUuid: string, workerConfirmRequest?: WorkerConfirmRequest) => {
    return ['POST', `/api/payroll/v1/team/${workerUuid}/confirm/`, workerConfirmRequest] as const;
};

export const usePayrollWorkerConfirmQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollWorkerConfirm>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerConfirmRequest?: WorkerConfirmRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerConfirm>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollWorkerConfirmQueryKey(workerUuid, workerConfirmRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollWorkerConfirm>>> = ({ signal }) =>
        payrollWorkerConfirm(workerUuid, workerConfirmRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerConfirm>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollWorkerConfirmQueryResult = NonNullable<Awaited<ReturnType<typeof payrollWorkerConfirm>>>;
export type PayrollWorkerConfirmQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollWorkerConfirm<
    TData = Awaited<ReturnType<typeof payrollWorkerConfirm>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerConfirmRequest: undefined | WorkerConfirmRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerConfirm>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollWorkerConfirm>>,
                    TError,
                    Awaited<ReturnType<typeof payrollWorkerConfirm>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollWorkerConfirm<
    TData = Awaited<ReturnType<typeof payrollWorkerConfirm>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerConfirmRequest?: WorkerConfirmRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerConfirm>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollWorkerConfirm>>,
                    TError,
                    Awaited<ReturnType<typeof payrollWorkerConfirm>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollWorkerConfirm<
    TData = Awaited<ReturnType<typeof payrollWorkerConfirm>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerConfirmRequest?: WorkerConfirmRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerConfirm>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Confirm a worker's details and let them be paid
 */

export function usePayrollWorkerConfirm<
    TData = Awaited<ReturnType<typeof payrollWorkerConfirm>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerConfirmRequest?: WorkerConfirmRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerConfirm>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollWorkerConfirmQueryOptions(workerUuid, workerConfirmRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Asks the worker to enter their own SSN, date of birth, home address, bank details and withholding at the provider. None of it passes through Collective.
 *
 * Send and resend are this one call: the provider's own status decides which happens, so a member who cannot tell whether the first press worked may press it again safely.
 *
 * **The link is not in the response.** It is a bearer credential and every command result is stored on the ledger, so it is fetched from `GET /team/{id}/onboarding-link/` instead -- which mints a fresh one and stores nothing. The provider does not email the worker; sending them the link is the employer's to do.
 * @summary Send or resend a self-onboarding invitation
 */
export const payrollWorkerInvite = (
    workerUuid: string,
    workerInviteRequest?: WorkerInviteRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/team/${workerUuid}/invite/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: workerInviteRequest,
            signal,
        },
        options
    );
};

export const getPayrollWorkerInviteQueryKey = (workerUuid: string, workerInviteRequest?: WorkerInviteRequest) => {
    return ['POST', `/api/payroll/v1/team/${workerUuid}/invite/`, workerInviteRequest] as const;
};

export const usePayrollWorkerInviteQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollWorkerInvite>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerInviteRequest?: WorkerInviteRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerInvite>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollWorkerInviteQueryKey(workerUuid, workerInviteRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollWorkerInvite>>> = ({ signal }) =>
        payrollWorkerInvite(workerUuid, workerInviteRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerInvite>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollWorkerInviteQueryResult = NonNullable<Awaited<ReturnType<typeof payrollWorkerInvite>>>;
export type PayrollWorkerInviteQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollWorkerInvite<
    TData = Awaited<ReturnType<typeof payrollWorkerInvite>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerInviteRequest: undefined | WorkerInviteRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerInvite>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollWorkerInvite>>,
                    TError,
                    Awaited<ReturnType<typeof payrollWorkerInvite>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollWorkerInvite<
    TData = Awaited<ReturnType<typeof payrollWorkerInvite>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerInviteRequest?: WorkerInviteRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerInvite>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollWorkerInvite>>,
                    TError,
                    Awaited<ReturnType<typeof payrollWorkerInvite>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollWorkerInvite<
    TData = Awaited<ReturnType<typeof payrollWorkerInvite>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerInviteRequest?: WorkerInviteRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerInvite>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Send or resend a self-onboarding invitation
 */

export function usePayrollWorkerInvite<
    TData = Awaited<ReturnType<typeof payrollWorkerInvite>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerInviteRequest?: WorkerInviteRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerInvite>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollWorkerInviteQueryOptions(workerUuid, workerInviteRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Needs no Collective login. The worker is not a member and will never have an account here, so the link is the whole credential -- which is why it is short-lived and minted per request rather than stored and re-shown.
 *
 * The provider expires a flow an hour after the worker stops using it, or a day after it was made, whichever comes first. Ask again for a fresh one; nothing is spent by doing so.
 *
 * Refused for a worker with no invitation open, which is what keeps this a read: without that, opening a detail page would invite somebody.
 * @summary Retrieve a worker's self-onboarding link
 */
export const payrollWorkerOnboardingLinkRetrieve = (
    workerUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<WorkerOnboardingLink>(
        { url: `/api/payroll/v1/team/${workerUuid}/onboarding-link/`, method: 'GET', signal },
        options
    );
};

export const getPayrollWorkerOnboardingLinkRetrieveQueryKey = (workerUuid: string) => {
    return [`/api/payroll/v1/team/${workerUuid}/onboarding-link/`] as const;
};

export const usePayrollWorkerOnboardingLinkRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollWorkerOnboardingLinkRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerOnboardingLinkRetrieve>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollWorkerOnboardingLinkRetrieveQueryKey(workerUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollWorkerOnboardingLinkRetrieve>>> = ({ signal }) =>
        payrollWorkerOnboardingLinkRetrieve(workerUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollWorkerOnboardingLinkRetrieve>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollWorkerOnboardingLinkRetrieveQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollWorkerOnboardingLinkRetrieve>>
>;
export type PayrollWorkerOnboardingLinkRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollWorkerOnboardingLinkRetrieve<
    TData = Awaited<ReturnType<typeof payrollWorkerOnboardingLinkRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerOnboardingLinkRetrieve>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollWorkerOnboardingLinkRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollWorkerOnboardingLinkRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollWorkerOnboardingLinkRetrieve<
    TData = Awaited<ReturnType<typeof payrollWorkerOnboardingLinkRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerOnboardingLinkRetrieve>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollWorkerOnboardingLinkRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollWorkerOnboardingLinkRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollWorkerOnboardingLinkRetrieve<
    TData = Awaited<ReturnType<typeof payrollWorkerOnboardingLinkRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerOnboardingLinkRetrieve>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve a worker's self-onboarding link
 */

export function usePayrollWorkerOnboardingLinkRetrieve<
    TData = Awaited<ReturnType<typeof payrollWorkerOnboardingLinkRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerOnboardingLinkRetrieve>>, TError, TData>
        >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollWorkerOnboardingLinkRetrieveQueryOptions(workerUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * **Deprecated. Use `POST /team/employees/{worker_uuid}/terminate/`.**
 *
 * The pre-split `team/{worker_uuid}/terminate/` path, kept so an already-generated client keeps resolving. Each rail has its own route because the bodies differ -- an employee termination carries a final-wage decision, a contractor's is a bare end date.
 *
 * **This path is employees only.** A contractor uuid is a 404 here and belongs at `POST /team/contractors/{worker_uuid}/terminate/`.
 * @deprecated
 * @summary Terminate an employee (deprecated path)
 */
export const payrollWorkerTerminateLegacy = (
    workerUuid: string,
    workerTerminateRequest: WorkerTerminateRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollCommandAccepted>(
        {
            url: `/api/payroll/v1/team/${workerUuid}/terminate/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: workerTerminateRequest,
            signal,
        },
        options
    );
};

export const getPayrollWorkerTerminateLegacyQueryKey = (
    workerUuid: string,
    workerTerminateRequest?: WorkerTerminateRequest
) => {
    return ['POST', `/api/payroll/v1/team/${workerUuid}/terminate/`, workerTerminateRequest] as const;
};

export const usePayrollWorkerTerminateLegacyQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollWorkerTerminateLegacy>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerTerminateRequest: WorkerTerminateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerTerminateLegacy>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getPayrollWorkerTerminateLegacyQueryKey(workerUuid, workerTerminateRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollWorkerTerminateLegacy>>> = ({ signal }) =>
        payrollWorkerTerminateLegacy(workerUuid, workerTerminateRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: workerUuid !== null && workerUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerTerminateLegacy>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollWorkerTerminateLegacyQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollWorkerTerminateLegacy>>
>;
export type PayrollWorkerTerminateLegacyQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollWorkerTerminateLegacy<
    TData = Awaited<ReturnType<typeof payrollWorkerTerminateLegacy>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerTerminateRequest: WorkerTerminateRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerTerminateLegacy>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollWorkerTerminateLegacy>>,
                    TError,
                    Awaited<ReturnType<typeof payrollWorkerTerminateLegacy>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollWorkerTerminateLegacy<
    TData = Awaited<ReturnType<typeof payrollWorkerTerminateLegacy>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerTerminateRequest: WorkerTerminateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerTerminateLegacy>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollWorkerTerminateLegacy>>,
                    TError,
                    Awaited<ReturnType<typeof payrollWorkerTerminateLegacy>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollWorkerTerminateLegacy<
    TData = Awaited<ReturnType<typeof payrollWorkerTerminateLegacy>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerTerminateRequest: WorkerTerminateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerTerminateLegacy>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @deprecated
 * @summary Terminate an employee (deprecated path)
 */

export function usePayrollWorkerTerminateLegacy<
    TData = Awaited<ReturnType<typeof payrollWorkerTerminateLegacy>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    workerUuid: string,
    workerTerminateRequest: WorkerTerminateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWorkerTerminateLegacy>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollWorkerTerminateLegacyQueryOptions(workerUuid, workerTerminateRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Newest period first. Filter by worker, state or period to drive an approval queue.
 * @summary List timesheets
 */
export const payrollTimesheetsList = (
    params?: PayrollTimesheetsListParams,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<TimesheetPage>(
        { url: `/api/payroll/v1/timesheets/`, method: 'GET', params, signal },
        options
    );
};

export const getPayrollTimesheetsListQueryKey = (params?: PayrollTimesheetsListParams) => {
    return [`/api/payroll/v1/timesheets/`, ...(params ? [params] : [])] as const;
};

export const usePayrollTimesheetsListQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTimesheetsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollTimesheetsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTimesheetsListQueryKey(params);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTimesheetsList>>> = ({ signal }) =>
        payrollTimesheetsList(params, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetsList>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollTimesheetsListQueryResult = NonNullable<Awaited<ReturnType<typeof payrollTimesheetsList>>>;
export type PayrollTimesheetsListQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTimesheetsList<
    TData = Awaited<ReturnType<typeof payrollTimesheetsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params: undefined | PayrollTimesheetsListParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetsList>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTimesheetsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTimesheetsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTimesheetsList<
    TData = Awaited<ReturnType<typeof payrollTimesheetsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollTimesheetsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetsList>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTimesheetsList>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTimesheetsList>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTimesheetsList<
    TData = Awaited<ReturnType<typeof payrollTimesheetsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollTimesheetsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List timesheets
 */

export function usePayrollTimesheetsList<
    TData = Awaited<ReturnType<typeof payrollTimesheetsList>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollTimesheetsListParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetsList>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTimesheetsListQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * One timesheet per worker per period. A repeat answers 409 with the existing `timesheet_uuid`.
 * @summary Create a timesheet for a worker
 */
export const payrollTimesheetsCreate = (
    timesheetCreateRequest: TimesheetCreateRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<TimesheetDetail>(
        {
            url: `/api/payroll/v1/timesheets/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: timesheetCreateRequest,
            signal,
        },
        options
    );
};

export const getPayrollTimesheetsCreateQueryKey = (timesheetCreateRequest?: TimesheetCreateRequest) => {
    return ['POST', `/api/payroll/v1/timesheets/`, timesheetCreateRequest] as const;
};

export const usePayrollTimesheetsCreateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTimesheetsCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetCreateRequest: TimesheetCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetsCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTimesheetsCreateQueryKey(timesheetCreateRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTimesheetsCreate>>> = ({ signal }) =>
        payrollTimesheetsCreate(timesheetCreateRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetsCreate>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollTimesheetsCreateQueryResult = NonNullable<Awaited<ReturnType<typeof payrollTimesheetsCreate>>>;
export type PayrollTimesheetsCreateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTimesheetsCreate<
    TData = Awaited<ReturnType<typeof payrollTimesheetsCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetCreateRequest: TimesheetCreateRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetsCreate>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTimesheetsCreate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTimesheetsCreate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTimesheetsCreate<
    TData = Awaited<ReturnType<typeof payrollTimesheetsCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetCreateRequest: TimesheetCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetsCreate>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTimesheetsCreate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTimesheetsCreate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTimesheetsCreate<
    TData = Awaited<ReturnType<typeof payrollTimesheetsCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetCreateRequest: TimesheetCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetsCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create a timesheet for a worker
 */

export function usePayrollTimesheetsCreate<
    TData = Awaited<ReturnType<typeof payrollTimesheetsCreate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetCreateRequest: TimesheetCreateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetsCreate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTimesheetsCreateQueryOptions(timesheetCreateRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Base for every stub: the live views' auth and member scoping, and no handler body.
 *
 * Subclasses set :attr:`operation` and implement their verbs as ``raise
 * self.not_implemented()``. The verb methods exist so an unsupported verb answers 405.
 * @summary Retrieve a timesheet and its days
 */
export const payrollTimesheetRetrieve = (
    timesheetUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<TimesheetDetail>(
        { url: `/api/payroll/v1/timesheets/${timesheetUuid}/`, method: 'GET', signal },
        options
    );
};

export const getPayrollTimesheetRetrieveQueryKey = (timesheetUuid: string) => {
    return [`/api/payroll/v1/timesheets/${timesheetUuid}/`] as const;
};

export const usePayrollTimesheetRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTimesheetRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTimesheetRetrieveQueryKey(timesheetUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTimesheetRetrieve>>> = ({ signal }) =>
        payrollTimesheetRetrieve(timesheetUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: timesheetUuid !== null && timesheetUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetRetrieve>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollTimesheetRetrieveQueryResult = NonNullable<Awaited<ReturnType<typeof payrollTimesheetRetrieve>>>;
export type PayrollTimesheetRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTimesheetRetrieve<
    TData = Awaited<ReturnType<typeof payrollTimesheetRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTimesheetRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTimesheetRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTimesheetRetrieve<
    TData = Awaited<ReturnType<typeof payrollTimesheetRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTimesheetRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTimesheetRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTimesheetRetrieve<
    TData = Awaited<ReturnType<typeof payrollTimesheetRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve a timesheet and its days
 */

export function usePayrollTimesheetRetrieve<
    TData = Awaited<ReturnType<typeof payrollTimesheetRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTimesheetRetrieveQueryOptions(timesheetUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Approved hours are the ones a run prefills from. Approval records who and when, so a paid amount traces back to the shifts behind it.
 * @summary Approve a timesheet
 */
export const payrollTimesheetApprove = (
    timesheetUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<TimesheetDetail>(
        { url: `/api/payroll/v1/timesheets/${timesheetUuid}/approve/`, method: 'POST', signal },
        options
    );
};

export const getPayrollTimesheetApproveQueryKey = (timesheetUuid: string) => {
    return ['POST', `/api/payroll/v1/timesheets/${timesheetUuid}/approve/`] as const;
};

export const usePayrollTimesheetApproveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTimesheetApprove>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetApprove>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTimesheetApproveQueryKey(timesheetUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTimesheetApprove>>> = ({ signal }) =>
        payrollTimesheetApprove(timesheetUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: timesheetUuid !== null && timesheetUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetApprove>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollTimesheetApproveQueryResult = NonNullable<Awaited<ReturnType<typeof payrollTimesheetApprove>>>;
export type PayrollTimesheetApproveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTimesheetApprove<
    TData = Awaited<ReturnType<typeof payrollTimesheetApprove>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetApprove>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTimesheetApprove>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTimesheetApprove>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTimesheetApprove<
    TData = Awaited<ReturnType<typeof payrollTimesheetApprove>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetApprove>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTimesheetApprove>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTimesheetApprove>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTimesheetApprove<
    TData = Awaited<ReturnType<typeof payrollTimesheetApprove>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetApprove>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Approve a timesheet
 */

export function usePayrollTimesheetApprove<
    TData = Awaited<ReturnType<typeof payrollTimesheetApprove>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetApprove>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTimesheetApproveQueryOptions(timesheetUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Send the whole grid. A timesheet is edited as one, so the full set says which rows survive. Once submitted it answers 409, and a rejection is what reopens it for editing.
 * @summary Replace the days on a timesheet
 */
export const payrollTimesheetEntriesUpdate = (
    timesheetUuid: string,
    timesheetEntriesUpdateRequest: TimesheetEntriesUpdateRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<TimesheetDetail>(
        {
            url: `/api/payroll/v1/timesheets/${timesheetUuid}/entries/`,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            data: timesheetEntriesUpdateRequest,
            signal,
        },
        options
    );
};

export const getPayrollTimesheetEntriesUpdateQueryKey = (
    timesheetUuid: string,
    timesheetEntriesUpdateRequest?: TimesheetEntriesUpdateRequest
) => {
    return ['PUT', `/api/payroll/v1/timesheets/${timesheetUuid}/entries/`, timesheetEntriesUpdateRequest] as const;
};

export const usePayrollTimesheetEntriesUpdateQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTimesheetEntriesUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    timesheetEntriesUpdateRequest: TimesheetEntriesUpdateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetEntriesUpdate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ??
        getPayrollTimesheetEntriesUpdateQueryKey(timesheetUuid, timesheetEntriesUpdateRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTimesheetEntriesUpdate>>> = ({ signal }) =>
        payrollTimesheetEntriesUpdate(timesheetUuid, timesheetEntriesUpdateRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: timesheetUuid !== null && timesheetUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<typeof payrollTimesheetEntriesUpdate>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PayrollTimesheetEntriesUpdateQueryResult = NonNullable<
    Awaited<ReturnType<typeof payrollTimesheetEntriesUpdate>>
>;
export type PayrollTimesheetEntriesUpdateQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTimesheetEntriesUpdate<
    TData = Awaited<ReturnType<typeof payrollTimesheetEntriesUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    timesheetEntriesUpdateRequest: TimesheetEntriesUpdateRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetEntriesUpdate>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTimesheetEntriesUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTimesheetEntriesUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTimesheetEntriesUpdate<
    TData = Awaited<ReturnType<typeof payrollTimesheetEntriesUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    timesheetEntriesUpdateRequest: TimesheetEntriesUpdateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetEntriesUpdate>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTimesheetEntriesUpdate>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTimesheetEntriesUpdate>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTimesheetEntriesUpdate<
    TData = Awaited<ReturnType<typeof payrollTimesheetEntriesUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    timesheetEntriesUpdateRequest: TimesheetEntriesUpdateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetEntriesUpdate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Replace the days on a timesheet
 */

export function usePayrollTimesheetEntriesUpdate<
    TData = Awaited<ReturnType<typeof payrollTimesheetEntriesUpdate>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    timesheetEntriesUpdateRequest: TimesheetEntriesUpdateRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetEntriesUpdate>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTimesheetEntriesUpdateQueryOptions(
        timesheetUuid,
        timesheetEntriesUpdateRequest,
        options
    );

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Returns it to the worker with a reason and reopens it for editing.
 * @summary Send a timesheet back
 */
export const payrollTimesheetReject = (
    timesheetUuid: string,
    timesheetRejectRequest: TimesheetRejectRequest,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<TimesheetDetail>(
        {
            url: `/api/payroll/v1/timesheets/${timesheetUuid}/reject/`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: timesheetRejectRequest,
            signal,
        },
        options
    );
};

export const getPayrollTimesheetRejectQueryKey = (
    timesheetUuid: string,
    timesheetRejectRequest?: TimesheetRejectRequest
) => {
    return ['POST', `/api/payroll/v1/timesheets/${timesheetUuid}/reject/`, timesheetRejectRequest] as const;
};

export const usePayrollTimesheetRejectQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTimesheetReject>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    timesheetRejectRequest: TimesheetRejectRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetReject>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTimesheetRejectQueryKey(timesheetUuid, timesheetRejectRequest);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTimesheetReject>>> = ({ signal }) =>
        payrollTimesheetReject(timesheetUuid, timesheetRejectRequest, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: timesheetUuid !== null && timesheetUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetReject>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollTimesheetRejectQueryResult = NonNullable<Awaited<ReturnType<typeof payrollTimesheetReject>>>;
export type PayrollTimesheetRejectQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTimesheetReject<
    TData = Awaited<ReturnType<typeof payrollTimesheetReject>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    timesheetRejectRequest: TimesheetRejectRequest,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetReject>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTimesheetReject>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTimesheetReject>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTimesheetReject<
    TData = Awaited<ReturnType<typeof payrollTimesheetReject>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    timesheetRejectRequest: TimesheetRejectRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetReject>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTimesheetReject>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTimesheetReject>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTimesheetReject<
    TData = Awaited<ReturnType<typeof payrollTimesheetReject>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    timesheetRejectRequest: TimesheetRejectRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetReject>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Send a timesheet back
 */

export function usePayrollTimesheetReject<
    TData = Awaited<ReturnType<typeof payrollTimesheetReject>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    timesheetRejectRequest: TimesheetRejectRequest,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetReject>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTimesheetRejectQueryOptions(timesheetUuid, timesheetRejectRequest, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Hands the sheet to the owner for review. Money moves later, when a run pays these hours.
 * @summary Send a timesheet for approval
 */
export const payrollTimesheetSubmit = (
    timesheetUuid: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<TimesheetDetail>(
        { url: `/api/payroll/v1/timesheets/${timesheetUuid}/submit/`, method: 'POST', signal },
        options
    );
};

export const getPayrollTimesheetSubmitQueryKey = (timesheetUuid: string) => {
    return ['POST', `/api/payroll/v1/timesheets/${timesheetUuid}/submit/`] as const;
};

export const usePayrollTimesheetSubmitQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollTimesheetSubmit>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetSubmit>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollTimesheetSubmitQueryKey(timesheetUuid);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollTimesheetSubmit>>> = ({ signal }) =>
        payrollTimesheetSubmit(timesheetUuid, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: timesheetUuid !== null && timesheetUuid !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetSubmit>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollTimesheetSubmitQueryResult = NonNullable<Awaited<ReturnType<typeof payrollTimesheetSubmit>>>;
export type PayrollTimesheetSubmitQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollTimesheetSubmit<
    TData = Awaited<ReturnType<typeof payrollTimesheetSubmit>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetSubmit>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTimesheetSubmit>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTimesheetSubmit>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTimesheetSubmit<
    TData = Awaited<ReturnType<typeof payrollTimesheetSubmit>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetSubmit>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollTimesheetSubmit>>,
                    TError,
                    Awaited<ReturnType<typeof payrollTimesheetSubmit>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollTimesheetSubmit<
    TData = Awaited<ReturnType<typeof payrollTimesheetSubmit>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetSubmit>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Send a timesheet for approval
 */

export function usePayrollTimesheetSubmit<
    TData = Awaited<ReturnType<typeof payrollTimesheetSubmit>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    timesheetUuid: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollTimesheetSubmit>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollTimesheetSubmitQueryOptions(timesheetUuid, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * A signature authenticates this, so there is no member on the request. Every delivery is recorded and deduped before anything advances a status, and `duplicate: true` is ordinary.
 * @summary Receive inbound payroll events
 */
export const payrollWebhookReceive = (
    provider: string,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<ProviderWebhookAck>(
        { url: `/api/payroll/v1/webhooks/${provider}/`, method: 'POST', signal },
        options
    );
};

export const getPayrollWebhookReceiveQueryKey = (provider: string) => {
    return ['POST', `/api/payroll/v1/webhooks/${provider}/`] as const;
};

export const usePayrollWebhookReceiveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollWebhookReceive>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    provider: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWebhookReceive>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollWebhookReceiveQueryKey(provider);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollWebhookReceive>>> = ({ signal }) =>
        payrollWebhookReceive(provider, requestOptions, signal);

    const customOptions = payrollQueryDefaults({
        queryKey,
        queryFn,
        enabled: provider !== null && provider !== undefined,
        ...queryOptions,
    });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollWebhookReceive>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollWebhookReceiveQueryResult = NonNullable<Awaited<ReturnType<typeof payrollWebhookReceive>>>;
export type PayrollWebhookReceiveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollWebhookReceive<
    TData = Awaited<ReturnType<typeof payrollWebhookReceive>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    provider: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWebhookReceive>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollWebhookReceive>>,
                    TError,
                    Awaited<ReturnType<typeof payrollWebhookReceive>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollWebhookReceive<
    TData = Awaited<ReturnType<typeof payrollWebhookReceive>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    provider: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWebhookReceive>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollWebhookReceive>>,
                    TError,
                    Awaited<ReturnType<typeof payrollWebhookReceive>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollWebhookReceive<
    TData = Awaited<ReturnType<typeof payrollWebhookReceive>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    provider: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWebhookReceive>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Receive inbound payroll events
 */

export function usePayrollWebhookReceive<
    TData = Awaited<ReturnType<typeof payrollWebhookReceive>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    provider: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollWebhookReceive>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollWebhookReceiveQueryOptions(provider, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Year-to-date payroll totals for the company.
 *
 * Defaults to the current year in US/Pacific, the timezone payroll runs in, so a request made either side of midnight elsewhere still reports the year payroll is filed under.
 * @summary Retrieve year-to-date totals
 */
export const payrollYtdRetrieve = (
    params?: PayrollYtdRetrieveParams,
    options?: SecondParameter<typeof payrollApiMutator>,
    signal?: AbortSignal
) => {
    return payrollApiMutator<PayrollYtd>({ url: `/api/payroll/v1/ytd/`, method: 'GET', params, signal }, options);
};

export const getPayrollYtdRetrieveQueryKey = (params?: PayrollYtdRetrieveParams) => {
    return [`/api/payroll/v1/ytd/`, ...(params ? [params] : [])] as const;
};

export const usePayrollYtdRetrieveQueryOptions = <
    TData = Awaited<ReturnType<typeof payrollYtdRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollYtdRetrieveParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollYtdRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getPayrollYtdRetrieveQueryKey(params);

    const queryFn: QueryFunction<Awaited<ReturnType<typeof payrollYtdRetrieve>>> = ({ signal }) =>
        payrollYtdRetrieve(params, requestOptions, signal);

    const customOptions = payrollQueryDefaults({ queryKey, queryFn, ...queryOptions });

    return customOptions as UseQueryOptions<Awaited<ReturnType<typeof payrollYtdRetrieve>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type PayrollYtdRetrieveQueryResult = NonNullable<Awaited<ReturnType<typeof payrollYtdRetrieve>>>;
export type PayrollYtdRetrieveQueryError = ErrorType<PayrollErrorResponse>;

export function usePayrollYtdRetrieve<
    TData = Awaited<ReturnType<typeof payrollYtdRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params: undefined | PayrollYtdRetrieveParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollYtdRetrieve>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollYtdRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollYtdRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollYtdRetrieve<
    TData = Awaited<ReturnType<typeof payrollYtdRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollYtdRetrieveParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollYtdRetrieve>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof payrollYtdRetrieve>>,
                    TError,
                    Awaited<ReturnType<typeof payrollYtdRetrieve>>
                >,
                'initialData'
            >;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePayrollYtdRetrieve<
    TData = Awaited<ReturnType<typeof payrollYtdRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollYtdRetrieveParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollYtdRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retrieve year-to-date totals
 */

export function usePayrollYtdRetrieve<
    TData = Awaited<ReturnType<typeof payrollYtdRetrieve>>,
    TError = ErrorType<PayrollErrorResponse>
>(
    params?: PayrollYtdRetrieveParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof payrollYtdRetrieve>>, TError, TData>>;
        request?: SecondParameter<typeof payrollApiMutator>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = usePayrollYtdRetrieveQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}
